import { Component, Input, OnChanges } from '@angular/core';
import { Product } from '../../../model/product/product.model';
import { lineTabFill } from '../line-color-set';
import { electricTrio, mixHex, vividHex } from '../line-electric-colors';
import { MAPLE_BLADE } from '../line-maple-glyph';
import { palmPaths } from '../line-palm-glyph';
import { SHELL, SHELL_RIBS } from '../line-shell-glyph';
import { LAMP_GLASS, LAMP_IRON } from '../line-lamp-glyph';
import { YACHT_HULL, YACHT_JIB, YACHT_MAIN, YACHT_MAST } from '../line-yacht-glyph';
import { PUFFIN_BILL, PUFFIN_BILL_INNER, PUFFIN_BILL_OUTER, PUFFIN_BILL_TIP, PUFFIN_BODY, PUFFIN_EYE, PUFFIN_FACE, PUFFIN_FEET, PUFFIN_FOOT } from '../line-puffin-glyph';
import { VIOLIN } from '../line-violin-glyph';
import { EMBER, VOLCANO_CONE, VOLCANO_FISSURE, VOLCANO_GLOW, VOLCANO_RIVER } from '../line-volcano-glyph';
import { PAGODA_BODIES, PAGODA_FINIAL, PAGODA_ROOFS } from '../line-pagoda-glyph';

/** A mountain ridge layer: cusped peaks + a smooth roll, all integer-frequency
 *  so the strip tiles seamlessly left-right. Optional sparse pine trees. */
interface RidgeSpec {
  base: number;                                  // baseline (fraction of H)
  cusps: [ number, number, number ][];           // [k peaks, amp, phase]
  roll: [ number, number, number ];              // [k, amp, phase] smooth sine
  fill: string;
  opacity: number;                               // semi-transparent backs = haze
  /** Pine clusters: [xCentre fraction, count, spread px], asymmetric clumps
   *  with gaps between them, never an even picket row. */
  trees?: { clusters: [ number, number, number ][], hMin: number, hMax: number, seed: number };
}

interface Ridge { d: string; fill: string; opacity: number; treeD: string; }

/**
 * A sweeping aurora ribbon: a broad arc whose thickness AND opacity taper to
 * ~zero towards its ends, so each tile shows a discrete curved light sweep,  * and the seam (where both are ~0) stays invisible when LINE repeats the strip.
 */
interface Ribbon {
  glow: string;            // full-thickness band (heavily blurred halo)
  core: string;            // narrower band (the bright body)
  crest: string;           // top edge (crisp light rim)
  gradId: string;
  color: string;           // halo colour
  coreColor: string;       // brighter body colour
  stops: { off: number, o: number }[];
}

interface Star { x: number; y: number; r: number; o: number; }

/**
 * The LINE "menu background" asset (i_11.png, a fixed 1472x150 strip behind
 * the bottom tab bar): an aurora-night panorama drawn live from the theme
 * tokens. A dark teal night sky with stars carries vivid mint aurora ribbons
 * sweeping diagonally (thickness + intensity taper into pointed tips, like
 * real curtains seen from afar) and a soft pink glow pooling on the horizon.
 * The mountains take their colours from the theme's windowBackground (hazy
 * back ranges) and editorBackground (dark front ridge + plain), with sparse
 * pines on the nearer ridges.
 *
 * LINE left-aligns this image and TILES it horizontally, so everything tiles:
 * ridges/ribbons use integer-frequency waves, every horizontal gradient's
 * first and last stops are identical, trees never touch the edges. The top is
 * FULLY transparent: the whole sky is painted inside a mask whose alpha is 0
 * above y=16, no glow or blur can ever reach the top edge.
 */
@Component( {
  selector: 'app-line-menu-bg',
  templateUrl: './line-menu-bg.component.html',
  styleUrls: [ './line-menu-bg.component.scss' ],
} )
export class LineMenuBgComponent implements OnChanges {

  @Input() product!: Product;

  readonly W = 1472;
  readonly H = 150;

  uid = 'mbg';

  // Dark teal night-sky shading.
  skyTop = '#0E2530';
  skyMid = '#11303D';
  skyLow = '#1A3E4C';

  // Soft aurora glow pooling on the horizon.
  horizonPink = '#E59FC6';

  stars: Star[] = [];
  ribbons: Ribbon[] = [];
  ridges: Ridge[] = [];

  /**
   * Per-theme design branch. The default is the aurora night panorama; Everest
   * gets its own "kawaii" daylight scene (owner 2026-07-16: the LINE market is
   * Japanese and competitors sell very cute drawings): a soft Fuji-silhouette
   * Everest with a scalloped snowcap, puffy cluster clouds, pastel sunrise
   * band and tiny sparkles, all in the theme's icy palette.
   */
  design: 'aurora' | 'arctic' | 'forest' | 'fall' | 'everest' | 'sakura' | 'tokyo' | 'tepui' | 'miami' | 'istanbul' | 'paris' | 'rio' | 'tallinn' | 'ocean' | 'salda' | 'prague' | 'sydney' | 'reynisfjara' | 'spring' | 'vienna' | 'madrid' | 'etna' | 'fuji' = 'aurora';

  // ---- Ocean ("ocean") scene fields: the seabed the theme's icon draws ----
  //
  // The constraint every scene in this family works under, and this is the first
  // one it actually HELPS. A classic tab glyph occupies y 13..93 of this
  // 150-unit strip and the labels y 95..120, so the band the glyphs land on has
  // to stay quiet. On a light theme that fight costs the scene its subject — see
  // Tallinn, whose roofline had to be held down at y 78. Here the quiet band is
  // simply DEEP WATER, which is what the top two thirds of a seabed is anyway.
  //
  // So the composition is the honest one: empty dark water where the glyphs go,
  // the coral garden rising into the tab GAPS, and the lit floor at the bottom.
  ocSkyTop = '#0A2429';
  ocSkyMid = '#0D3037';
  ocSkyLow = '#10373E';
  /** The bioluminescent currents the icon sweeps across its top half — kept LOW
   *  in opacity and low on the strip, because they cross the glyph band and a
   *  bright ribbon behind an active tab is the one thing that would undo it. */
  ocCurrents: { d: string; fill: string; o: number }[] = [];
  /** Rising bubbles, one path each so they can carry their own opacity. */
  ocBubbles: { cx: number; cy: number; r: number; o: number }[] = [];
  /** Seagrass blades, back rank first. */
  ocGrass: { d: string; fill: string; o: number }[] = [];
  /** The coral heads: the theme's own salmon, rose and tan. */
  ocCorals: { d: string; fill: string }[] = [];
  /** The lit floor the garden stands in. Its ramp's LAST stop is the tab bar's
   *  own fill, so the strip ends on exactly the colour LINE paints below it. */
  ocFloorD = '';
  /** Pebbles resting on the shelf, one path — what makes the floor a surface. */
  ocPebblesD = '';
  ocPebbleFill = '#1F4A50';
  /** KAWAII REEF LIFE (owner 2026-08-15: the strip read as empty water over a row
   *  of thorns). Three families, drawn at three depths: tube sponges standing in
   *  the garden, starfish resting on the floor, clownfish drifting in the tab
   *  gaps. Each is a flat list of {d,fill} so one creature can be several paths —
   *  a clownfish is a tail, a body, two bands and an eye, and it stops being
   *  cute the moment any of those is dropped to keep the array simple. */
  ocSponges: { d: string; fill: string }[] = [];
  ocStars: { d: string; fill: string }[] = [];
  ocFish: { d: string; fill: string }[] = [];
  ocFloorTop = '#123A40';
  ocFloorLow = '#123A40';

  // ---- Salda ("salda") scene fields: the white shore and the shallows ----
  //
  // Same constraint as every strip here: the classic tab glyph occupies y 13..93
  // and the labels y 95..120. This theme's glyphs are DARK on a LIGHT skin, so
  // the band they land on has to stay PALE — which puts the horizon at y 92 and
  // every saturated turquoise below it. That is one unit of margin, and it is why
  // the horizon is a hard number in this build rather than a composition choice.
  //
  // It also decides the water's order. Physically the deep turquoise belongs in
  // the middle distance with a paler haze at the horizon, and that happens to be
  // exactly what this constraint wants: pale at y 92, deepest at y 104, pale
  // again in the shallows. The theme's own icon paints it the same way.
  saSkyTop = '#F8FCFD';
  saSkyLow = '#E4F1F6';
  /** The low range across the far shore, back rank first. */
  saHills: { d: string; fill: string }[] = [];
  /** The water BEHIND the shoals, horizon band first. */
  saWater: { d: string; fill: string }[] = [];
  /** The two shallow bands drawn OVER the shoals, semi-transparent — what makes
   *  a shoal read as sand under water rather than as a sticker on top of it. */
  saWaterFront: { d: string; fill: string; o: number }[] = [];
  /** Foam: white edges where one band runs onto the next. */
  saFoam: { d: string; o: number }[] = [];
  /** Salda's BEYAZ ADALAR — the white sand shoals standing out in the shallows. */
  saIslands: { d: string; fill: string }[] = [];
  /** Shells and pebbles lying on the beach. A shell carries its own transform
   *  (it is the shared glyph, drawn in its own 120 box); a pebble is placed in
   *  strip coordinates and has none. */
  saShore: { d: string; fill: string; transform?: string }[] = [];
  /** The beach. Its ramp's LAST stop is the tab bar's own fill, so the sand of
   *  the scene and the fill of the bar are one surface. */
  saSandD = '';
  saSandTop = '#FFFDF7';
  saSandLow = '#FBF3E3';

  // ---- Prague ("prague") scene fields: Hradcany over the Vltava at night ----
  //
  // The one strip in this run whose constraint costs nothing at all. The glyph
  // band (y 13..93) has to stay quiet, and what belongs in the top of a night
  // skyline is NIGHT — so the sky is empty exactly where the tabs land, and every
  // lit thing sits below y 88 or stands in a tab gap.
  pgSkyTop = '#081524';
  pgSkyLow = '#123151';
  pgStars: { cx: number; cy: number; r: number; o: number }[] = [];
  /** The moon, in a tab gap — the one bright thing allowed inside the band. */
  pgMoonX = 1178;
  pgMoonY = 44;
  pgMoonR = 15;
  /** Thin cloud bands crossing it, the way the theme's own icon draws them. */
  pgClouds: { d: string; o: number }[] = [];
  /** The town: dark masonry first, then the gold-lit faces over it. */
  pgMasonry: { d: string; fill: string; transform?: string }[] = [];
  pgLit: { d: string; fill: string }[] = [];
  /** Every window in one path — they are all the same lamp gold. */
  pgWindowD = '';
  pgWindowFill = '#EFBA5A';
  /** The tree line along the bank. */
  pgTreeD = '';
  pgTreeFill = '#0B1A22';
  /** The Vltava. Its ramp's LAST stop is the tab bar's own fill, so the water of
   *  the scene and the fill of the bar are one surface. */
  pgRiverD = '';
  pgRiverTop = '#0A2138';
  pgRiverLow = '#123151';
  /** Lamplight lying on the water, in flat horizontal streaks. */
  pgGlints: { d: string; o: number }[] = [];

  // ---- Sydney ("sydney") scene fields: the harbour at midday ----
  //
  // Same constraint, third light theme: the tab glyphs are DARK and land in
  // y 13..93, so everything up there is sky. Sydney's own icon makes that easy —
  // it is two thirds sky, warm at the horizon and blue above, and the city sits
  // on a line about halfway down.
  sySkyTop = '#8FC9E6';
  sySkyLow = '#F7EFD2';
  /** The green headland along the far shore. */
  /** The two stops every Opera House shell is filled with. Each shell carries its
   *  OWN ramp between them (the def is in objectBoundingBox units), so the fan is
   *  six individually shaded shells rather than six flat ones. */
  syShellTop = '#FFFFFF';
  syShellLow = '#73B9D0';
  syLandD = '';
  syLandFill = '#5E9C55';
  /** The city: dark masonry, then the landmarks over it. */
  syCity: { d: string; fill: string; transform?: string }[] = [];
  /** The harbour. Its ramp's LAST stop is the tab bar's own fill. */
  syWaterD = '';
  syWaterTop = '#2FA3C9';
  syWaterLow = '#CAE4EC';
  /** The flat elliptical ripples the theme's icon draws all over its water. */
  syRipples: { d: string; o: number }[] = [];
  /** Anything floating ON the harbour rather than standing beside it. Drawn AFTER
   *  the water, which is the whole reason it is a separate list: the first pass
   *  put the sailboats in `syCity` and the water was then painted over their
   *  hulls, so only the mast tips showed and they read as specks. */
  syOnWater: { d: string; fill: string; transform?: string }[] = [];

  // ---- Reynisfjara ("reynisfjara") scene fields: the black beach at dusk ----
  //
  // The one composition in this run that argues with its own icon, on purpose.
  // The icon is a HIGH-KEY scene — a near-white overcast sky over black sand —
  // and that sky is unusable here: the tab glyphs land in y 13..93 and this
  // theme's skin is DARK, so its iOS-26 glass is dark too and its glyphs have to
  // be mid-toned to read on it. A near-white strip sky would need dark glyphs and
  // the glass would need light ones, and no colour is both.
  //
  // So the strip is the same beach at DUSK: a dark slate sky with the last of the
  // light along the horizon. "Cinematic" is one of this theme's own three tags,
  // and the place is at least as famous for its weather as for its light.
  reSkyTop = '#12161C';
  reSkyLow = '#39434F';
  /** The last light: a pale band lying on the horizon, under the glyph band. */
  reGlowD = '';
  /** The sea, and the surf lines running up the beach. */
  reSeaD = '';
  reSeaFill = '#242C35';
  reSurf: { d: string; o: number }[] = [];
  /** The rock: the column cliff, and the stacks standing offshore. */
  reRock: { d: string; fill: string; transform?: string }[] = [];
  /** The black sand. Its ramp's LAST stop is the tab bar's own fill. */
  reSandD = '';
  reSandTop = '#171C22';
  reSandLow = '#20262C';
  /** Wet sand: the sky lying on the beach in flat sheets, which is the thing
   *  everyone photographs there. */
  reWet: { d: string; o: number }[] = [];

  // ---- Spring ("spring") scene fields: the lupine meadow ----
  //
  // Fourth light theme, same constraint: the tab glyphs are DARK and land in
  // y 13..93, so everything up there stays pale. The icon makes it easy — it is a
  // blue sky with clouds over a low green range, and the flowers are all in the
  // bottom third.
  spSkyTop = '#79C6EC';
  spSkyLow = '#DDF3FB';
  spClouds: { d: string; o: number }[] = [];
  /** The low range across the back, two ranks. */
  spHills: { d: string; fill: string }[] = [];
  /** Grass: the back sward, then the blades, then the flower spikes over them. */
  spSward: { d: string; fill: string }[] = [];
  spBlades: { d: string; fill: string }[] = [];
  spSpikes: { d: string; fill: string }[] = [];
  /** The bird, hovering in a tab gap — the theme's own glyph in its own meadow. */
  spBirdD = '';
  spBirdFill = '#BA6173';
  spBirdTransform = '';
  /** The foreground haze. Its ramp's LAST stop is the tab bar's own fill. */
  spHazeD = '';
  spHazeTop = '#9BCF8A';
  spHazeLow = '#DCEFD2';

  // ---- Vienna ("vienna") scene fields: the auditorium ----
  //
  // The one INTERIOR in this run. Every other strip here is a landscape and hands
  // its bottom edge off to the tab fill with water, sand or grass; this one hands
  // it off with FURNITURE — the rows of red velvet seats run into the bar.
  //
  // The glyph band (y 13..93) is the dark of the ceiling and the upper tier's
  // shadowed openings, which is the quietest thing in a room like this and lets
  // the gold glyphs sit on it at 4.76:1.
  /** The lit panes of every street lamp, each with the halo it throws. The halo is
   *  a RADIAL GRADIENT in the template and never a flat fill — flat, it is a disc. */
  pgLamps: { iron: string; glass: string; ironFill: string; glassFill: string;
             transform: string; cx: number; cy: number; r: number }[] = [];
  /** Vienna's sheet of music: the paper, the ruling, the staff, the bar lines and
   *  the notes. The opera-house fields (tiers, gilding, stage, chandelier, stalls,
   *  seats) went with the scene they described. */
  viPaper = '#FAF4EF';
  viRule = '#C7B0A4';
  viStaffD = '';
  viBarsD = '';
  viNotes: { d: string; fill: string }[] = [];

  // ---- Madrid ("madrid") scene fields: Gran Via at sunset ----
  //
  // Fifth light theme, same constraint: DARK tab glyphs land in y 13..93, so the
  // sky and the upper storeys stay pale. That is what the hour does anyway — the
  // sun is low and behind the buildings, so their tops are lit and the street
  // under them is in shadow.
  maSkyTop = '#F6D9A8';
  maSkyLow = '#F9E7C4';
  /** The facades, dark first, then the lit stonework over them. */
  maBlocks: { d: string; fill: string; transform?: string }[] = [];
  /** Every window and balcony in one path — they are all the same shadow. */
  maWindowD = '';
  maWindowFill = '#8A6A3A';
  /** The street. Its ramp's LAST stop is the tab bar's own fill. */
  maStreetD = '';
  maStreetTop = '#D9BE8C';
  maStreetLow = '#F6E7C6';

  // ---- Etna ("etna") scene fields: the mountain at night ----
  //
  // The theme's icon puts a bright crimson sky ABOVE a black mountain, and that is
  // exactly backwards for a tab strip: the glyph band is y 13..93 and a saturated
  // crimson up there would swallow this theme's crimson glyphs. So the sky is
  // inverted — near-black at the top, heating toward the horizon — which is what a
  // night eruption actually looks like and is the same move Reynisfjara makes for
  // the same reason.
  etSkyTop = '#0A0909';
  etSkyLow = '#4A1C1C';
  /** The ash plume, drawn faint over the sky. */
  etPlume: { d: string; o: number }[] = [];
  /** The light the eruption throws into the sky — BLURRED. It has to be: drawn
   *  sharp it is a coloured dome sitting on the mountain, which is what the first
   *  pass shipped. */
  etGlow: { d: string; fill: string; o: number }[] = [];
  /** Stars, in the part of the sky the glow does not reach. */
  etStars: { cx: number; cy: number; r: number; o: number }[] = [];
  /** Cracks of light in the cooled lava field. */
  etCracks: { d: string; o: number }[] = [];
  /** The mountain: rock first, then everything hot on it. */
  etRock: { d: string; fill: string; transform?: string }[] = [];
  etHot: { d: string; fill: string; o?: number; transform?: string }[] = [];
  /** Embers rising off the crater. */
  etEmbers: { d: string; fill: string; transform: string; o: number }[] = [];
  /** The lava field. Its ramp's LAST stop is the tab bar's own fill. */
  etGroundD = '';
  etGroundTop = '#181414';
  etGroundLow = '#1E1D1D';

  // ---- Fuji ("fuji") scene fields: the mountain at dawn ----
  //
  // The last of the ten and the easiest constraint of them all: the tab glyphs are
  // DARK and land in y 13..93, and this theme's icon is a pastel sky with a pale
  // mountain in it. Nothing needed moving.
  //
  // What DID need deciding is what is absent. The icon's whole foreground is
  // cherry trees, and there is not one blossom anywhere in this build — Sakura is
  // a theme in the same set and the blossom is its entire identity.
  fuSkyTop = '#BCD7EC';
  fuSkyLow = '#FBE1D8';
  /** The sun, low and in a tab gap. */
  fuSunX = 294;
  fuSunY = 46;
  fuSunR = 20;
  /** Flat bands of cloud, the device a print of this mountain always uses. */
  fuClouds: { d: string; fill: string; o: number }[] = [];
  /** The mountain and the hills, back to front. */
  fuLand: { d: string; fill: string; transform?: string }[] = [];
  /** The dawn mist. Its ramp's LAST stop is the tab bar's own fill. */
  fuMistD = '';
  fuMistTop = '#F7D9D0';
  fuMistLow = '#FBEAE3';

  // ---- Tallinn ("tallinn") scene fields: the old town under snow ----
  //
  // Same constraint as Rio's, and it decides the whole composition: a classic tab
  // glyph sits at roughly y 25..80 of this 150-unit strip and the labels at
  // y 95..120. So the roofline is held at y 78 and below, everything that reaches
  // higher — the towers' cones, St Olaf's spire — stands in a TAB GAP, and the
  // sky the glyphs actually land on stays pale and empty.
  taSkyTop = '#EDF2FA';
  taSkyMid = '#DCE6F5';
  taSkyLow = '#D0DCEF';
  /** The back range of gables, flat and pale — the town seen through falling snow. */
  taBackD = '';
  taBackFill = '#C2CFE6';
  /** The houses: walls, then roofs, each its own colour. */
  taWalls: { d: string; fill: string }[] = [];
  taRoofs: { d: string; fill: string }[] = [];
  /** Every scrap of settled snow, in one path — ridges, sills, the tower cones. */
  taSnowD = '';
  taSnowFill = '#FFFFFF';
  /** Windows, one path: they are all the same dark. */
  taWindowD = '';
  taWindowFill = '#324979';
  /** The snow the town stands in. Its ramp's last stop is the tab bar's own fill,
   *  so the strip ends on exactly the colour LINE paints below it. */
  taGroundD = '';
  taGroundTop = '#FFFFFF';
  taGroundLow = '#4AC3E0';

  // ---- Rio ("rio") scene fields: Guanabara Bay at first light ----
  //
  // HIGH KEY on purpose, and it is the constraint the whole scene is built
  // around rather than a mood. Rio's classic tab glyphs are DEEP GREEN — the
  // theme's skin is light, so the lit glyph has to be the darker mark, the
  // inversion Paris ran into — and a LINE tab glyph sits roughly y 45..105 on
  // this 150px strip, i.e. straight across the horizon. So nothing in that band
  // may go dark: the morros are pale sage to mid green, never the theme's
  // Rainforest, and the deepest tones are kept for the water below the glyphs.
  riSkyTop = '#EAF6F4';
  riSkyMid = '#F4F7E8';
  riSkyLow = '#FBF3D8';
  /** The sun just up, low and to the left, with its warmth around it. */
  riSun = { cx: 300, cy: 100, r: 15 };
  riSunFill = '#F6E9A8';
  riGlowFill = '#ECDA61';
  /** The morros, back to front: each is a finished silhouette. */
  riHills: { d: string; fill: string }[] = [];
  /** The haze the range stands in. */
  riMistD = '';
  riMistFill = '#EDF6EA';
  /** The bay. */
  riWaterD = '';
  riWaterTop = '#CDE9EA';
  riWaterLow = '#8FCBAF';
  /** The near shore the whole scene is watched from, and the LAST thing in the
   *  strip: its gradient ends on the tab bar's own fill, so the 150px edge
   *  disappears under whatever LINE paints below it. */
  riShoreD = '';
  riShoreTop = '#3FB06A';
  riShoreLow = '#119B44';
  /** Flat light streaks on the water — never a blur, which would fade at the
   *  tile edge and bake a stripe into the seam. */
  riShimmer: { d: string; o: number }[] = [];
  riShimmerFill = '#FFFFFF';

  // ---- Roraima kawaii ("tepui") scene fields ----
  roSkyTop = '#E6C6CD';
  roSkyMid = '#F4D5B8';
  roSkyLow = '#F9E6CE';
  /** The setting sun, mostly swallowed by the cloud sea. */
  roSun = { cx: 470, cy: 122, r: 46 };
  roSunFill = '#F4D3A4';
  roGlowFill = '#E8A26B';
  /** The mountains: each one its OWN generated flat-topped silhouette, as a
   *  finished path. Not the shared tepui placed by transform — that stamped one
   *  recognisable outline across the strip nine times. */
  roWalls: { d: string; fill: string }[] = [];
  /** The hero's lit rim. Warm rather than GOLD: the first pass used #E8B98A and
   *  a saturated yellow band along a stepped rim read as torn paper stuck to the
   *  top of the wall. This sits a step off the wall's own colour, which is what
   *  a rim light actually looks like. */
  roCrownFill = '#DFA994';
  /** The cloud sea their bases stand in, and a lighter layer over it. */
  roMistD = '';
  roMistD2 = '';
  roMistFill = '#F6E7DC';
  roMistFill2 = '#FFF3EA';
  /** The near bank, ending exactly on the tab bar's own fill. */
  roGroundD = '';
  /** The near bank's crest. It has to bridge a pale sunset sky down to whatever
   *  colour the tab bar is, and the owner's skin turned out to be a dark brown,
   *  so this is a mid earth rather than the light clay the sand-skin pass used. */
  roGroundTop = '#7A5340';
  roGroundLow = '#9C5A4E';
  /** Palms standing in the haze at the mountains' feet — trunk and fronds kept
   *  apart so one can be filled and the other stroked. They replaced the banks
   *  of cloud that used to ride the mist line: one of those hung directly in
   *  front of the setting sun and read as a smear across it. */
  roTrees: { trunk: string; fronds: string; fill: string; sw: number }[] = [];
  /** Stars in the empty upper sky. The daylight version drifted pale cloud
   *  wisps here; on a night sky a pale cloud is the brightest thing on the strip
   *  and pulls the eye off the icons, so it is pinpricks instead. */
  roStars: { cx: number; cy: number; r: number; fill: string; o: number }[] = [];

  // ---- Miami neon-sunset skyline ("miami") scene fields ----
  // Built from the theme's own icon (owner 2026-08-13: "neon binalar, önde deniz
  // ve palmiyeler, gün batımı, miami icon svg'ye bakabilirsin"): a violet-to-
  // coral dusk, a low sun, a skyline in silhouette with lit windows, the bay in
  // front of it, and palms on the near shore.
  miSkyTop = '#5E2A6E';
  miSkyMid = '#A03F6C';
  miSkyLow = '#F5895F';
  /** The sun, sitting ON the horizon so the skyline cuts into it. */
  miSun = { cx: 520, cy: 86, r: 32 };
  miSunFill = '#FBD340';
  miGlowFill = '#FDBB5D';
  /** Two rows of towers: a far row for depth, a near row that carries the neon. */
  miFar: { x: number, y: number, w: number, h: number }[] = [];
  miNear: { x: number, y: number, w: number, h: number }[] = [];
  miFarFill = '#7E3260';
  miNearFill = '#4E1D3E';
  /** Lit windows, already positioned; drawn in the theme's Electric Aqua. */
  miWindows: { x: number, y: number, w: number, h: number }[] = [];
  miWindowFill = '#00D1C1';
  /** The bay: ONE sheet from the waterline to the bottom edge. It deepens at the
   *  waterline and lands on the tab bar's own colour, so the strip's bottom edge
   *  still disappears while the water is not one flat wash of pale pink. */
  miSeaFill = '#FECDE0';
  miSeaDeep = '#EE96AC';
  /** Where the WATER starts — deliberately below the line the city stands on. */
  miSeaTop = 126;
  /** Where the sun's disc is cut off: the line the city stands on, NOT the
   *  waterline. A setting sun goes down behind the land, so the slice of disc
   *  that used to show between the towers' feet and the water read as the sun
   *  floating in front of the city. */
  miSunClip = 112;
  miStreaks: { x: number, y: number, w: number, h: number, o: number }[] = [];
  miStreakFill = '#F0A03C';   // denser than the sun: it has to read on pale pink
  /** What LINE paints below the strip. The bay is filled with exactly this, so
   *  the strip's bottom edge disappears. */
  miShoreFill = '#FECDE0';

  // ---- Istanbul dawn-Bosphorus ("istanbul") scene fields ----
  // The Bosphorus at first light, seen from the water: a COOL sky held at one
  // mid tone across the whole icon band, the warm dawn haze pushed right down
  // onto the far shore, the old city in silhouette on it, and the strait in
  // front landing on the tab bar's own fill.
  isSkyTop = '#0C97BA';
  isSkyMid = '#3DB9CA';
  isSkyLow = '#9AD7E0';
  /** The warm haze. It starts at isHazeTop with zero opacity and only reaches
   *  isHazeMax down on the shore, which is what keeps it a DAWN BAND rather
   *  than a pale sky everywhere. */
  isHazeTop = 84;
  isHazeFill = '#D1A493';
  isHazeMax = 0.6;
  /** First light: the sun still under the horizon, its glow spread flat along
   *  the far shore. Wide and shallow on purpose — a rounder glow climbs into
   *  the glyph band. */
  isGlow = { cx: 640, cy: 120, rx: 500, ry: 58 };
  isGlowFill = '#E1D8A6';
  /** The last of the night's moon, a waning crescent with its lit limb turned
   *  toward the coming sun. */
  isMoonD = '';
  isMoonFill = '#EFEAD0';
  /** The old city, in three depth layers: a far mass of roofs, domes and
   *  minarets; a nearer, darker row of mosque complexes; and the waterfront
   *  itself, which is the darkest and lies lowest. */
  isRoofD = '';
  isFarD = '';
  isNearD = '';
  isShoreD = '';
  isRoofFill = '#B5A6A9';
  isFarFill = '#A4939C';
  isNearFill = '#866F7A';
  isShoreFill = '#6C5865';
  /** Where the strait starts. Everything below it is water. */
  isWaterTop = 117;
  isWaterDeep = '#0C8298';
  isWaterMid = '#1FA5BE';
  /** The last stop of the water ramp = lineTabFill('istanbul'). */
  isWaterLow = '#4AC3E0';
  /** Dawn light broken on the strait, under the glow. */
  isGlints: { x: number, y: number, w: number, h: number, o: number }[] = [];
  isGlintFill = '#EFEAD0';
  /** Two Bosphorus ferries crossing, and the wake behind them. */
  /** The Bosphorus bridge over the strait (owner 2026-08-14: "ortada boğaz
   *  olacak köprü olacak"). Two H-towers, the main cable, the deck — and NO
   *  vertical hangers, which is the icon's own simplification and the reason
   *  the thing survives being 150px tall. */
  isBridgeD = '';
  isCableD = '';
  isBridgeFill = '#B5544F';
  isCableFill = '#8E3F3C';
  isDeckLine = '#FAFDFD';
  isDeckLineD = '';

  isFerries: { d: string, fill: string }[] = [];
  isWakeFill = '#B6E1E1';
  isWakes: { x: number, y: number, w: number, o: number }[] = [];

  // ---- Paris night-Seine ("paris") scene fields ----
  // The Seine after dark, seen over a bridge arcade. Top to bottom: a plum-black
  // night sky with the city's own glow pooled low on it, the skyline the theme's
  // own icon draws — Arc de Triomphe, the Eiffel Tower centre and dominant,
  // Notre-Dame, plus a Panthéon-ish dome to answer them on the right — in
  // silhouette on the far bank behind a run of Haussmann roofs, and TWELVE
  // arched bays (Pont Neuf's count) carrying the river across the strip.
  //
  // The arcade is what makes this design tile. Its period is exactly W/12, so
  // the arch rhythm continues across the join by construction rather than by
  // fudging the edges, and the seam falls down the centre line of a pier.
  //
  // THE SKIN. This scene was drafted against Roraima's dark brown as a stand-in,
  // because Paris had no LINE colour set yet; while it was being drawn the owner
  // picked the real one and it is the opposite — a LIGHT ROSE skin whose chat
  // input bar, and therefore whose classic tab fill, is the pale pink #FDBCC8
  // (line-color-set.ts, sampled 2026-08-14). Nothing here hardcodes it: the
  // water's last stop is lineTabFill(), so the strip followed the skin change
  // with no edit, and it is what turns the river under the arches into the pale
  // lit sheet the theme's own icon puts at the foot of the avenue.
  paSkyTop = '#0C0A11';
  paSkyMid = '#221A24';
  paSkyLow = '#593645';
  paStars: { cx: number; cy: number; r: number; o: number }[] = [];
  paStarFill = '#B6C2DE';
  /** The city's own light on the sky. Deliberately DIM and pushed low: the five
   *  tab glyphs sit over roughly y=30..110 and the brightest passage must not be
   *  in that band, so the sky halo only lifts the silhouettes off the sky and
   *  the real light lives on the water, below the glyphs. */
  paGlow = { cx: 520, cy: 84, rx: 560, ry: 32 };
  /** A second, narrower glow standing behind the Eiffel Tower. It exists for one
   *  measured reason: the tower's base is read against the bright horizon and its
   *  top against a near-black sky, and NO single fill silhouettes in both. Light
   *  behind the whole tower lets one dark fill work over its full height. */
  paHeroGlow = { cx: 520, cy: 86, rx: 250, ry: 66 };
  paGlowFill = '#D5A24C';
  /** The far bank: monuments, then the roofs in front of them, then the quay
   *  stone and its plane trees. Each layer a step DARKER than the one behind —
   *  aerial perspective is what keeps a night skyline readable at 150px. */
  paCityD = '';
  paCityFill = '#653F41';
  /** The Eiffel Tower, kept as its OWN path so it can carry its own, lighter
   *  fill — the floodlit gold that is the only reason it wins a 150px frame it
   *  cannot win on size. */
  paHeroD = '';
  paHeroFill = '#8A5B45';
  paRoofD = '';
  paRoofFill = '#1F171B';
  paQuayD = '';
  paQuayFill = '#151014';
  /** Lit windows along the roofline. A dozen 1px warm marks: the one thing that
   *  says the silhouette is a CITY at night rather than a hill. */
  paWindows: { x: number; y: number; s: number; o: number }[] = [];
  /** Paris's windows as ONE path. The night design drew them as individual lit
   *  rectangles with their own opacities; the light one cuts them as arched
   *  openings in the stone, which is one fill and one path. */
  paWindowD = '';
  /** Paris's quay trees, and the light green they are drawn in. */
  paTreeD = '';
  paTreeFill = '#9DCBA6';
  paTrunkD = '';
  paTrunkFill = '#996D59';
  paWindowFill = '#F1C970';
  /** Where the Seine starts. Everything below it is water. */
  paWaterTop = 82;
  paWaterDeep = '#161222';
  paWaterMid = '#3A2429';
  /** The last stop of the water ramp = lineTabFill('paris'). */
  paWaterLow = '#422C21';
  /** The city's glow pooled ON the river — the brightest thing on the strip, and
   *  it sits at y≈118, i.e. BELOW the tab glyphs. It shows through the arcade's
   *  bays, which is the whole reason the arches read at all. */
  paRiverGlow = { cx: 520, cy: 118, rx: 470, ry: 28 };
  paGlints: { x: number; y: number; w: number; h: number; o: number }[] = [];
  paGlintFill = '#FBBA77';
  /** The arcade itself: ONE polygon, deck-bottom across the top and an
   *  undulating bottom edge that dips to the pier feet and rises over each arch.
   *  Not a slab with subtracted openings — SVG's nonzero rule cancels opposite
   *  windings where subpaths overlap, which is what punched holes through
   *  Istanbul's bridge towers. */
  paBridgeD = '';
  paBridgeFill = '#141017';
  paDeckTop = 90;
  paDeckBot = 98;
  paCopingFill = '#3B2A35';
  paBalusterD = '';
  paBalusterFill = '#2A1F28';
  /** The arch rings + the pier imposts, the only drawn detail with any lift in
   *  it. Both live at y>=104, under the glyph band. */
  paArchRingD = '';
  paArchRingFill = '#4A3542';
  paImpostD = '';
  /** Six parapet lamps — the one Paris signature that has to be light. Small on
   *  purpose (r=2.1 globes): they are the only bright marks inside the glyph
   *  band and they are spaced W/6 apart, so at most two ever fall behind a tab. */
  paLampPostD = '';
  paLampPostFill = '#241A22';
  paLamps: { cx: number; cy: number }[] = [];
  paLampFill = '#FBBA77';
  paLampR = 2.1;
  paLampHaloR = 10;
  paRefl: { x: number; y: number; w: number; h: number; o: number }[] = [];

  // ---- Autumn kawaii ("fall") scene fields ----
  auSkyTopF = '#FDEFE1';
  auSkyLowF = '#F7E7DA';
  /** One maple: a trunk plus the transforms of the leaves its crown is built
   *  from, drawn as a group so a single opacity covers the whole tree. */
  fallTrees: { trunk: string; leaves: string[]; fill: string; o: number }[] = [];
  fallLeaves: { transform: string; fill: string; o: number }[] = [];
  fallGroundD = '';
  /** Soil: the band the trees stand in. It is a GRADIENT, from earth at the
   *  ground line down to exactly the tab bar's own fill at y=150, so the strip
   *  has no visible bottom edge — whatever LINE paints below the 150px asset is
   *  the same colour the asset ends on. */
  fallSoilTop = '#B57C42';
  fallSoilLow = '#C7813B';
  readonly mapleBlade = MAPLE_BLADE;

  // ---- Sequoia kawaii ("forest") scene fields ----
  seqSkyTop = '#DDE8CE';
  seqSkyLow = '#FCFCF3';
  seqStars: Star[] = [];
  seqFireflies: { x: number; y: number; r: number; o: number }[] = [];
  /** Tree rows, far to near: [d, fill, opacity]. */
  seqRows: { d: string; fill: string; o: number }[] = [];
  seqGroundD = '';
  seqGroundFill = '#20231C';

  // ---- Aurora kawaii ("arctic") scene fields ----
  // The realistic 'aurora' panorama above stays the FALLBACK for every theme
  // that has no scene of its own; only aurora-borealis moves to this one.
  auSkyTop = '#122A36';
  auSkyLow = '#131313';
  auMoon = { cx: 120, cy: 46, r: 17 };
  auMoonFill = '#EAF6F2';
  auCraters: { cx: number; cy: number; r: number }[] = [];
  auStars: Star[] = [];
  auSparkles: { d: string; fill: string; o: number }[] = [];
  /** ONE aurora band (owner 2026-08-12: no more separate ribbons) — a single
   *  tapered sweep filled with a gradient that runs green → violet → blue along
   *  its length, plus the same shape widened as its halo. */
  auBandD = '';
  auBandHaloD = '';
  auBandStops: { off: number; color: string }[] = [];
  auBandOpacity = 0.55;
  /** The lights themselves: tapered sweeps, each already filled with a colour. */
  auRibbons: { d: string; fill: string; core: string; w: number; cw: number; o: number }[] = [];
  /** Aurora curtains standing on the horizon, `ci` indexing auCurtainColors. */
  auCurtains: { d: string; ci: number }[] = [];
  auCurtainColors: string[] = [];
  /** Soft pool of light along the horizon under the curtains. */
  auGlowD = '';
  /** Horizontal aurora bands: wavy, overlapping, blurred and semi-transparent. */
  auBands: { d: string; fill: string; o: number }[] = [];
  auHillBackD = '';
  auHillBackFill = '#1C3D4B';
  auHillFrontD = '';
  auHillFrontFill = '#131313';
  /** The front hill's top edge, drawn as a thin highlight. */
  auCrestD = '';
  /** Scalloped snow caps sitting on the hill crests. */
  auCaps: string[] = [];
  auPines: { d: string; o: number }[] = [];

  // ---- Tokyo kawaii skyline fields ----
  tkSkyTop = '#4A3357';
  tkSkyLow = '#B99EC5';               // == the tab-fill below, so the strip blends into it
  tkMoon = { cx: 235, cy: 40, r: 20 };
  tkStars: { x: number; y: number; r: number; o: number }[] = [];
  tkBuildings: { d: string; fill: string }[] = [];
  tkWindows: { x: number; y: number; s: number; fill: string; o: number }[] = [];
  tkGroundTop = '#A98FB8';             // ground/road band top (fades to the fill)
  tkTowerD = '';
  tkTowerFill = '#E8846B';
  tkTowerDecks: { x: number; y: number; w: number; h: number }[] = [];

  // ---- Sakura kawaii scene fields ----
  /** Unit cherry-blossom petal: rounded body, base at the origin, tip up (−y),
   *  a tiny V-notch at the tip. Reused for drifting petals and the five-petal
   *  blossoms (rotated around the origin). */
  readonly petalD = 'M0,0 C-2.2,-1.5 -2.0,-5.0 -0.4,-6.0 L0,-5.4 L0.4,-6.0 C2.0,-5.0 2.2,-1.5 0,0 Z';
  skTop = '#FEFCFC';
  skLow = '#FBE7EF';
  skHaze = '#E898B4';
  skGroundD = '';
  skGroundFill = '#E6F0D8';
  skPlainFill = '#D8E7C4';
  skTrunkD = '';
  skBranchD = '';
  skTrunkFill = '#8A6650';
  skLeafFill = '#CFE3BA';
  skDotFill = '#CB6B91';
  skCanopy: { cx: number; cy: number; r: number; fill: string; o: number }[] = [];
  skLeaves: { cx: number; cy: number; r: number }[] = [];
  skDots: { cx: number; cy: number; r: number }[] = [];
  skBushes: { circles: { cx: number; cy: number; r: number }[]; fill: string; o: number }[] = [];
  skClouds: { o: number; circles: { cx: number; cy: number; r: number }[] }[] = [];
  skPetals: { transform: string; fill: string; o: number }[] = [];
  skBlossoms: { cx: number; cy: number; s: number; o: number; petalFill: string; centerFill: string; petalTransforms: string[] }[] = [];
  skSparkles: { d: string; fill: string; o: number }[] = [];

  evSkyTop = '#F4F9FC';
  evSkyLow = '#DCE9F2';
  evPeach = '#F6C6AC';
  evMainD = '';
  evMainFill = '#9DBBD0';
  evCapD = '';
  evPeaks: { d: string; fill: string; o: number }[] = [];
  evClouds: { o: number; front: boolean; circles: { cx: number; cy: number; r: number }[] }[] = [];
  evSparkles: { d: string; fill: string; o: number }[] = [];
  evGroundD = '';
  evGroundFill = '#F0F6FA';
  evPlainFill = '#E5EEF4';

  ngOnChanges(): void {
    const p = this.product;
    if ( !p ) {
      return;
    }
    this.uid = 'mbg-' + ( p.key || 'x' );

    if ( p.key === 'everest' ) {
      this.design = 'everest';
      this.buildEverest( p );
      return;
    }
    if ( p.key === 'sakura' ) {
      this.design = 'sakura';
      this.buildSakura( p );
      return;
    }
    if ( p.key === 'tokyo' ) {
      this.design = 'tokyo';
      this.buildTokyo( p );
      return;
    }
    if ( p.key === 'aurora-borealis' ) {
      this.design = 'arctic';
      this.buildArctic( p );
      return;
    }
    if ( p.key === 'sequoia' ) {
      this.design = 'forest';
      this.buildForest( p );
      return;
    }
    if ( p.key === 'autumn' ) {
      this.design = 'fall';
      this.buildFall( p );
      return;
    }
    if ( p.key === 'roraima' ) {
      this.design = 'tepui';
      this.buildTepui( p );
      return;
    }
    if ( p.key === 'miami' ) {
      this.design = 'miami';
      this.buildMiami( p );
      return;
    }
    if ( p.key === 'istanbul' ) {
      this.design = 'istanbul';
      this.buildIstanbul( p );
      return;
    }
    if ( p.key === 'paris' ) {
      this.design = 'paris';
      this.buildParis( p );
      return;
    }
    if ( p.key === 'rio-de-janeiro' ) {
      this.design = 'rio';
      this.buildRio( p );
      return;
    }
    if ( p.key === 'ocean' ) {
      this.design = 'ocean';
      this.buildOcean( p );
      return;
    }
    if ( p.key === 'salda' ) {
      this.design = 'salda';
      this.buildSalda( p );
      return;
    }
    if ( p.key === 'prague' ) {
      this.design = 'prague';
      this.buildPrague( p );
      return;
    }
    if ( p.key === 'sydney' ) {
      this.design = 'sydney';
      this.buildSydney( p );
      return;
    }
    if ( p.key === 'reynisfjara' ) {
      this.design = 'reynisfjara';
      this.buildReynisfjara( p );
      return;
    }
    if ( p.key === 'spring' ) {
      this.design = 'spring';
      this.buildSpring( p );
      return;
    }
    if ( p.key === 'vienna' ) {
      this.design = 'vienna';
      this.buildVienna( p );
      return;
    }
    if ( p.key === 'madrid' ) {
      this.design = 'madrid';
      this.buildMadrid( p );
      return;
    }
    if ( p.key === 'etna' ) {
      this.design = 'etna';
      this.buildEtna( p );
      return;
    }
    if ( p.key === 'fuji' ) {
      this.design = 'fuji';
      this.buildFuji( p );
      return;
    }
    if ( p.key === 'tallinn' ) {
      this.design = 'tallinn';
      this.buildTallinn( p );
      return;
    }
    this.design = 'aurora';

    const t: any = p.designTokens || {};
    const cp = ( p.compactPalette?.length ? p.compactPalette : p.palette ) || [];
    const wb = t.windowBackground || cp[ 0 ] || '#123243';
    const eb = t.editorBackground || t.windowBackground || '#142B37';
    const sf = t.surface || this.lighten( wb, 0.06 );
    const blue = cp[ 1 ] || t.primaryColor || '#549EFF';
    const teal = cp[ 2 ] || t.secondaryColor || '#1ECFB6';
    const pink = cp[ 3 ] || t.tertiaryColor || '#BB719B';
    const lav = cp[ 4 ] || '#BAA5FF';

    // Electric aurora colours from the SHARED trio (line-electric-colors.ts),
    // so the ribbons and the tab ON glyphs match 1:1.
    const trio = electricTrio( p );
    const electricBlue = trio.blue;
    const electricBlueCore = trio.blueCore;
    const electricGreen = trio.green;
    const electricGreenCore = trio.greenCore;
    const electricPink = trio.pink;
    const electricPinkCore = trio.pinkCore;
    this.horizonPink = vividHex( pink, 0.30, 0.16 );

    // ---- night sky: blue-leaning, built on the surface token (the pure
    // editorBackground version read too dark/navy) ----
    this.skyTop = mixHex( sf, blue, 0.14 );
    this.skyMid = this.lighten( mixHex( sf, blue, 0.20 ), 0.03 );
    this.skyLow = this.lighten( mixHex( sf, blue, 0.30 ), 0.08 );

    // ---- stars (deterministic; painted behind the ribbons) ----
    const fr = ( v: number ) => v - Math.floor( v );
    this.stars = [];
    for ( let i = 0; i < 30; i++ ) {
      this.stars.push( {
        x: Math.round( 14 + fr( i * 0.6180339887 + 0.27 ) * ( this.W - 28 ) ),
        y: Math.round( 22 + fr( i * 0.7548776662 + 0.49 ) * 60 ),
        r: +( 0.7 + fr( i * 0.8736592734 + 0.13 ) * 0.9 ).toFixed( 2 ),
        o: +( 0.30 + fr( i * 0.5497004779 + 0.71 ) * 0.55 ).toFixed( 2 ),
      } );
    }

    // ---- sweeping aurora ribbons (each a discrete tapered arc) ----
    // [base, crest harmonics, xPeak (fraction), tMax, colour, coreColour, stops]
    this.ribbons = [
      // Hero electric-blue sweep, dead centre (circle colour 2), sits behind
      // the Chats tab, strongest of the three. Phase lifts the crest HIGH at
      // the centre (it dives low at the seam, where opacity is 0 anyway).
      this.ribbon( 0.40, [ [ 1, 0.150, 1.9 ], [ 2, 0.025, 1.3 ] ], 0.50, 0.19, electricBlue, electricBlueCore,
        [ { off: 0, o: 0 }, { off: 0.15, o: 0.05 }, { off: 0.32, o: 0.50 }, { off: 0.50, o: 1.0 }, { off: 0.68, o: 0.45 }, { off: 0.88, o: 0 }, { off: 1, o: 0 } ] ),
      // Electric-green arc up on the left (circle colour 3, pushed greener),       // behind the Home tab.
      this.ribbon( 0.26, [ [ 1, 0.105, 1.0 ], [ 3, 0.020, 2.7 ] ], 0.20, 0.11, electricGreen, electricGreenCore,
        [ { off: 0, o: 0 }, { off: 0.08, o: 0.30 }, { off: 0.20, o: 0.80 }, { off: 0.42, o: 0.28 }, { off: 0.65, o: 0.05 }, { off: 1, o: 0 } ] ),
      // Electric-pink arc low on the right (circle colour 4), behind Calls.
      this.ribbon( 0.54, [ [ 1, 0.075, 5.6 ], [ 2, 0.020, 0.4 ] ], 0.82, 0.11, electricPink, electricPinkCore,
        [ { off: 0, o: 0 }, { off: 0.55, o: 0.10 }, { off: 0.70, o: 0.50 }, { off: 0.82, o: 0.95 }, { off: 0.96, o: 0 }, { off: 1, o: 0 } ] ),
    ];

    // ---- mountains + ground: window/surface tones pulled toward blue (the
    // editorBackground versions read too dark); pines in asymmetric clumps ----
    const specs: RidgeSpec[] = [
      { base: 0.58, cusps: [ [ 5, 0.090, 0.7 ], [ 9, 0.040, 2.4 ] ], roll: [ 2, 0.030, 1.3 ],
        fill: mixHex( this.lighten( wb, 0.30 ), blue, 0.15 ), opacity: 0.50 },
      { base: 0.66, cusps: [ [ 4, 0.120, 2.9 ], [ 7, 0.045, 0.4 ] ], roll: [ 3, 0.025, 4.1 ],
        fill: mixHex( this.lighten( wb, 0.16 ), blue, 0.12 ), opacity: 0.75 },
      { base: 0.75, cusps: [ [ 3, 0.140, 5.1 ], [ 8, 0.050, 1.7 ] ], roll: [ 2, 0.030, 2.8 ],
        fill: mixHex( this.lighten( sf, 0.04 ), blue, 0.10 ), opacity: 1,
        trees: { clusters: [ [ 0.07, 4, 55 ], [ 0.30, 2, 26 ], [ 0.54, 5, 70 ], [ 0.82, 3, 38 ] ], hMin: 6, hMax: 12, seed: 3 } },
      { base: 0.835, cusps: [ [ 2, 0.130, 1.9 ], [ 5, 0.050, 3.7 ] ], roll: [ 3, 0.020, 0.9 ],
        fill: mixHex( this.lighten( sf, -0.22 ), blue, 0.08 ), opacity: 1,
        trees: { clusters: [ [ 0.16, 3, 48 ], [ 0.47, 1, 10 ], [ 0.66, 4, 58 ], [ 0.93, 2, 26 ] ], hMin: 8, hMax: 16, seed: 7 } },
      { base: 0.90, cusps: [], roll: [ 3, 0.012, 2.2 ],
        fill: mixHex( this.lighten( sf, -0.45 ), blue, 0.06 ), opacity: 1 },
    ];
    this.ridges = specs.map( s => ( {
      d: this.ridgePath( s ),
      fill: s.fill,
      opacity: s.opacity,
      treeD: s.trees ? this.forest( s ) : '',
    } ) );
  }

  /** Build one tapered aurora arc. Thickness follows a wrapped sine peaking at
   *  xPeak and melting to ~0 opposite it, so the arc has pointed tips and the
   *  tile seam (where opacity is 0 too) is invisible. */
  private ribbon(
    base: number, hs: [ number, number, number ][], xPeak: number, tMax: number,
    color: string, coreColor: string, stops: { off: number, o: number }[]
  ): Ribbon {
    const tp = Math.PI / 2 - 2 * Math.PI * xPeak;
    const taper = ( x: number ): number =>
      Math.pow( 0.5 + 0.5 * Math.sin( 2 * Math.PI * x / this.W + tp ), 1.3 );
    return {
      glow: this.taperedBand( base, hs, tMax, taper ),
      core: this.taperedBand( base, hs, tMax * 0.45, taper ),
      crest: this.crest( base, hs ),
      gradId: `${ this.uid }-rb${ Math.round( xPeak * 100 ) }`,
      color,
      coreColor,
      stops,
    };
  }

  /** y of a tiling wave at x: base + integer-frequency harmonics. */
  private waveY( x: number, base: number, hs: [ number, number, number ][] ): number {
    let y = this.H * base;
    for ( const [ k, a, ph ] of hs ) {
      y += this.H * a * Math.sin( 2 * Math.PI * k * x / this.W + ph );
    }
    return y;
  }

  /** Open crest path of a tiling wave. */
  private crest( base: number, hs: [ number, number, number ][] ): string {
    const pts: string[] = [];
    for ( let x = 0; x <= this.W; x += 6 ) {
      pts.push( `${ x },${ this.waveY( x, base, hs ).toFixed( 1 ) }` );
    }
    return 'M' + pts.join( ' L' );
  }

  /** A band under a crest whose thickness is scaled pointwise by taper(x). */
  private taperedBand( base: number, hs: [ number, number, number ][], tMax: number, taper: ( x: number ) => number ): string {
    const top: string[] = [];
    const bot: string[] = [];
    for ( let x = 0; x <= this.W; x += 6 ) {
      const y = this.waveY( x, base, hs );
      const th = Math.max( 0.6, this.H * tMax * taper( x ) );
      top.push( `${ x },${ y.toFixed( 1 ) }` );
      bot.push( `${ x },${ ( y + th ).toFixed( 1 ) }` );
    }
    return 'M' + top.join( ' L' ) + ' L' + bot.reverse().join( ' L' ) + ' Z';
  }

  /** Ridge height at x: cusped peaks (1-|sin|) + a smooth roll. */
  private ridgeY( s: RidgeSpec, x: number ): number {
    let y = s.base;
    for ( const [ k, a, ph ] of s.cusps ) {
      y -= a * ( 1 - Math.abs( Math.sin( Math.PI * k * x / this.W + ph ) ) );
    }
    const [ rk, ra, rp ] = s.roll;
    y -= ra * ( 0.5 + 0.5 * Math.sin( 2 * Math.PI * rk * x / this.W + rp ) );
    return this.H * y;
  }

  private ridgePath( s: RidgeSpec ): string {
    const pts: string[] = [];
    for ( let x = 0; x <= this.W; x += 4 ) {
      pts.push( `${ x },${ this.ridgeY( s, x ).toFixed( 1 ) }` );
    }
    return 'M' + pts.join( ' L' ) + ` L${ this.W },${ this.H } L0,${ this.H } Z`;
  }

  /** Pines in asymmetric CLUMPS along a ridge (an evenly-spread row read like
   *  a graveyard): each cluster scatters its trees around a centre with
   *  strongly varied heights. Deterministic (reproducible exports); margins
   *  keep the edges clean so the strip still tiles. */
  private forest( s: RidgeSpec ): string {
    const trees = s.trees!;
    const parts: string[] = [];
    const fr = ( v: number ) => v - Math.floor( v );
    let i = 0;
    for ( const [ xf, n, spread ] of trees.clusters ) {
      for ( let j = 0; j < n; j++, i++ ) {
        const jitter = ( fr( i * 0.6180339887 + trees.seed * 0.37 ) - 0.5 ) * 2 * spread;
        const x = Math.min( this.W - 14, Math.max( 14, this.W * xf + jitter ) );
        const h = trees.hMin + fr( i * 0.7548776662 + trees.seed * 0.61 ) * ( trees.hMax - trees.hMin );
        const y = this.ridgeY( s, x ) + 1;
        const w = h * 0.8;
        parts.push(
          `M${ ( x - w / 2 ).toFixed( 1 ) },${ y.toFixed( 1 ) } L${ x.toFixed( 1 ) },${ ( y - h * 0.65 ).toFixed( 1 ) } L${ ( x + w / 2 ).toFixed( 1 ) },${ y.toFixed( 1 ) } Z` +
          `M${ ( x - w * 0.33 ).toFixed( 1 ) },${ ( y - h * 0.42 ).toFixed( 1 ) } L${ x.toFixed( 1 ) },${ ( y - h ).toFixed( 1 ) } L${ ( x + w * 0.33 ).toFixed( 1 ) },${ ( y - h * 0.42 ).toFixed( 1 ) } Z`
        );
      }
    }
    return parts.join( ' ' );
  }

  // ---- Autumn kawaii ("fall") scene ----

  /**
   * Autumn's treeline, on EVEREST's structure (owner 2026-08-13: design it like
   * Everest): a pale warm sky, soft round canopies instead of Everest's peaks,
   * a few of the theme's own maple leaves drifting where Everest puts sparkles,
   * and a rolling ground grounding the tab icons.
   *
   * Same constraint as every strip in this family: the five tab glyphs sit over
   * it, so the canopies stay LOW and PALE and the upper field stays quiet. Rows
   * carry equal heights at x=0 and x=W and nothing touches an edge, so the strip
   * tiles when LINE repeats it, and the hero sits in the left ~880px.
   *
   * PROVISIONAL: no LINE skin has been picked for Autumn yet, so the sky lands
   * on the theme's own cream rather than a sampled tab-bar fill.
   */
  /**
   * Roraima's sunset over the tepuis.
   *
   * The scene is the theme's own icon, opened out: a table mountain standing in
   * a cloud sea under a sunset, with the sun going down behind it. Four more
   * walls recede behind the hero, each smaller, paler and a touch higher in the
   * frame, which is the only way a flat strip gets depth.
   *
   * PALE, deliberately. Five tab glyphs are drawn over this and the icon band is
   * roughly y=30..110, so the walls climb straight through it — the same problem
   * Sequoia's grove hit, with the same answer: aerial perspective. Distant
   * tepuis really are washed out by the air between, so the physically honest
   * drawing is also the one that leaves the icons legible. Only the hero's lit
   * rim and the near bank carry any real saturation, and both sit low.
   *
   * TILING (LINE repeats this strip and rejected Aurora for failing it): the
   * cloud sea and the near bank are sums of INTEGER-frequency sines, so their
   * height AND their slope match at x=0 and x=W — equal heights alone leave a
   * V-notch at every seam. No wall, wisp or sun comes near an edge, and nothing
   * here is blurred to the edge, so there is no blur fade to bake in either.
   */
  /**
   * A closed path through `pts` with every corner ROUNDED by `r`.
   *
   * This is what makes the mountains kawaii rather than blunt (owner 2026-08-13:
   * "dağlar kawaii olacak, çok küt ve özensiz yaptın"). The family's cute scenes
   * are all flat fills with soft forms — Sakura's canopy, Everest's clouds,
   * Aurora's hills — and a mesa drawn as a hard polygon is the one shape that
   * cannot pass for one. The radius is clamped per corner to half the shorter
   * adjacent edge, so short rim steps round off without collapsing.
   */
  private roRound( pts: [ number, number ][], r: number ): string {
    const f = ( v: number ) => +v.toFixed( 1 );
    const n = pts.length;
    let d = '';
    for ( let i = 0; i < n; i++ ) {
      const prev = pts[ ( i - 1 + n ) % n ];
      const cur = pts[ i ];
      const next = pts[ ( i + 1 ) % n ];
      const v1 = [ prev[ 0 ] - cur[ 0 ], prev[ 1 ] - cur[ 1 ] ];
      const v2 = [ next[ 0 ] - cur[ 0 ], next[ 1 ] - cur[ 1 ] ];
      const l1 = Math.hypot( v1[ 0 ], v1[ 1 ] ) || 1;
      const l2 = Math.hypot( v2[ 0 ], v2[ 1 ] ) || 1;
      const rr = Math.min( r, l1 / 2, l2 / 2 );
      const a: [ number, number ] = [ cur[ 0 ] + ( v1[ 0 ] / l1 ) * rr, cur[ 1 ] + ( v1[ 1 ] / l1 ) * rr ];
      const b: [ number, number ] = [ cur[ 0 ] + ( v2[ 0 ] / l2 ) * rr, cur[ 1 ] + ( v2[ 1 ] / l2 ) * rr ];
      d += i === 0 ? `M${ f( a[ 0 ] ) },${ f( a[ 1 ] ) }` : ` L${ f( a[ 0 ] ) },${ f( a[ 1 ] ) }`;
      d += ` Q${ f( cur[ 0 ] ) },${ f( cur[ 1 ] ) } ${ f( b[ 0 ] ) },${ f( b[ 1 ] ) }`;
    }
    return d + ' Z';
  }

  /**
   * The mountain profiles, AUTHORED one at a time.
   *
   * The pass before this generated them from a hash and the owner's verdict was
   * "baştan savma" — and it was: a hash gives you variety without giving you
   * design, so every mesa came out the same blunt trapezoid with the steps in
   * different places. These are drawn deliberately instead, each with a
   * character of its own, the way the theme's traced tepui has one.
   *
   * Coordinates are in a unit box: x runs 0..1 across the mountain, y runs 0 at
   * the foot to 1 at the rim, so one profile can be placed at any size. Every
   * one keeps a level top — that is the whole subject — and differs in how it
   * gets there.
   */
  private static readonly RO_PROFILES: [ number, number ][][] = [
    // 0 — a long table with a stepped shoulder dropping away to the right.
    [ [ 0, 0 ], [ 0.06, 0.86 ], [ 0.34, 0.9 ], [ 0.36, 1 ], [ 0.72, 0.98 ],
      [ 0.74, 0.82 ], [ 0.95, 0.79 ], [ 1, 0 ] ],
    // 1 — a tall narrow tower, leaning very slightly, with a clipped left corner.
    [ [ 0, 0 ], [ 0.14, 0.88 ], [ 0.3, 1 ], [ 0.72, 0.96 ], [ 0.86, 0.83 ], [ 1, 0 ] ],
    // 2 — twin terraces: a low shelf on both sides of a raised centre.
    [ [ 0, 0 ], [ 0.05, 0.6 ], [ 0.22, 0.63 ], [ 0.24, 0.97 ], [ 0.6, 1 ],
      [ 0.62, 0.65 ], [ 0.9, 0.61 ], [ 1, 0 ] ],
    // 3 — broad and low, one shoulder lifted, with a long talus skirt.
    [ [ 0, 0 ], [ 0.1, 0.7 ], [ 0.44, 0.76 ], [ 0.5, 0.9 ], [ 0.82, 0.85 ],
      [ 0.92, 0.68 ], [ 1, 0 ] ],
    // 4 — a small butte, almost symmetrical, the farthest thing on the strip.
    [ [ 0, 0 ], [ 0.16, 0.86 ], [ 0.52, 1 ], [ 0.84, 0.88 ], [ 1, 0 ] ],
    // 5 — a wide mesa whose rim dips once in the middle, like a saddle.
    [ [ 0, 0 ], [ 0.08, 0.9 ], [ 0.4, 0.94 ], [ 0.5, 0.84 ], [ 0.6, 0.94 ],
      [ 0.93, 0.88 ], [ 1, 0 ] ],
  ];

  /** Place one profile: `cx` centre, `w` wide, `h` tall, foot on `foot`. */
  private roMesa( shape: number, cx: number, w: number, h: number, foot: number ): string {
    const pts = LineMenuBgComponent.RO_PROFILES[ shape ].map(
      ( [ x, y ] ) => [ cx + ( x - 0.5 ) * w, foot - y * h ] as [ number, number ] );
    return this.roRound( pts, Math.max( 3.5, h * 0.15 ) );
  }

  /**
   * MIAMI — a neon skyline at sunset over the bay, from the theme's own icon.
   *
   * SEAMLESS is solved per layer, not globally: the sky and sea are flat
   * gradients (uniform across x, so they cannot seam) and the shore and its
   * crest are integer-frequency sines, periodic by construction.
   *
   * The SKYLINE simply stops short of both edges (owner 2026-08-13: "en sol ve
   * en sağdaki binalar silinsin"), so the seam falls in open sky and no tower is
   * ever cut in half. Wrapping a tower across the join was tried first and is
   * the more literal answer, but a building sliced by the edge reads as damage
   * on a strip this short; open water and sky at the join reads as distance.
   */
  private buildMiami( p: Product ): void {
    const f = ( v: number ) => +v.toFixed( 1 );
    const cp = ( p.compactPalette?.length ? p.compactPalette : p.palette ) || [];
// The waterline sits LOW, and got lower (owner 2026-08-13: "daha az deniz
    // daha çok gökyüzü"). The top 46px of the strip ramps to transparent, so
    // everything above it is thrown away; at 84 most of the sky gradient was
    // spent in the masked band and the visible part came out a flat pale
    // magenta. At 114 the violet-to-coral run happens where it can be seen, the
    // towers grow tall enough to fill the band the tab glyphs sit in — which is
    // also what gives those glyphs something dark to read against — and the bay
    // is down to the strip it should be.
    const HORIZON = 112;

    // The WATERLINE is not the same line the city stands on (owner 2026-08-13:
    // "binalar suya gömüldü, deniz yeterince aşağı gitmedi"). With the two at
    // one y the towers met the pale water flush at their feet and read as
    // sinking into it. The gap between them shows the warmest part of the sky
    // ramp, which is exactly what the far shore looks like at this hour: the
    // city is ACROSS the bay, not standing in it.
    const SEA = HORIZON + 14;

    // Deterministic noise. Math.random would give a different strip on every
    // rebuild, and these assets are captured from the live page.
    let seed = 20260813;
    const rnd = () => {
      seed = ( seed * 1103515245 + 12345 ) & 0x7fffffff;
      return seed / 0x7fffffff;
    };

    this.miShoreFill = lineTabFill( p.key, p.appearance );
    this.miWindowFill = cp[ 3 ] || '#00D1C1';   // Electric Aqua, the tab ON colour

    // The dusk ramp: pink overhead into peach at the waterline (owner
    // 2026-08-13: "gökyüzünden koyu moru çıkaralım, pembe ve şeftali
    // arttıralım"). The icon's near-black violet is gone from the top entirely.
    //
    // The top is a deep PINK-PLUM, not a light one, and that is measured rather
    // than taste. The tab glyphs sit high on the strip and the towers do not
    // cover all of that band — wherever a glyph falls in a gap it is read against
    // the sky. At #B4568F the idle glyph scored 1.04:1 there, i.e. invisible.
    // #8A3A72 has no purple in it (its red channel beats its blue) and still
    // gives the idle glyph 2.16:1 and the active one 3.72:1.
    this.miSkyTop = '#8A3A72';
    this.miSkyMid = '#E4788C';
    this.miSkyLow = '#FDB584';

    // The sun is placed further down, once the skyline exists — it needs a
    // CLEARING, and that cannot be chosen before there are buildings.
    //
    // Left of centre on purpose: LINE tiles this strip left-aligned, so the left
    // third is the part every phone shows whatever its width. The icon puts the
    // sun on the right; on a tab bar that would be the first thing cropped.
    const SUN_CX = 520;
    const SUN_R = 42;

    // ---- the skyline ----
    // Two rows. The far row is shorter, flatter and unlit, and exists only to
    // stop the near row reading as a single cardboard cut-out.
    // Both rows live inside [EDGE, W-EDGE]; nothing touches a seam.
    const EDGE = 92;
    const far: { x: number, y: number, w: number, h: number }[] = [];
    for ( let x = EDGE; x < this.W - EDGE; ) {
      const w = 30 + Math.round( rnd() * 44 );
      const h = 30 + Math.round( rnd() * 26 );
      if ( x + w <= this.W - EDGE ) { far.push( { x, y: HORIZON - h, w, h } ); }
      x += w + 4 + Math.round( rnd() * 12 );
    }
    this.miFar = far;

    const near: { x: number, y: number, w: number, h: number }[] = [];
    const windows: { x: number, y: number, w: number, h: number }[] = [];
    for ( let x = EDGE + 10; x < this.W - EDGE; ) {
      const w = 22 + Math.round( rnd() * 30 );
      // Every few buildings, a tower — a skyline of one height is a wall.
      const tall = rnd() < 0.3;
      // Capped so no roof climbs above y=50 — past that the alpha ramp starts
      // eating the tower and it fades out mid-facade.
      const h = tall ? 46 + Math.round( rnd() * 16 ) : 26 + Math.round( rnd() * 16 );
      const y = HORIZON - h;
      if ( x + w > this.W - EDGE ) { break; }
      near.push( { x, y, w, h } );

      // Lit windows: a grid inset in the facade, only on buildings with room for
      // one. They are what makes the skyline read as NEON rather than as hills.
      if ( h >= 26 && w >= 26 ) {
        const cols = Math.floor( ( w - 8 ) / 7 );
        const rows = Math.floor( ( h - 12 ) / 7 );
        for ( let c = 0; c < cols; c++ ) {
          for ( let r = 0; r < rows; r++ ) {
            if ( rnd() < 0.34 ) { continue; }   // some floors are dark
            windows.push( { x: x + 5 + c * 7, y: y + 7 + r * 7, w: 3.4, h: 3.4 } );
          }
        }
      }
      x += w + 3 + Math.round( rnd() * 9 );
    }
    // Two bookend towers, one at each end (owner 2026-08-13: "sol ve sağa birer
    // bina ekle ama köşeye değmesin"). They sit INSIDE a 30px margin, so the
    // skyline reaches toward both edges without any tower being cut by the seam
    // — the run still ends in open sky, it just no longer stops short by a third
    // of the strip.
    const BOOK = 30;
    for ( const [ bx, bw, bh ] of [ [ BOOK, 42, 44 ], [ this.W - BOOK - 46, 46, 38 ] ] ) {
      const by = HORIZON - bh;
      near.push( { x: bx, y: by, w: bw, h: bh } );
      const cols = Math.floor( ( bw - 8 ) / 7 );
      const rows = Math.floor( ( bh - 12 ) / 7 );
      for ( let c = 0; c < cols; c++ ) {
        for ( let r = 0; r < rows; r++ ) {
          if ( rnd() < 0.34 ) { continue; }
          windows.push( { x: bx + 5 + c * 7, y: by + 7 + r * 7, w: 3.4, h: 3.4 } );
        }
      }
    }

    // ---- give the sun its clearing ----
    // A setting sun sits BEHIND the skyline, but behind is not the same as
    // hidden: in the reference the towers cover its base while its upper half
    // stands clear above the roofs. Here every tower near it was taller than the
    // whole visible disc, so the sun read as a stray glow between two buildings
    // (owner 2026-08-13: "önünde bina var, çok saçma").
    //
    // The fix is the skyline, not the sun: any tower standing in front of the
    // disc is cut down to a low-rise, so the disc clears the roofline it sits
    // behind. Cities really do look like this — the tall stuff is rarely on the
    // waterfront edge.
    const clearFrom = SUN_CX - SUN_R - 4, clearTo = SUN_CX + SUN_R + 4;
    // Low enough that the disc clears them by a real margin. This is the number
    // that decides whether the sun reads at all: at LOW=30 against a 42px disc
    // the visible sliver was 8px and the sun went back to looking like a smudge.
    const LOW = 18;
    const cut = ( b: { x: number, y: number, w: number, h: number } ) => {
      if ( b.x + b.w < clearFrom || b.x > clearTo || b.h <= LOW ) { return b; }
      return { ...b, h: LOW, y: HORIZON - LOW };
    };
    for ( let i = 0; i < near.length; i++ ) { near[ i ] = cut( near[ i ] ); }
    for ( let i = 0; i < far.length; i++ ) { far[ i ] = cut( far[ i ] ); }
    // Windows that were on a floor which no longer exists have to go with it.
    const kept = windows.filter( w => {
      const host = near.find( b => w.x >= b.x && w.x <= b.x + b.w );
      return !host || w.y >= host.y;
    } );

    this.miSun = { cx: SUN_CX, cy: HORIZON - 4, r: SUN_R };
    this.miSunClip = HORIZON;
    this.miNear = near;
    this.miWindows = kept;

    // ---- the bay ----
    // The sun's reflection, broken into a column of dashes that spreads and fades
    // as it comes toward the shore. Centred on the sun, because a reflection that
    // is not under its source reads as litter on the water.
    const streaks: { x: number, y: number, w: number, h: number, o: number }[] = [];
    for ( let i = 0; i < 11; i++ ) {
      const t = i / 10;
      const y = SEA + 2 + t * 20;
      const spread = 16 + t * 70;
      const n = 1 + Math.round( t * 3 );
      for ( let k = 0; k < n; k++ ) {
        const w = ( 10 + rnd() * 34 ) * ( 1 - t * 0.4 );
        const cx = this.miSun.cx + ( rnd() - 0.5 ) * spread;
        streaks.push( { x: f( cx - w / 2 ), y: f( y ), w: f( w ), h: 2, o: +( 0.85 - t * 0.55 ).toFixed( 2 ) } );
      }
    }
    this.miStreaks = streaks;

    // ---- the bay ----
    // ONE layer, from the waterline straight down to the bottom edge, in the tab
    // bar's own colour (owner 2026-08-13: "deniz aslında en alttaki açık renk
    // pembe olan olsun, ortaya katman eklemeyelim").
    //
    // The first pass had a magenta bay under the skyline and a separate pale
    // shore in front of it, with a crest line where they met. That is three
    // bands stacked in 54px, and the middle one existed only to get from the
    // skyline down to the colour the strip has to end on. Dropping it makes the
    // water read as one sheet AND removes the seam risk entirely, since a flat
    // fill cannot disagree with itself across the join.
    this.miSeaTop = SEA;
    this.miSeaFill = this.miShoreFill;
    // Halfway back toward the magenta bay this replaced (owner 2026-08-13: "çok
    // açık renk oldu, biraz ortalayalım"). Still ONE layer — the deepening is a
    // ramp inside it, not a second band — and it runs out well before the bottom
    // row so the strip still ends on exactly the bar's colour.
    this.miSeaDeep = mixHex( this.miShoreFill, '#E0687A', 0.5 );

    /* NO PALMS on the strip (owner 2026-08-13: "menu background'da ağaçlar
     * olmasın"). They stood on the shore in the first pass. The strip is 150px
     * tall and the tab glyphs sit on it, so a tree big enough to read competed
     * with them; the palm stays the passcode's and the Home tab's motif. */
  }

  /**
   * One mosque complex in silhouette: a cube of wall, a main dome on its drum,
   * a half-dome buttressing it on each side and a finial on top.
   *
   * The half-domes sit AGAINST the drum rather than out at the corners, which
   * is the actual Ottoman section and the only thing that separates this
   * profile from a generic domed box at 30px tall.
   */
  private istMosque( cx: number, w: number, h: number, foot: number ): string {
    const f = ( v: number ) => +v.toFixed( 1 );
    const wallH = h * 0.44;
    const drumH = h * 0.08;
    const R = Math.min( h - wallH - drumH, w * 0.30 );
    // Half the main dome, hard-capped: the first pass sized these off the width
    // alone and on the hero they came out 13.6 against a 15.8 main dome, i.e.
    // three domes in a row instead of a dome buttressed by two half-domes.
    const hd = Math.min( w * 0.075, R * 0.5 );
    const wallY = foot - wallH;
    const drumY = wallY - drumH;
    const L = cx - w / 2;
    const Rt = cx + w / 2;
    const domeTop = drumY - R;
    return `M${ f( L ) },${ f( foot ) } L${ f( L ) },${ f( wallY ) }`
      + ` L${ f( cx - R - 2 * hd ) },${ f( wallY ) }`
      + ` A${ f( hd ) },${ f( hd ) } 0 0 1 ${ f( cx - R ) },${ f( wallY ) }`
      + ` L${ f( cx - R ) },${ f( drumY ) }`
      + ` A${ f( R ) },${ f( R ) } 0 0 1 ${ f( cx + R ) },${ f( drumY ) }`
      + ` L${ f( cx + R ) },${ f( wallY ) }`
      + ` A${ f( hd ) },${ f( hd ) } 0 0 1 ${ f( cx + R + 2 * hd ) },${ f( wallY ) }`
      + ` L${ f( Rt ) },${ f( wallY ) } L${ f( Rt ) },${ f( foot ) } Z`
      // the finial (alem) — a needle, not a ball: a ball this small renders as
      // a smudge on the dome's crown and reads as a rendering fault.
      + ` M${ f( cx - 0.9 ) },${ f( domeTop + 0.6 ) } L${ f( cx ) },${ f( domeTop - h * 0.17 ) }`
      + ` L${ f( cx + 0.9 ) },${ f( domeTop + 0.6 ) } Z`
      // the courtyard wall the complex stands in, wider than the mosque itself.
      + ` M${ f( cx - w * 0.66 ) },${ f( foot - h * 0.16 ) } L${ f( cx + w * 0.66 ) },${ f( foot - h * 0.16 ) }`
      + ` L${ f( cx + w * 0.66 ) },${ f( foot ) } L${ f( cx - w * 0.66 ) },${ f( foot ) } Z`;
  }

  /** A minaret: a pencil shaft, one balcony (şerefe) ringing it and a conical
   *  cap. Deliberately thin — these cross the tab-glyph band and a 3px needle
   *  passes behind a glyph without competing with it. */
  private istMinaret( x: number, foot: number, h: number ): string {
    const f = ( v: number ) => +v.toFixed( 1 );
    const t = Math.max( 1.3, h * 0.05 );
    const b = t * 0.85;
    const capH = h * 0.22;
    const balY = foot - h + capH + 2.4;
    return `M${ f( x - t ) },${ f( foot ) } L${ f( x - t ) },${ f( balY ) }`
      + ` L${ f( x - t - b ) },${ f( balY ) } L${ f( x - t - b ) },${ f( balY - 2.1 ) }`
      + ` L${ f( x - t * 0.8 ) },${ f( balY - 3.1 ) } L${ f( x ) },${ f( foot - h ) }`
      + ` L${ f( x + t * 0.8 ) },${ f( balY - 3.1 ) } L${ f( x + t + b ) },${ f( balY - 2.1 ) }`
      + ` L${ f( x + t + b ) },${ f( balY ) } L${ f( x + t ) },${ f( balY ) }`
      + ` L${ f( x + t ) },${ f( foot ) } Z`;
  }

  /** A Bosphorus ferry in silhouette: raked hull, a deckhouse and one funnel. */
  private istFerry( x: number, y: number, s: number ): string {
    const f = ( v: number ) => +v.toFixed( 1 );
    const hh = s * 0.26;
    return `M${ f( x - s ) },${ f( y ) } L${ f( x + s ) },${ f( y ) }`
      + ` L${ f( x + s * 0.78 ) },${ f( y + hh ) } L${ f( x - s * 0.84 ) },${ f( y + hh ) } Z`
      + ` M${ f( x - s * 0.58 ) },${ f( y ) } L${ f( x - s * 0.58 ) },${ f( y - hh * 1.2 ) }`
      + ` L${ f( x + s * 0.5 ) },${ f( y - hh * 1.2 ) } L${ f( x + s * 0.5 ) },${ f( y ) } Z`
      + ` M${ f( x - s * 0.12 ) },${ f( y - hh * 1.2 ) } L${ f( x - s * 0.12 ) },${ f( y - hh * 2.5 ) }`
      + ` L${ f( x + s * 0.16 ) },${ f( y - hh * 2.5 ) } L${ f( x + s * 0.16 ) },${ f( y - hh * 1.2 ) } Z`;
  }


  /**
   * Tallinn: THE CITY WALL AND THE OLD TOWN BEHIND IT (owner 2026-08-14: "kale
   * duvarları olacak işte bide eski kiremit çatılı binalar").
   *
   * The first build was a row of little gabled houses and it was rightly called
   * careless: any European town draws that. What makes Tallinn's old town
   * photograph the way it does is that you are always looking at it THROUGH the
   * wall — 1.85 km of limestone curtain with round towers standing out of it, and
   * the red tile roofs rising behind. So the wall is the foreground here and the
   * houses are what it holds back.
   *
   * Built from a reference pass rather than from memory. What that pass changed:
   *
   *  - The WALL TOWERS ARE HORSESHOE DRUMS WITH CONICAL TILE ROOFS, and they are
   *    the vertical accents — 26 to 28 of the original 46 survive, and their cones
   *    are the shape the skyline repeats. Three of them stand here.
   *  - ST OLAF'S IS THE ONE NEEDLE. Its spire is roughly twice the height of the
   *    masonry under it, which is true of nothing else in the city; drawn at any
   *    other proportion it is just another church.
   *  - The ROOFS ARE STREAKED, NOT WHITE. Old Town attics are barely insulated, so
   *    the roofs melt from below and a real winter photograph shows terracotta
   *    showing through in down-slope stripes, with snow holding in the pans, on
   *    the ridge and along the eaves. A solid white roof is a day-old snowfall and
   *    it also throws away the only warm colour in the picture.
   *  - The GABLES FACE THE STREET and some are CROW-STEPPED. That is the Hanseatic
   *    plot: narrow frontage, ridge running back, stepped gable on the older ones.
   *  - The LIGHT IS OVERCAST. December here gets about 0.7 hours of sun a day, so
   *    a low golden sun is the rare picture, not the honest one; the sky is a pale
   *    luminous grey and snow shadows are blue-violet rather than cyan.
   *
   * The tab constraints decide the placement, as everywhere in this family: LINE's
   * five glyphs sit at x 147, 441, 736, 1030 and 1325 across y 25..80, so every
   * tower and the spire stand in the GAPS at 294, 589, 883 and 1178, and the wall,
   * the roofline and the snow all live below the glyph band.
   */
  /**
   * OCEAN — the seabed the theme's own icon draws, given 1472px to be a garden in.
   *
   * The icon is three bioluminescent currents sweeping over a dark sea, with
   * coral heads and seagrass rising off the floor and bubbles going up through
   * it. All four of those are here, and the composition is decided by the one
   * constraint every strip in this family works under: the classic tab glyph
   * occupies y 13..93 and the labels y 95..120.
   *
   * On a light theme that constraint is a fight — Tallinn's roofline had to be
   * pinned under y 78 and the tab colour re-judged against a red pantile. Here it
   * is a gift. The thing that belongs in the top two thirds of a seabed is
   * NOTHING: open water. So the garden is built where it will be seen, in the
   * TAB GAPS at x 294 / 589 / 883 / 1178, and the water the glyphs land on stays
   * empty and dark on purpose.
   *
   * Everything tiles: LINE left-aligns this strip and repeats it, so the currents
   * and the floor are integer-frequency waves, and nothing tall stands within 40
   * units of either edge.
   */
  /**
   * SALDA — the white shore and the shallows, the scene the theme's icon draws.
   *
   * The composition is decided by one number. The classic tab glyph occupies
   * y 13..93 of this strip and this theme's glyphs are DARK, so everything above
   * y 92 has to stay pale: sky, and a low pale range, and nothing else. Every
   * saturated turquoise lives under the horizon.
   *
   * That is a constraint on paper and a gift in practice, because it also happens
   * to be what the place looks like. From the beach the far water hazes out to
   * near the colour of the sky, the deep turquoise sits in the middle distance,
   * and the shallows go pale again over white sand. The icon paints exactly that
   * order and so does this.
   *
   * The BEYAZ ADALAR — the white shoals Salda is known for — stand in the tab
   * gaps at x 294 / 589 / 883 / 1178, which is where anything with a shape is
   * allowed to be. They are the only thing in the scene that is not a band.
   */
  /**
   * PRAGUE — Hradcany over the Vltava at night, the scene the theme's icon draws.
   *
   * This is the one strip in the run whose constraint costs nothing. The classic
   * tab glyph occupies y 13..93 and has to land on something quiet; what belongs
   * in the top of a night skyline is NIGHT. So the sky stays empty where the tabs
   * are, every lit thing sits below y 88, and the two things that DO reach up —
   * the cathedral and the moon — stand in tab gaps.
   *
   * The gold is the subject and it is used sparingly on purpose. A skyline where
   * every window is lit is a hotel; what the icon actually shows is a dark town
   * with a lit palace terrace along its front, and most of the gold in the scene
   * is in one band forty units tall.
   */
  /**
   * SYDNEY — the harbour at midday, the scene the theme's own icon draws.
   *
   * Two thirds sky, warm at the horizon and blue above, with the city on a line
   * about halfway down and the water under it. That composition is the icon's,
   * and it happens to be exactly what the tab bar wants: the glyph band
   * (y 13..93) is sky, so the dark glyphs land on the palest thing in the scene.
   *
   * Its landmarks stand in the TAB GAPS at x 294 / 589 / 883 / 1178, and the
   * first of them is the theme's OWN glyph — the Opera House the Home tab draws.
   * Prague does the same with its cathedral, and it works for the same reason: the
   * object is a building and the strip is the harbour it stands on.
   */
  /**
   * REYNISFJARA — the black beach at dusk.
   *
   * This is the one scene in the run that departs from its own icon, and the
   * reason is worth stating plainly. The icon is HIGH KEY: a near-white overcast
   * sky over black sand, which is exactly what the place looks like on the day
   * everyone photographs it. That sky cannot be used here. The tab glyphs land in
   * y 13..93, and this theme's SKIN is dark — so its iOS-26 glass is dark, and its
   * glyphs have to be mid-toned to read on that. A near-white strip would demand
   * dark glyphs; the glass demands light ones; no colour is both.
   *
   * Dusk resolves it without lying about the place. "Cinematic" is one of this
   * theme's own three tags, and Reynisfjara is at least as famous for its weather
   * as for its light. The sky goes dark, the last of it lies along the horizon
   * BELOW the glyph band, and the whole scene keeps its subject: black sand, cold
   * water, and basalt in both of its forms.
   */
  /**
   * SPRING — the lupine meadow the theme's own icon draws.
   *
   * The composition is the icon's and it needs no argument: blue sky with soft
   * clouds, a low green range, and a meadow of flower spikes filling the bottom
   * third. The tab glyphs land in y 13..93, and everything up there is sky.
   *
   * The FLOWER SPIKES stand in the tab gaps at x 294 / 589 / 883 / 1178, tall
   * enough to be seen; between the gaps they are short, so the row of tabs never
   * has a lupine growing through it. The hummingbird — the theme's own Home glyph
   * — hovers in the second gap over the tallest of them.
   */
  /**
   * VIENNA — the auditorium, the room the theme's own icon is a picture of.
   *
   * The one INTERIOR in this run, and it changes what the strip has to do. Every
   * other scene here is a landscape that hands its bottom edge to the tab fill
   * with water, sand or grass; this one hands it off with FURNITURE — the rows of
   * seats in the stalls run straight into the bar.
   *
   * The composition is a horizontal band of BOX FRONTS rather than the icon's
   * frontal view of the room, and that is a necessity rather than a preference: an
   * auditorium seen from the back tiles about as well as a photograph does, which
   * is to say not at all. Two tiers of boxes running the length of the strip is
   * what the room looks like from a box, it repeats honestly, and it puts the
   * gilding in a band rather than in a heap.
   *
   * The glyph band (y 13..93) is ceiling and the shadowed openings of the upper
   * tier — the quietest thing a room like this has.
   */
  /**
   * MADRID — Gran Via at sunset, the street behind the theme's own icon.
   *
   * Fifth light theme in this run and the same constraint every one of them works
   * under: the tab glyphs are DARK and land in y 13..93, so the sky and the upper
   * storeys have to stay pale. Here the hour does that on its own — the sun is low
   * and behind the buildings, so their tops are lit and the street under them is
   * in shadow, which is the picture and the constraint at once.
   *
   * The DOMES stand in the tab gaps at x 294 / 589 / 883 / 1178. The first is the
   * Metropolis with its winged victory, which is the one silhouette on this street
   * anybody can name; the theme's own fan is being carried along the pavement in
   * the last gap, the way Prague puts its cathedral in its strip and Vienna leaves
   * a violin on a box rail.
   */
  /**
   * ETNA — the mountain at night.
   *
   * The theme's icon puts a bright crimson sky ABOVE a black cone, and that is
   * exactly backwards for a tab strip: the glyph band is y 13..93 and a saturated
   * crimson up there would swallow this theme's crimson glyphs whole. So the sky
   * is inverted — near-black at the top, heating toward the horizon — which is
   * also what a night eruption actually looks like from a distance, since the glow
   * is coming off the ground rather than out of the sky.
   *
   * Reynisfjara makes the same move for the same reason. Both of them are dark
   * themes whose icons are lit from the wrong end for a tab bar, and in both cases
   * the honest fix was to change the HOUR rather than the subject.
   *
   * The cone stands over the second tab gap and is wide enough to sit under two
   * tabs either side of it — but it is near-black there, so the crimson glyphs
   * land on it at 4.5:1, which is better than they do on the sky.
   */
  /**
   * FUJI — the mountain at dawn, the scene the theme's own icon draws.
   *
   * The last of the ten, and the only one where the constraint every other strip
   * fought cost nothing at all: this theme's tab glyphs are DARK and land in
   * y 13..93, and its icon is already a pastel sky with a pale mountain standing
   * in it. Nothing needed moving, nothing needed re-timing, no hour had to change.
   *
   * What did need deciding is what is ABSENT. The icon's whole foreground is
   * cherry in flower, and there is not one blossom anywhere in this theme's assets.
   * Sakura is a theme in the same set of twenty-two and the blossom is its entire
   * identity; borrowing it would make two of them read as versions of each other.
   * So Fuji keeps the mountain, the pagoda and the dawn, and leaves the flowers.
   */
  private buildFuji( p: Product ): void {
    const f = ( v: number ) => +v.toFixed( 1 );
    const fr = ( v: number ) => v - Math.floor( v );

    // The theme's own palette (base-themes/nature/fuji-theme), by name.
    const SNOW = '#FFFCFB';
    const BLUSH = '#F5D9D0';
    const VERMILION = '#DC5B26';
    const CORAL = '#D37154';
    const ROSE = '#DD747A';
    const PINK = '#ED84B0';
    const WISTERIA = '#BF8AB3';
    const TEAL = '#39A1A5';
    const DAWN_SLATE = '#5F7D9F';
    const UMBER = '#45302B';

    /** Where the mist begins. */
    const MIST = 126;
    const GAPS = [ 294, 589, 883, 1178 ];

    this.fuSkyTop = mixHex( DAWN_SLATE, '#DFF0FA', 0.72 );
    this.fuSkyLow = mixHex( BLUSH, PINK, 0.14 );

    // ---- the sun ----
    // Low, pale and in a TAB GAP — the same rule the moon follows on Prague.
    this.fuSunX = GAPS[ 0 ];
    this.fuSunY = 44;
    this.fuSunR = 19;

    // ---- clouds ----
    // Flat horizontal bands rather than puffs, which is the device a Japanese
    // print of this mountain uses nine times out of ten and is also the only cloud
    // that survives at strip scale. Spring's are ellipses because Spring's icon
    // draws cumulus; this theme's icon draws bands.
    this.fuClouds = [];
    for ( let i = 0; i < 12; i++ ) {
      const y = 26 + fr( i * 0.4142 + 0.3 ) * 62;
      const x0 = fr( i * 0.618 + 0.11 ) * this.W - 200;
      const w = 180 + fr( i * 0.7548 ) * 300;
      const h = 3.4 + fr( i * 0.271 ) * 3.4;
      this.fuClouds.push( {
        d: `M${ f( x0 + h ) },${ f( y ) } L${ f( x0 + w - h ) },${ f( y ) }`
          + ` a${ f( h ) },${ f( h ) } 0 0,1 0,${ f( h * 2 ) }`
          + ` L${ f( x0 + h ) },${ f( y + h * 2 ) }`
          + ` a${ f( h ) },${ f( h ) } 0 0,1 0,${ f( -h * 2 ) } Z`,
        fill: i % 3 === 0 ? mixHex( WISTERIA, SNOW, 0.62 ) : SNOW,
        o: +( 0.34 + fr( i * 0.3819 ) * 0.34 ).toFixed( 2 ),
      } );
    }

    /** A band whose top edge waves at integer frequency, so the strip tiles. */
    const band = ( base: number, waves: [ number, number, number ][], to: number ): string => {
      const pts: string[] = [];
      for ( let i = 0; i <= 96; i++ ) {
        const t = i / 96, x = t * this.W;
        let y = base;
        for ( const [ k, amp, ph ] of waves ) { y += Math.sin( t * Math.PI * 2 * k + ph ) * amp; }
        pts.push( `${ f( x ) },${ f( y ) }` );
      }
      return `M0,${ f( to ) } L` + pts.join( ' L' ) + ` L${ this.W },${ f( to ) } Z`;
    };

    this.fuLand = [];

    // ---- the far range ----
    this.fuLand.push( {
      d: band( MIST - 22, [ [ 1, 7, 1.4 ], [ 3, 3, 3.2 ] ], MIST + 4 ),
      fill: mixHex( DAWN_SLATE, SNOW, 0.68 ),
    } );

    // NO MOUNTAIN IN THE STRIP (owner 2026-08-17: "menu background'daki fujiyi
    // kaldıracağız zaten home icon'ı fuji dağı").
    //
    // It stood over the second tab gap at nine times its wallpaper size, with a
    // smaller peak behind it. The reasoning was that Fuji is the one theme whose two
    // assets are two different subjects and the strip was where they met — but the
    // Home tab IS the mountain, and it sits a few pixels below this image on the
    // same screen. Two drawings of one subject that close together is a repeat, not
    // a rhyme.
    //
    // The same verdict Prague's lamp, Reynisfjara's puffins, Spring's flower and
    // Madrid's churro cup got. A menu strip is a HORIZON; the hero belongs on the
    // tab and the lock screen. What is left here is only what a horizon is made of:
    // the far range, the near hills and the mist.

    // ---- the near hills ----
    this.fuLand.push( {
      d: band( MIST - 8, [ [ 2, 5, 0.6 ], [ 5, 2.5, 2.4 ] ], MIST + 6 ),
      fill: mixHex( TEAL, SNOW, 0.62 ),
    } );

    // ---- the pagoda ----
    // Standing on the near hill, in the SECOND tab gap (owner 2026-08-17: "pagoda
    // sola kayacak"). Prague puts its cathedral in its strip, Sydney its opera
    // house, Vienna a violin on a box rail; this is the tenth and last of them.
    //
    // It was in the last gap, at the far right. Two reasons the second one is
    // better: it is the gap the mountain used to fill, so removing the mountain no
    // longer leaves a hole in the middle of the image — and it puts the pagoda on
    // the opposite side of the strip from the SUN, which sits in the first gap. Two
    // objects at the two ends of a horizon read as a composition; two at the same
    // end read as everything having drifted.
    const pplace = `translate(${ GAPS[ 1 ] },${ MIST - 2 }) scale(0.52) translate(-60,-112)`;
    this.fuLand.push( { d: PAGODA_BODIES, fill: mixHex( UMBER, CORAL, 0.3 ), transform: pplace } );
    this.fuLand.push( { d: PAGODA_ROOFS, fill: VERMILION, transform: pplace } );
    this.fuLand.push( { d: PAGODA_FINIAL, fill: mixHex( UMBER, CORAL, 0.3 ), transform: pplace } );

    // a stand of dark pines along the hill, which is what actually grows in this
    // foreground once the cherry is left out of it
    // CLUSTERED and varied. The first pass scattered 46 identical triangles at
    // even spacing and the result read as a saw blade laid along the hill — trees
    // grow in stands, and the gaps between stands are as much of the shape as the
    // trees are.
    //
    // Two ranks: a hazier one behind, a darker one in front, offset from each
    // other. One rank of anything on a horizon is a fence.
    for ( const [ rank, tint, dy ] of [ [ 0, 0.26, -3 ], [ 1, 0.5, 1 ] ] as [ number, number, number ][] ) {
      const pines: string[] = [];
      for ( let g = 0; g < 9; g++ ) {
        const gx = 30 + fr( ( g + rank * 5 ) * 0.618 + 0.2 ) * ( this.W - 60 );
        const n = 3 + Math.floor( fr( ( g + rank ) * 0.4142 ) * 5 );
        for ( let i = 0; i < n; i++ ) {
          const j = g * 11 + i + rank * 71;
          const x = gx + ( i - n / 2 ) * ( 13 + fr( j * 0.271 ) * 9 );
          const h = ( 8 + fr( j * 0.4142 + 0.6 ) * 12 ) * ( rank ? 1 : 0.8 );
          const w = h * ( 0.34 + fr( j * 0.7548 ) * 0.16 );
          pines.push( `M${ f( x - w ) },${ f( MIST + 2 + dy ) } L${ f( x ) },${ f( MIST + 2 + dy - h ) }`
            + ` L${ f( x + w ) },${ f( MIST + 2 + dy ) } Z` );
        }
      }
      this.fuLand.push( { d: pines.join( ' ' ), fill: mixHex( TEAL, UMBER, tint ) } );
    }

    // ---- the mist ----
    this.fuMistD = `M0,${ MIST } L${ this.W },${ MIST } L${ this.W },${ this.H } L0,${ this.H } Z`;
    this.fuMistTop = mixHex( BLUSH, ROSE, 0.16 );
    this.fuMistLow = lineTabFill( p.key, p.appearance );
  }

  private buildEtna( p: Product ): void {
    const f = ( v: number ) => +v.toFixed( 1 );
    const fr = ( v: number ) => v - Math.floor( v );

    // The theme's own palette (base-themes/nature/etna-theme), by name.
    const PITCH = '#121111';
    const BASALT = '#1E1D1D';
    const STONE = '#222121';
    const LAVA = '#D74C57';
    const FLAME = '#F1735F';
    const EMBER_ORANGE = '#E67748';
    const SAND = '#E2BF79';
    const ASH = '#B3AEAE';

    /** Where the lava field begins. */
    const GROUND = 122;
    const GAPS = [ 294, 589, 883, 1178 ];
    /** The summit, in the second tab gap. */
    const SX = GAPS[ 1 ], SUMMIT = 30;

    this.etSkyTop = mixHex( PITCH, '#000000', 0.5 );
    this.etSkyLow = mixHex( LAVA, PITCH, 0.68 );

    this.etRock = [];
    this.etHot = [];

    /** A ridge whose top edge waves at integer frequency, so the strip tiles. */
    const ridge = ( base: number, waves: [ number, number, number ][], fill: string ): void => {
      const pts: string[] = [];
      for ( let i = 0; i <= 96; i++ ) {
        const t = i / 96, x = t * this.W;
        let y = base;
        for ( const [ k, amp, ph ] of waves ) { y += Math.sin( t * Math.PI * 2 * k + ph ) * amp; }
        pts.push( `${ f( x ) },${ f( y ) }` );
      }
      this.etRock.push( {
        d: `M0,${ GROUND + 4 } L` + pts.join( ' L' ) + ` L${ this.W },${ GROUND + 4 } Z`,
        fill,
      } );
    };

    // ---- the far ridges ----
    // Two of them, both lighter than the cone in front — distance on a dark scene
    // means less contrast with the sky, not more.
    ridge( 104, [ [ 1, 8, 1.2 ], [ 3, 4, 3.4 ] ], mixHex( STONE, '#4A1C1C', 0.42 ) );
    ridge( 112, [ [ 2, 6, 0.4 ], [ 5, 3, 2.2 ] ], mixHex( PITCH, STONE, 0.6 ) );

    // ---- the cone ----
    // The theme's OWN glyph, at nine times its box, standing over the second tab
    // gap. Prague puts its cathedral in its strip and Sydney its opera house; this
    // is the same move at landscape scale, and it works because the object is a
    // mountain and the strip is the range it stands in.
    const SC = 1.16;
    const place = `translate(${ SX },${ GROUND }) scale(${ SC }) translate(-60,-106)`;
    this.etRock.push( { d: VOLCANO_CONE, fill: mixHex( PITCH, '#000000', 0.35 ), transform: place } );
    this.etHot.push( { d: VOLCANO_RIVER, fill: mixHex( LAVA, PITCH, 0.2 ), transform: place } );
    this.etHot.push( { d: VOLCANO_FISSURE, fill: LAVA, transform: place } );
    this.etHot.push( { d: VOLCANO_GLOW, fill: mixHex( EMBER_ORANGE, SAND, 0.4 ), transform: place } );

    // The light the eruption throws, and the one bright thing inside the glyph
    // band. It sits in a TAB GAP, which is what gaps are for — and it goes on the
    // BLURRED layer, which is the whole difference between a glow and a dome: the
    // first pass drew this arc sharp and it came back as a maroon semicircle
    // parked on the summit.
    //
    // Three lobes rather than one, each smaller and hotter than the last, so the
    // light falls off from the crater instead of stopping at an edge.
    this.etGlow = [
      { d: `M${ SX - 150 },${ SUMMIT + 60 } C${ SX - 80 },${ SUMMIT - 46 } ${ SX + 80 },${ SUMMIT - 46 }`
          + ` ${ SX + 150 },${ SUMMIT + 60 } Z`, fill: LAVA, o: 0.16 },
      { d: `M${ SX - 84 },${ SUMMIT + 48 } C${ SX - 44 },${ SUMMIT - 24 } ${ SX + 44 },${ SUMMIT - 24 }`
          + ` ${ SX + 84 },${ SUMMIT + 48 } Z`, fill: FLAME, o: 0.22 },
      { d: `M${ SX - 40 },${ SUMMIT + 34 } C${ SX - 22 },${ SUMMIT - 8 } ${ SX + 22 },${ SUMMIT - 8 }`
          + ` ${ SX + 40 },${ SUMMIT + 34 } Z`, fill: EMBER_ORANGE, o: 0.30 },
    ];

    // ---- stars ----
    // Only in the upper third, where the glow does not reach. They are what makes
    // the top of this strip a NIGHT SKY rather than an empty dark rectangle, which
    // is what the first pass had up there.
    this.etStars = [];
    for ( let i = 0; i < 40; i++ ) {
      const x = fr( i * 0.6180339887 + 0.11 ) * this.W;
      const y = 20 + fr( i * 0.7548776662 + 0.37 ) * 40;
      // fade out toward the summit, where the eruption's light drowns them
      const near = Math.max( 0, 1 - Math.abs( x - SX ) / 380 );
      this.etStars.push( {
        cx: f( x ), cy: f( y ),
        r: f( 0.7 + fr( i * 0.271 ) * 1.0 ),
        o: +( ( 0.18 + fr( i * 0.4142 ) * 0.34 ) * ( 1 - near * 0.85 ) ).toFixed( 3 ),
      } );
    }

    // ---- cracks in the lava field ----
    // Cooled lava is a crust with light still in the joints, and it is the only
    // thing that stops the bottom quarter of this strip being a flat dark band.
    this.etCracks = [];
    for ( let i = 0; i < 30; i++ ) {
      const x = fr( i * 0.6180339887 + 0.53 ) * this.W;
      const t = fr( i * 0.4142 + 0.19 );
      const y = GROUND + 4 + t * 20;
      const w = 18 + fr( i * 0.7548 ) * 54;
      this.etCracks.push( {
        d: `M${ f( x ) },${ f( y ) } L${ f( x + w * 0.4 ) },${ f( y - 1.6 ) }`
          + ` L${ f( x + w ) },${ f( y + 0.6 ) } L${ f( x + w ) },${ f( y + 2.2 ) }`
          + ` L${ f( x + w * 0.4 ) },${ f( y + 0.4 ) } L${ f( x ) },${ f( y + 2 ) } Z`,
        // fainter the further from the mountain, and fainter toward the bottom
        o: +( ( 0.34 - t * 0.2 ) * Math.max( 0.25, 1 - Math.abs( x - SX ) / 700 ) ).toFixed( 3 ),
      } );
    }

    // ---- two lower vents ----
    // Etna is a mountain with flank vents rather than one hole at the top, and
    // they put a second and third point of light along the strip. In tab gaps.
    for ( const [ vx, s2 ] of [ [ GAPS[ 0 ], 0.34 ], [ GAPS[ 3 ], 0.28 ] ] as [ number, number ][] ) {
      const p2 = `translate(${ vx },${ GROUND }) scale(${ s2 }) translate(-60,-106)`;
      this.etRock.push( { d: VOLCANO_CONE, fill: mixHex( PITCH, '#000000', 0.2 ), transform: p2 } );
      this.etHot.push( { d: VOLCANO_GLOW, fill: mixHex( EMBER_ORANGE, SAND, 0.3 ), transform: p2 } );
    }

    // ---- the plume ----
    // Faint, wide, leaning off the summit. It crosses the glyph band, so it is held
    // under 0.12 — a plume is the one thing here that is allowed up there and only
    // because it is barely there.
    this.etPlume = [];
    for ( let i = 0; i < 7; i++ ) {
      const t = i / 6;
      const cx = SX + 20 + t * 90;
      const cy = SUMMIT - 6 - t * 14;
      const rx = 34 + t * 66, ry = 10 + t * 16;
      this.etPlume.push( {
        d: `M${ f( cx - rx ) },${ f( cy ) } a${ f( rx ) },${ f( ry ) } 0 1,0 ${ f( rx * 2 ) },0`
          + ` a${ f( rx ) },${ f( ry ) } 0 1,0 ${ f( -rx * 2 ) },0 Z`,
        o: +( 0.11 - t * 0.06 ).toFixed( 3 ),
      } );
    }

    // ---- embers ----
    // Thrown off the crater and drifting with the plume. They are the theme's own
    // wallpaper grain, put where the strip can show a few of them at size.
    this.etEmbers = [];
    for ( let i = 0; i < 26; i++ ) {
      const t = fr( i * 0.6180339887 + 0.2 );
      const x = SX + 8 + t * 150 + ( fr( i * 0.271 ) - 0.5 ) * 30;
      const y = SUMMIT + 4 - t * 22 + ( fr( i * 0.4142 ) - 0.5 ) * 24;
      const sc = +( 0.05 + fr( i * 0.7548 ) * 0.07 ).toFixed( 3 );
      this.etEmbers.push( {
        d: EMBER,
        fill: i % 3 === 0 ? SAND : EMBER_ORANGE,
        transform: `translate(${ f( x ) },${ f( y ) }) scale(${ sc }) translate(-60,-60)`,
        o: +( 0.8 - t * 0.5 ).toFixed( 2 ),
      } );
    }

    // ---- the lava field ----
    this.etGroundD = `M0,${ GROUND } L${ this.W },${ GROUND } L${ this.W },${ this.H } L0,${ this.H } Z`;
    this.etGroundTop = mixHex( PITCH, STONE, 0.5 );
    this.etGroundLow = lineTabFill( p.key, p.appearance );
  }

  private buildMadrid( p: Product ): void {
    const f = ( v: number ) => +v.toFixed( 1 );
    const fr = ( v: number ) => v - Math.floor( v );

    // The theme's own palette (base-themes/cities/madrid-theme), by name.
    const CREAM = '#FDF5DF';
    const SAFFRON = '#E8C25D';
    const OCHRE = '#DCA225';
    const DEEP_OCHRE = '#A57F02';
    const CORAL = '#D37969';
    const FLAMENCO = '#B84440';
    const DEEP_RED = '#902926';
    const SKY_BLUE = '#5A91A8';

    /** Where the pavement starts. */
    const STREET = 128;
    const GAPS = [ 294, 589, 883, 1178 ];

    this.maSkyTop = mixHex( SAFFRON, '#FFFFFF', 0.42 );
    this.maSkyLow = mixHex( CORAL, '#FFFFFF', 0.72 );

    this.maBlocks = [];
    const windows: string[] = [];

    // ---- the facades ----
    // A run of blocks along the street, six or seven storeys, with a band of
    // windows per storey and a cornice on top. Their heights and widths both vary,
    // and the variation is not decoration: Gran Via is a street of buildings that
    // were put up one at a time by people competing with each other.
    const N = 24;
    // PINNED at both ends. The jitter used to apply at k=0 and k=N as well, so the
    // first plot did not start at 0 and the last did not finish at W — measured,
    // the tile wrapped with 129 of error against an interior median of 7.
    const edge = ( k: number ): number =>
      k * ( this.W / N )
      + ( k % N === 0 ? 0
        : ( fr( ( ( k % N ) + N ) % N * 0.4142 + 0.11 ) - 0.5 ) * ( this.W / N ) * 0.5 );
    /** The plot at each end of the row is the SAME building.
     *
     *  Pinning the edges is only half of it: the two plots that meet across the
     *  seam still had their own heights and their own plaster, so the join came out
     *  as one facade with a step in the middle of it. Giving the last plot the
     *  first one's height and colour makes the two halves read as one building the
     *  tile happens to be cut through. */
    const plot = ( i: number ): number => ( i === N - 1 ? 0 : i );
    // ONE unit of gap between plots, not three. These are terraced burgher houses
    // and they share party walls; at three units the row came out with dark slots
    // between the buildings, which reads as missing teeth rather than as a street.
    for ( let i = 0; i < N; i++ ) {
      const x0 = edge( i ) + 0.5, x1 = edge( i + 1 ) - 0.5;
      const w = x1 - x0;
      // A wider spread of heights than the first pass, which ran 44..78 and gave
      // a skyline as flat as a wall. Gran Via is a street of buildings put up one
      // at a time by people competing with each other, and it looks like it.
      const top = 34 + Math.pow( fr( plot( i ) * 0.7548 + 0.2 ), 1.3 ) * 54;
      // the block, in sunlit plaster — paler the higher it goes, because the sun
      // is behind it and low
      this.maBlocks.push( {
        d: `M${ f( x0 ) },${ f( STREET ) } L${ f( x0 ) },${ f( top ) }`
          + ` L${ f( x1 ) },${ f( top ) } L${ f( x1 ) },${ f( STREET ) } Z`,
        fill: mixHex( CREAM, plot( i ) % 3 === 1 ? CORAL : SAFFRON,
          0.12 + fr( plot( i ) * 0.271 ) * 0.20 ),
      } );
      // its cornice, a shade deeper
      this.maBlocks.push( {
        d: `M${ f( x0 - 2 ) },${ f( top ) } L${ f( x0 - 2 ) },${ f( top - 4 ) }`
          + ` L${ f( x1 + 2 ) },${ f( top - 4 ) } L${ f( x1 + 2 ) },${ f( top ) } Z`,
        fill: mixHex( SAFFRON, DEEP_OCHRE, 0.34 ),
      } );
      // storeys of windows, with a balcony rail under each row
      // TALLER windows, and the balcony rail only on every OTHER floor. The first
      // pass drew a 7-unit window with a full-width rail under every row, and what
      // came back was three short bars sitting on a line, repeated — a row of
      // teeth. A window is a hole taller than it is wide; a balcony is a thing
      // some of them have.
      const cols = Math.max( 2, Math.min( 5, Math.round( w / 13 ) ) );
      let floor = 0;
      for ( let y = top + 9; y < STREET - 11; y += 15, floor++ ) {
        for ( let c = 0; c < cols; c++ ) {
          const wx = x0 + 4 + c * ( ( w - 8 ) / cols );
          windows.push( `M${ f( wx ) },${ f( y ) } L${ f( wx ) },${ f( y + 10 ) }`
            + ` L${ f( wx + 4.6 ) },${ f( y + 10 ) } L${ f( wx + 4.6 ) },${ f( y ) } Z` );
        }
        if ( floor % 2 === 1 ) {
          windows.push( `M${ f( x0 + 3 ) },${ f( y + 10 ) } L${ f( x1 - 3 ) },${ f( y + 10 ) }`
            + ` L${ f( x1 - 3 ) },${ f( y + 11.2 ) } L${ f( x0 + 3 ) },${ f( y + 11.2 ) } Z` );
        }
      }
    }

    /** A dome on a drum, with a lantern on top — the shape half of this street
     *  ends its corner buildings with. */
    const dome = ( cx: number, w: number, base: number, h: number, fill: string ): void => {
      this.maBlocks.push( {
        d: `M${ f( cx - w ) },${ f( base ) } C${ f( cx - w ) },${ f( base - h ) }`
          + ` ${ f( cx + w ) },${ f( base - h ) } ${ f( cx + w ) },${ f( base ) } Z`
          + ` M${ f( cx - 2.4 ) },${ f( base - h * 0.95 ) } L${ f( cx - 2.4 ) },${ f( base - h - 8 ) }`
          + ` L${ f( cx + 2.4 ) },${ f( base - h - 8 ) } L${ f( cx + 2.4 ) },${ f( base - h * 0.95 ) } Z`,
        fill,
      } );
    };

    // ---- the landmarks ----
    // the METROPOLIS: a slate dome with the winged victory standing on it, and it
    // is the one silhouette on this street anybody can name
    const mx = GAPS[ 0 ];
    this.maBlocks.push( {
      d: `M${ mx - 34 },${ STREET } L${ mx - 34 },${ 52 } L${ mx + 34 },${ 52 } L${ mx + 34 },${ STREET } Z`,
      fill: mixHex( CREAM, SAFFRON, 0.16 ),
    } );
    dome( mx, 26, 52, 26, mixHex( SKY_BLUE, '#2C2418', 0.42 ) );
    // the victory: a small standing figure with its wings out. It is six points
    // and it is the reason this dome is not just a dome.
    this.maBlocks.push( {
      d: `M${ mx - 1.6 },${ 18 } L${ mx - 1.6 },${ 34 } L${ mx + 1.6 },${ 34 } L${ mx + 1.6 },${ 18 } Z`
        + ` M${ mx },${ 14 } L${ mx - 9 },${ 22 } L${ mx - 2 },${ 22 } L${ mx - 2 },${ 20 } Z`
        + ` M${ mx },${ 14 } L${ mx + 9 },${ 22 } L${ mx + 2 },${ 22 } L${ mx + 2 },${ 20 } Z`,
      fill: mixHex( DEEP_OCHRE, '#2C2418', 0.3 ),
    } );

    // two more domes, each a different size and colour
    dome( GAPS[ 1 ], 22, 58, 22, mixHex( CORAL, '#2C2418', 0.34 ) );
    dome( GAPS[ 2 ], 28, 54, 30, mixHex( DEEP_OCHRE, '#2C2418', 0.28 ) );

    // NO CAFE TERRACE. There was a table with the theme's own churro cup standing
    // on it in the fourth gap, and the owner's read was that it did not belong
    // (2026-08-16: "o sağdaki şeyi sil, kahve churrosu alakasız").
    //
    // It is the same verdict Prague's lamp, Reynisfjara's puffins and Spring's
    // flower got, and by now it is a rule rather than a coincidence: a menu strip
    // is a HORIZON, and a horizon does not have a hero object standing in it. The
    // cup was drawn at a scale that made it as tall as a four-storey building,
    // because anything smaller vanished — which is the tell that it was never
    // going to work there.
    //
    // The churro is still the theme's hero. It is the Home tab and all four
    // passcode dots, which is where an object that size belongs.

    this.maWindowD = windows.join( ' ' );
    this.maWindowFill = mixHex( DEEP_OCHRE, '#3A2A10', 0.45 );

    // ---- the street ----
    this.maStreetD = `M0,${ STREET } L${ this.W },${ STREET } L${ this.W },${ this.H } L0,${ this.H } Z`;
    // a band of shade where the buildings meet the pavement — the sun is behind
    // them, so the base of the street is the darkest thing in the scene and
    // without it the facades float
    this.maBlocks.push( {
      d: `M0,${ STREET - 9 } L${ this.W },${ STREET - 9 } L${ this.W },${ STREET } L0,${ STREET } Z`,
      fill: mixHex( DEEP_OCHRE, '#3A2A10', 0.35 ),
    } );
    this.maStreetTop = mixHex( SAFFRON, DEEP_OCHRE, 0.28 );
    this.maStreetLow = lineTabFill( p.key, p.appearance );
  }

  private buildVienna( p: Product ): void {
    const f = ( v: number ) => +v.toFixed( 1 );
    const fr = ( v: number ) => v - Math.floor( v );

    // The PAPER is the tab bar's own fill, so the sheet runs off the bottom of the
    // image straight onto the bar. Every scene in this family hands its foreground
    // off to that colour; this is the only one where the GROUND is the handoff,
    // which is what lets the strip have no bottom edge at all.
    this.viPaper = lineTabFill( p.key, p.appearance );
    // The ruling: warm grey-brown, the idle tab's own tone. Printed music is ruled
    // in a line you look past rather than at — 2.07:1 on the paper, which is a rule
    // you see and never read.
    this.viRule = '#C7B0A4';

    // ---- the staff ----
    // Five lines, each running the FULL width. This is the property the scene was
    // chosen for: a line with no ends cannot mismatch at the tile edge, and the
    // scene this replaced measured 145 of wrap error against an interior median of
    // 64 — more than twice its own worst internal step.
    //
    // The whole staff sits BELOW y=50. The template fades this image out from y=16
    // to y=46 (LINE wants the top of a menu background to disappear into the chat
    // above it), and the first pass put the top line at y=27, straight into that
    // ramp: a five-line staff with two lines missing is ruled paper.
    const GAP = 19, BOTTOM = 126, RULE = 2.8;
    const lineY = ( i: number ): number => BOTTOM - i * GAP;   // i = 0 (bottom) .. 4 (top)
    const staff: string[] = [];
    for ( let i = 0; i < 5; i++ ) {
      const y = lineY( i );
      staff.push( `M0,${ f( y ) } L${ this.W },${ f( y ) }`
        + ` L${ this.W },${ f( y + RULE ) } L0,${ f( y + RULE ) } Z` );
    }
    this.viStaffD = staff.join( ' ' );

    // ---- bar lines ----
    // UNEVEN. Evenly spaced verticals across a set of horizontals is a table, and
    // the scene this replaced failed in exactly that way — its eighteen identical
    // box piers read as a fence. Real bars are uneven because the music in them is.
    const bars: string[] = [];
    let bx = 0, k = 0;
    while ( true ) {
      bx += 210 + fr( k * 0.6180339887 + 0.21 ) * 160;
      k++;
      if ( bx >= this.W ) { break; }
      bars.push( `M${ f( bx ) },${ f( lineY( 4 ) ) } L${ f( bx + 3 ) },${ f( lineY( 4 ) ) }`
        + ` L${ f( bx + 3 ) },${ f( lineY( 0 ) + RULE ) } L${ f( bx ) },${ f( lineY( 0 ) + RULE ) } Z` );
    }
    this.viBarsD = bars.join( ' ' );

    // ---- the notes ----
    const INK = [ '#8A4B2E', '#A85E2C', '#6A2F34', '#B8722A' ];
    const HEAD_RX = 11, HEAD_RY = 7.8, STEM_W = 2.8, STEM_H = 36;
    // NOTE: there is no fade clamp any more. There used to be one — nothing was
    // allowed above y=54, where the template starts fading the image out — and it
    // was the direct cause of the broken beams: clamping a stem's top is the same
    // as deleting the stem. Stem DIRECTION solves it properly, since a high note
    // now stems downward and never asks for room up there at all.

    /** A note head, tilted the way an engraved one is, as a closed path. */
    const head = ( cx: number, cy: number ): string => {
      const t = -20 * Math.PI / 180, ct = Math.cos( t ), st = Math.sin( t );
      const pts: string[] = [];
      for ( let i = 0; i < 24; i++ ) {
        const a2 = i / 24 * Math.PI * 2;
        const x = Math.cos( a2 ) * HEAD_RX, y = Math.sin( a2 ) * HEAD_RY;
        pts.push( `${ f( cx + x * ct - y * st ) },${ f( cy + x * st + y * ct ) }` );
      }
      return 'M' + pts.join( ' L' ) + ' Z';
    };
    /**
     * A stem, on the correct SIDE and running in the correct DIRECTION.
     *
     * `up` is engraving's own rule: a note below the middle line stems upward off
     * its right shoulder, one above it stems downward off its left. Ignoring that
     * is what broke the first two passes here — every stem went up, so a note high
     * on the staff needed its beam above the top line, the fade ceiling pushed the
     * beam back down onto the note's own head, and the note ended up with a beam
     * for a hat and no stem at all.
     *
     * The rule is not decoration. It exists precisely so that a stem always has
     * somewhere to go.
     */
    const stemTo = ( cx: number, cy: number, end: number, up: boolean ): string => {
      const sx = up ? cx + HEAD_RX - 2.4 : cx - HEAD_RX + 2.4 - STEM_W;
      const from = up ? cy - 3 : cy + 3;
      return `M${ f( sx ) },${ f( from ) } L${ f( sx ) },${ f( end ) }`
        + ` L${ f( sx + STEM_W ) },${ f( end ) } L${ f( sx + STEM_W ) },${ f( from ) } Z`;
    };
    /**
     * The FLAG on a lone quaver: one sweep, never two — two is a semiquaver and
     * reads as a scribble at this size.
     *
     * It always sweeps to the RIGHT and only flips vertically. Mirroring it
     * horizontally with the stem was the obvious thing to do and it is wrong: a
     * down-stem flag on real engraved music still hangs off the right of the stem,
     * and mirrored it came out as a hook curling back under the note.
     */
    const flag = ( cx: number, end: number, up: boolean ): string => {
      const sx = up ? cx + HEAD_RX - 2.4 + STEM_W : cx - HEAD_RX + 2.4;
      const s = up ? 1 : -1;
      // SHORTER curling back than it sweeps out. On a down stem the flag curls
      // toward the head, and at 25 of reach against a 36 stem its tip landed on the
      // head's own edge and closed a loop around it — the note came out as a
      // lowercase b. Down stems are longer for the same reason (see STEM below).
      return `M${ f( sx ) },${ f( end ) } C${ f( sx + 15 ) },${ f( end + 5 * s ) }`
        + ` ${ f( sx + 16 ) },${ f( end + 14 * s ) } ${ f( sx + 9 ) },${ f( end + 20 * s ) }`
        + ` C${ f( sx + 13 ) },${ f( end + 12 * s ) } ${ f( sx + 10 ) },${ f( end + 7 * s ) } ${ f( sx ) },${ f( end + 8 * s ) } Z`;
    };

    this.viNotes = [];

    // Pitches come off the staff itself — a note sits ON a line or IN a gap, never
    // between the two, which is the difference between music and decoration. Steps
    // 0..8 span bottom line to top line, so nothing needs a ledger line.
    const step = ( n: number ): number => BOTTOM - n * ( GAP / 2 );
    /** Stems point up below the MIDDLE line and down above it. */
    const stemsUp = ( n: number ): boolean => n <= 4;

    /**
     * One note, or one BEAMED PAIR, drawn at an arbitrary x. Taking x as an
     * argument is what makes the wrap below possible: the same group is simply
     * drawn again a full width to the left.
     *
     * EVERY STEM POINTS UP AND EVERY BEAM SITS ABOVE ITS NOTES.
     *
     * The pairs went through three states to get here. They were drawn with the
     * beam hung off the wrong note, which put it on top of the higher note's head;
     * then engraved properly, which stems a high pair DOWNWARD with the beam
     * underneath; then deleted outright, when that read as upside down. The owner's
     * correction was that ONE of them had been right — the up-stem one — and that
     * is the one form kept.
     *
     * It is a fair verdict. The down-stem rule exists to keep stems inside the
     * staff on a full page of music; on a 36-pixel strip carrying six notes there
     * is no page to be consistent with, and half the notes pointing the other way
     * just looks like half the notes are wrong.
     */
    const group = ( gx: number, i: number ): { d: string, wide: number, fill: string } => {
      const fill = INK[ i % INK.length ];
      // The bottom positions only. An up stem needs room above the head and the
      // template fades this image out above y=54, so a note any higher would put
      // its stem into the fade and come out dissolving halfway up. A PAIR needs
      // more of that room than a single does, because its beam has to clear the
      // higher of the two heads — hence the tighter range below.
      const paired = fr( i * 0.4142 + 0.11 ) > 0.5;
      const span = paired ? 3 : 4;
      const pitch = Math.round( fr( i * 0.6180339887 + 0.37 ) * span );
      const y1 = step( pitch );
      if ( !paired ) {
        const end = y1 - 32;
        return { d: `${ head( gx, y1 ) } ${ stemTo( gx, y1, end, true ) } ${ flag( gx, end, true ) }`,
          wide: 36, fill };
      }
      const x2 = gx + 46;
      const p2 = Math.max( 0, Math.min( span, pitch + Math.round( ( fr( i * 0.7548776662 + 0.29 ) - 0.5 ) * 5 ) ) );
      const y2 = step( p2 );
      // The beam hangs off the HIGHER note, so both stems keep a real length. Off
      // the lower one it lands on the higher note's head and that note loses its
      // stem entirely, which is how this broke the first time.
      const base = Math.min( y1, y2 ) - 34;
      // it leans WITH the interval, but only a quarter of it
      const lean = Math.max( -7, Math.min( 7, ( y2 - y1 ) * 0.25 ) );
      const t1 = base - lean / 2, t2 = base + lean / 2;
      // flush ends: the left stem's LEFT edge to the right stem's RIGHT edge, so
      // the beam overhangs neither and leaves no step
      const a1 = gx + HEAD_RX - 2.4, a2 = x2 + HEAD_RX - 2.4 + STEM_W;
      const beam = `M${ f( a1 ) },${ f( t1 ) } L${ f( a2 ) },${ f( t2 ) }`
        + ` L${ f( a2 ) },${ f( t2 + 7 ) } L${ f( a1 ) },${ f( t1 + 7 ) } Z`;
      return {
        d: `${ head( gx, y1 ) } ${ head( x2, y2 ) }`
          + ` ${ stemTo( gx, y1, t1, true ) } ${ stemTo( x2, y2, t2, true ) } ${ beam }`,
        wide: 82, fill,
      };
    };

    let x = 84;
    let i = 0;
    while ( x < this.W ) {
      const g = group( x, i );
      this.viNotes.push( { d: g.d, fill: g.fill } );
      // WRAP. A group that runs off the right edge is drawn again off the left, so
      // the tile joins mid-note instead of cutting one in half. The staff lines do
      // not need this; nothing else in the image is exempt from it.
      if ( x + g.wide > this.W ) {
        const w = group( x - this.W, i );
        this.viNotes.push( { d: w.d, fill: w.fill } );
      }
      x += g.wide + 66 + fr( i * 0.271 + 0.53 ) * 82;
      i++;
    }
  }

  private buildSpring( p: Product ): void {
    const f = ( v: number ) => +v.toFixed( 1 );
    const fr = ( v: number ) => v - Math.floor( v );

    // The theme's own palette (base-themes/nature/spring-theme), by name.
    const NEW_LEAF = '#C8EAB9';
    const MEADOW = '#559565';
    const DEEP_GRASS = '#3C8F63';
    const ROSE = '#BA6173';
    const BLOSSOM = '#DC8395';
    const VIOLET = '#8C68AC';
    const LILAC = '#D5B3F4';
    const CORNFLOWER = '#527FB6';
    const HAZE_BLUE = '#5E9AAE';

    /** Where the meadow starts. */
    const GRASS = 96;
    const GAPS = [ 294, 589, 883, 1178 ];

    this.spSkyTop = mixHex( CORNFLOWER, '#8FD8F2', 0.62 );
    this.spSkyLow = mixHex( '#FFFFFF', HAZE_BLUE, 0.14 );

    /** A band whose top edge waves at integer frequency, so the strip tiles. */
    const band = ( base: number, waves: [ number, number, number ][], to: number ): string => {
      const pts: string[] = [];
      for ( let i = 0; i <= 96; i++ ) {
        const t = i / 96, x = t * this.W;
        let y = base;
        for ( const [ k, amp, ph ] of waves ) { y += Math.sin( t * Math.PI * 2 * k + ph ) * amp; }
        pts.push( `${ f( x ) },${ f( y ) }` );
      }
      return `M0,${ f( to ) } L` + pts.join( ' L' ) + ` L${ this.W },${ f( to ) } Z`;
    };

    // ---- clouds ----
    // Soft, flat and PALE, and only in the upper half. The icon's are cumulus
    // puffs; at strip scale a puff outline turns to mush, so these are stacked
    // flat ellipses instead — which is what a cumulus reads as from far enough
    // away, and far enough away is where this strip is standing.
    this.spClouds = [];
    // TEN, and smaller. Sixteen at up to 96 units across filled the sky and left
    // the strip reading as cloud with a meadow under it.
    for ( let i = 0; i < 10; i++ ) {
      const cx = 40 + fr( i * 0.618 + 0.27 ) * ( this.W - 80 );
      const cy = 24 + fr( i * 0.4142 + 0.6 ) * 36;
      const rx = 26 + fr( i * 0.7548 ) * 44;
      const ry = 4 + fr( i * 0.271 ) * 4;
      this.spClouds.push( {
        d: `M${ f( cx - rx ) },${ f( cy ) } a${ f( rx ) },${ f( ry ) } 0 1,0 ${ f( rx * 2 ) },0`
          + ` a${ f( rx ) },${ f( ry ) } 0 1,0 ${ f( -rx * 2 ) },0 Z`,
        o: +( 0.38 + fr( i * 0.3819 ) * 0.30 ).toFixed( 2 ),
      } );
    }

    // ---- the range ----
    // Two ranks, the back one hazier — and both PALE, because a green hill painted
    // in its own local colour reads as a hill two metres away rather than as one
    // across a valley.
    this.spHills = [
      { d: band( GRASS - 22, [ [ 1, 9, 0.4 ], [ 3, 4, 2.1 ] ], GRASS + 4 ),
        fill: mixHex( MEADOW, '#DDF3FB', 0.62 ) },
      { d: band( GRASS - 11, [ [ 2, 7, 1.7 ], [ 5, 3, 0.3 ] ], GRASS + 4 ),
        fill: mixHex( MEADOW, '#DDF3FB', 0.40 ) },
    ];

    // ---- the sward ----
    // Three bands of grass, each deeper and nearer than the last.
    this.spSward = [
      { d: band( GRASS, [ [ 1, 4, 1.2 ] ], this.H ), fill: mixHex( MEADOW, NEW_LEAF, 0.46 ) },
      { d: band( GRASS + 12, [ [ 2, 5, 3.1 ] ], this.H ), fill: MEADOW },
      { d: band( GRASS + 26, [ [ 1, 6, 5.2 ] ], this.H ), fill: mixHex( MEADOW, DEEP_GRASS, 0.55 ) },
    ];

    /** One blade of grass: a tapered ribbon that bends more the higher it goes. */
    const blade = ( x: number, h: number, lean: number, w: number, root: number ): string => {
      const N = 12;
      const l: string[] = [], r: string[] = [];
      for ( let i = 0; i <= N; i++ ) {
        const t = i / N;
        const y = root - h * t;
        const cx = x + lean * t * t;
        const half = w * ( 1 - t * 0.9 ) / 2;
        l.push( `${ f( cx - half ) },${ f( y ) }` );
        r.push( `${ f( cx + half ) },${ f( y ) }` );
      }
      return 'M' + l.join( ' L' ) + ' L' + r.reverse().join( ' L' ) + ' Z';
    };

    // ---- blades ----
    this.spBlades = [];
    // NINETY blades and none of them near-black. The first pass ran 130 and mixed
    // the front rank toward #123407, which put a field of dark spikes across the
    // meadow — grass in sunlight is not darker than the sward it grows out of, it
    // is a different green at roughly the same weight.
    for ( let i = 0; i < 90; i++ ) {
      const x = fr( i * 0.6180339887 + 0.13 ) * this.W;
      const near = fr( i * 0.271 + 0.4 );
      const root = GRASS + 14 + near * 30;
      const h = 11 + near * 17;
      this.spBlades.push( {
        d: blade( x, h, ( fr( i * 0.3819660112 + 0.7 ) - 0.4 ) * 16, 2.2 + near * 1.6, root ),
        fill: mixHex( DEEP_GRASS, near > 0.6 ? MEADOW : NEW_LEAF, 0.25 + near * 0.35 ),
      } );
    }

    /**
     * One LUPINE SPIKE: a stem with pea blooms alternating up it, tapering to a
     * bud at the tip.
     *
     * The alternation is the whole thing. Blooms placed symmetrically either side
     * read as a fir tree; offset up the stem by half a step they read as a raceme,
     * which is what a lupine is.
     */
    const spike = ( x: number, root: number, h: number, lean: number, seed: number ): string => {
      const parts: string[] = [];
      // DENSE. The first pass stepped the blooms 7 units apart and offset them a
      // full radius either side of the stem, and what came back was a chain of
      // circles zigzagging up a wire — a caterpillar, not a flower. A real lupine
      // raceme is packed: the blooms OVERLAP both their neighbours and the stem,
      // so the spike reads as one solid tapering object with a texture, which is
      // the only thing that survives at strip scale anyway.
      // Tighter still: at h/3.4 the individual blooms were still countable as
      // circles at 120px, which is a bead necklace. At h/2.4 they merge into a
      // texture and only the spike's outline is a shape.
      const N = Math.round( h / 2.4 );
      parts.push( blade( x, h, lean, 3.2, root ) );
      for ( let i = 0; i < N; i++ ) {
        const t = i / N;
        const y = root - h * ( 0.14 + t * 0.86 );
        const cx = x + lean * ( 0.14 + t * 0.86 ) * ( 0.14 + t * 0.86 );
        const side = i % 2 ? 1 : -1;
        const r = ( 4.6 - t * 3.0 ) * ( 0.85 + fr( ( seed + i ) * 0.618 ) * 0.3 );
        // offset by a THIRD of a radius, not a whole one — enough that the spike
        // has a texture rather than a smooth edge, little enough that it never
        // becomes a zigzag
        parts.push( `M${ f( cx + side * r * 0.32 - r ) },${ f( y ) }`
          + ` a${ f( r ) },${ f( r * 0.82 ) } 0 1,0 ${ f( r * 2 ) },0`
          + ` a${ f( r ) },${ f( r * 0.82 ) } 0 1,0 ${ f( -r * 2 ) },0 Z` );
      }
      return parts.join( ' ' );
    };

    // ---- the flower spikes ----
    // TALL in the tab gaps, SHORT everywhere else — so nothing ever grows through
    // the row of tabs, and the four gaps get the four best clumps.
    this.spSpikes = [];
    const SPIKE_COLORS = [ VIOLET, LILAC, mixHex( VIOLET, LILAC, 0.5 ), BLOSSOM,
                           mixHex( VIOLET, CORNFLOWER, 0.35 ) ];
    for ( let i = 0; i < 54; i++ ) {
      const x = 14 + fr( i * 0.6180339887 + 0.31 ) * ( this.W - 28 );
      // how close this spike is to the middle of a tab gap, 0..1
      const near = Math.max( ...GAPS.map( ( g ) => Math.max( 0, 1 - Math.abs( x - g ) / 90 ) ) );
      const root = GRASS + 18 + fr( i * 0.271 + 0.2 ) * 26;
      const h = 16 + near * 42 + fr( i * 0.4142 + 0.5 ) * 12;
      this.spSpikes.push( {
        d: spike( x, root, h, ( fr( i * 0.7548 + 0.1 ) - 0.45 ) * 14, i ),
        fill: SPIKE_COLORS[ i % SPIKE_COLORS.length ],
      } );
    }

    // ---- NO HERO FLOWER ----
    //
    // The theme's own bellflower stood here, in the second tab gap, and it is
    // gone. It was drawn at 0.62 and read as a torn red FLAG on a pole; taken down
    // to meadow height it still did, because the failure is not scale. At strip
    // resolution the bell's scalloped hem coarsens into one straight edge and what
    // is left is a folded shape on a thin stick — and the one bellflower in a
    // field of lupine spikes has nothing beside it to be compared against.
    //
    // This is the third hero object removed from a strip in two days: Prague's
    // lamp and Reynisfjara's puffins went the same way and for the same reason. A
    // strip is 1472 units of scene, and a single glyph parked in it is not a
    // landmark — it is whatever happens to be standing there. The meadow is the
    // subject; the bellflower keeps the Home tab, the passcode and the wallpaper.
    //
    // NOTE (open): the glyph and the strip still draw DIFFERENT plants — a
    // bellflower on the tab, lupine spikes in the meadow. That is a real
    // inconsistency and it is the owner's call to settle, since the bellflower was
    // their choice when it replaced a hummingbird.
    this.spBirdD = '';
    this.spBirdFill = '';
    this.spBirdTransform = '';

    // ---- the foreground haze ----
    // A short band of sunlit blur at the very bottom, ramping into the tab bar's
    // own fill. It is what a meadow does to a lens focused past it, and it is how
    // this scene hands off to the flat colour LINE paints below the strip.
    this.spHazeD = `M0,${ this.H - 22 } L${ this.W },${ this.H - 22 }`
      + ` L${ this.W },${ this.H } L0,${ this.H } Z`;
    this.spHazeTop = mixHex( MEADOW, NEW_LEAF, 0.5 );
    this.spHazeLow = lineTabFill( p.key, p.appearance );
  }

  private buildReynisfjara( p: Product ): void {
    const f = ( v: number ) => +v.toFixed( 1 );
    const fr = ( v: number ) => v - Math.floor( v );

    // The theme's own palette (base-themes/nature/reynisfjara-theme), by name.
    const PITCH = '#060709';
    const NIGHT = '#151A20';
    const SLATE_DARK = '#20262C';
    const SLATE = '#2A3138';
    const MIST = '#7D848D';
    const BASALT_BROWN = '#8E8276';
    const DRIFTWOOD = '#AC8563';
    const SAND_LIGHT = '#C5A98F';
    const BONE = '#C3B9AD';

    /** The horizon — held BELOW the glyph band, which is the whole reason this
     *  scene is at dusk rather than under the icon's white sky. */
    const HORIZON = 96;
    /** Where the sea gives way to the sand. */
    const SHORE = 118;
    const GAPS = [ 294, 589, 883, 1178 ];

    // LIGHTER than the first pass at both ends. At #12161C over #39434F the whole
    // strip came back as one near-black band: the dusk was legible as a mood and
    // as nothing else, and on a phone it would simply have been a dark stripe
    // under the tabs. Dusk is not night — there is still a sky up there.
    // A WIDER value range than the pass before, which ran the whole sky between
    // two greys eight steps apart and came back as one flat field. Dark at the top,
    // and the horizon carries the warmth (see reGlow below).
    this.reSkyTop = mixHex( NIGHT, PITCH, 0.42 );
    // A touch of the theme's DRIFTWOOD in the low sky. This is the sternest theme
    // in the set and the kawaii note is not going to come from its shapes — a sea
    // stack cannot be made cute without lying about it — so it comes from
    // temperature: the last light is warm, the way it actually is at that hour,
    // and a warm horizon is the difference between moody and bleak.
    this.reSkyLow = mixHex( mixHex( SLATE, MIST, 0.52 ), DRIFTWOOD, 0.18 );

    /** A band whose top edge waves at integer frequency, so the strip tiles. */
    const band = ( base: number, waves: [ number, number, number ][], to: number ): string => {
      const pts: string[] = [];
      for ( let i = 0; i <= 96; i++ ) {
        const t = i / 96, x = t * this.W;
        let y = base;
        for ( const [ k, amp, ph ] of waves ) { y += Math.sin( t * Math.PI * 2 * k + ph ) * amp; }
        pts.push( `${ f( x ) },${ f( y ) }` );
      }
      return `M0,${ f( to ) } L` + pts.join( ' L' ) + ` L${ this.W },${ f( to ) } Z`;
    };

    // ---- the last light ----
    // A pale band lying on the horizon and nowhere else. It is the only bright
    // thing in the scene and it is deliberately below y 96, so no tab glyph ever
    // has to be legible against it.
    this.reGlowD = `M0,${ HORIZON - 30 } L${ this.W },${ HORIZON - 30 }`
      + ` L${ this.W },${ HORIZON + 2 } L0,${ HORIZON + 2 } Z`;

    // ---- the sea ----
    this.reSeaD = band( HORIZON, [ [ 1, 1.5, 0.6 ] ], SHORE + 4 );
    // a step LIGHTER than the sand below it, so the two dark bands are two bands
    // rather than one — the surf alone was carrying that and it is a thin line
    this.reSeaFill = mixHex( SLATE, NIGHT, 0.12 );

    // ---- the headland ----
    // A low dark mass running the whole width, behind everything. Without it the
    // scene is two landmarks with a long empty middle between them; with it the
    // beach has a back wall, which is what Reynisfjara actually has.
    this.reRock = [];
    this.reRock.push( {
      // LOW. At an amplitude of 9 around y 90 this was a black mass across the
      // whole strip that swallowed the sea and buried the stacks to their
      // shoulders — a back wall is meant to close the scene, not to be it. Four
      // units of relief just above the waterline does the job.
      d: band( HORIZON + 2, [ [ 1, 4, 2.4 ], [ 2, 2.5, 0.8 ], [ 4, 1.5, 4.1 ] ], HORIZON + 8 ),
      fill: mixHex( SLATE, PITCH, 0.66 ),
    } );

    // ---- the rock ----
    // Both of this beach's forms of it, and they are the same lava: the CLIFF of
    // hexagonal columns behind the sand, and the STACKS standing offshore.

    // the column cliff, in the first tab gap. Columns of varying height with
    // their tops stepping — the real Gardar wall does exactly this, and the step
    // is what stops a row of vertical bars reading as a fence.
    // The COLUMNS, and this is the thing the glyph module's own docs warn about:
    // a row of same-width bars with flat tops is a bar chart, and the first pass
    // duly produced one. Three changes fix it, and they are the three the real
    // Gardar wall has:
    //
    //   the widths vary by more than two to one, so no two neighbours pair up;
    //   the tops follow a CLIFF PROFILE — an arc, high in the middle, falling
    //   away at both ends — rather than a jitter around one height;
    //   every column's top is SLOPED, and they do not all slope the same way.
    for ( let i = 0; i < 30; i++ ) {
      const t = i / 29;
      const cw2 = 5 + fr( i * 0.4142 + 0.2 ) * 8;
      const x = GAPS[ 0 ] - 108 + i * 7.2;
      // the cliff's own profile: an arc rather than a level line
      const profile = HORIZON - 8 - Math.sin( t * Math.PI ) * 46;
      const top = profile + fr( i * 0.618 + 0.3 ) * 14;
      const slope = ( fr( i * 0.7548 + 0.5 ) - 0.5 ) * 5;
      this.reRock.push( {
        d: `M${ f( x ) },${ f( SHORE ) } L${ f( x ) },${ f( top ) }`
          + ` L${ f( x + cw2 ) },${ f( top + slope ) } L${ f( x + cw2 ) },${ f( SHORE ) } Z`,
        // LIGHTER than the first pass, which mixed them 62-78% toward pitch and
        // put the cliff below the sky it stands against. Basalt is dark; it is
        // not invisible.
        // COOL, not tan. The theme's Basalt token is a warm grey-brown and mixed
        // toward pitch on its own it stays warm — the first pass produced a
        // sandstone cliff, which is the wrong rock on the wrong island. Pulled
        // toward the slate first, it comes out the cold near-black basalt is.
        fill: mixHex( mixHex( BASALT_BROWN, SLATE, 0.55 ), PITCH,
                      0.34 + fr( i * 0.271 ) * 0.20 ),
      } );
    }

    // ---- NO PUFFINS ON THE SAND (owner 2026-08-16) ----
    //
    // Four of them stood along the shoreline here, and they are gone. The strip
    // had already swapped its sea stacks for them once, on the argument that the
    // rocks were the wrong subject; this removes the replacement rather than
    // restoring the rocks.
    //
    // What it leaves is the beach itself, which is what this theme is named for: a
    // basalt column cluster at one end, black sand, surf, and a low dusk sky. The
    // puffin keeps the Home tab, the four passcode dots and the chat wallpaper, so
    // the theme's own animal is not lost — only the copies that were standing in
    // the scene it lives on.

    // ---- surf ----
    // Thin white edges running up the beach. On a black sand beach the surf is the
    // brightest thing at ground level and it is what tells the sea from the sand,
    // which are otherwise two dark bands.
    this.reSurf = [];
    for ( let k = 0; k < 3; k++ ) {
      this.reSurf.push( {
        // thicker and stronger than the first pass — on a beach this dark the
        // surf is the only thing at ground level with any light in it, and at 3
        // units and half opacity it was a hairline
        // Between the two extremes it was drawn at: a hairline nobody could see,
        // then a white band that read as a road marking. Three units at half
        // opacity is surf.
        d: band( SHORE - 10 + k * 5, [ [ 1 + k, 4 - k, 1.2 + k * 2 ] ], SHORE - 7 + k * 5 ),
        o: +( 0.5 - k * 0.14 ).toFixed( 2 ),
      } );
    }

    // ---- the black sand ----
    this.reSandD = band( SHORE, [ [ 1, 3, 1.4 ], [ 3, 1.5, 2.8 ] ], this.H );
    this.reSandTop = mixHex( NIGHT, PITCH, 0.3 );
    this.reSandLow = lineTabFill( p.key, p.appearance );

    // ---- wet sand ----
    // The sky lying on the beach in flat sheets — the thing everyone photographs
    // there, and the only way a band of near-black reads as WET rather than as
    // empty. Each sheet is a long flat ellipse in the horizon's own pale tone.
    this.reWet = [];
    for ( let i = 0; i < 34; i++ ) {
      const x = fr( i * 0.6180339887 + 0.29 ) * this.W;
      const t = fr( i * 0.7548776662 + 0.13 );
      const y = SHORE + 5 + t * 24;
      const rx = 30 + t * 40 + fr( i * 0.4142 ) * 24;
      // Drawn again on the far side when it crosses an edge. A wet sheet runs up
      // to 94 units wide on a canvas LINE repeats, and one cut at x = W leaves a
      // hard vertical edge along the beach — which measured 48 at the join against
      // a typical interior joint of 9, the worst seam in the run after Sydney's.
      const o = +( 0.30 - t * 0.16 ).toFixed( 2 );
      const sheet = ( cx: number ): string =>
        `M${ f( cx - rx ) },${ f( y ) } a${ f( rx ) },2.6 0 1,0 ${ f( rx * 2 ) },0`
        + ` a${ f( rx ) },2.6 0 1,0 ${ f( -rx * 2 ) },0 Z`;
      this.reWet.push( { d: sheet( x ), o } );
      if ( x + rx > this.W ) { this.reWet.push( { d: sheet( x - this.W ), o } ); }
      if ( x - rx < 0 ) { this.reWet.push( { d: sheet( x + this.W ), o } ); }
    }
  }

  private buildSydney( p: Product ): void {
    const f = ( v: number ) => +v.toFixed( 1 );
    const fr = ( v: number ) => v - Math.floor( v );

    // The theme's own palette (base-themes/cities/sydney-theme), by name.
    const PALE_HARBOUR = '#CAE4EC';
    const MID_HARBOUR = '#73B9D0';
    const HARBOUR = '#0F91C7';
    const DEEP_HARBOUR = '#0E5E76';
    const WATTLE = '#ECD268';
    const SANDSTONE = '#AF8427';
    const EUCALYPT = '#2F9383';
    const TERRA = '#D17F51';
    const NEAR_BLACK = '#062E3A';

    /** The waterline. Everything built stands on it. */
    const QUAY = 104;
    const GAPS = [ 294, 589, 883, 1178 ];

    // Blue above, warm at the horizon — the icon's own sky, and the reverse of
    // the usual gradient. It matters for the glyphs: the palest part of the sky
    // is at the BOTTOM of the band they land in, so the deepest a dark glyph ever
    // has to work is the top, where the sky is bluest and the contrast best.
    this.sySkyTop = mixHex( HARBOUR, '#FFFFFF', 0.42 );
    this.sySkyLow = mixHex( WATTLE, '#FFFFFF', 0.72 );

    /** A band whose top edge waves at integer frequency, so the strip tiles. */
    const band = ( base: number, waves: [ number, number, number ][], to: number ): string => {
      const pts: string[] = [];
      for ( let i = 0; i <= 96; i++ ) {
        const t = i / 96, x = t * this.W;
        let y = base;
        for ( const [ k, amp, ph ] of waves ) { y += Math.sin( t * Math.PI * 2 * k + ph ) * amp; }
        pts.push( `${ f( x ) },${ f( y ) }` );
      }
      return `M0,${ f( to ) } L` + pts.join( ' L' ) + ` L${ this.W },${ f( to ) } Z`;
    };

    // ---- the far shore ----
    // A low green headland, and low is the point: the icon's own is a wedge at
    // one edge rather than a range across the back, because Sydney's harbour is
    // not ringed by hills. Two ranks, the back one hazier.
    // TALLER and greener than the first pass, where it was a sliver behind the
    // towers and might as well not have been drawn.
    // Between the two extremes it was drawn at: a sliver nobody could see, then a
    // green wall across the whole back of the scene. Sydney's harbour has bush on
    // its headlands and towers in front of them, so the range shows BETWEEN the
    // buildings and not above them.
    this.syLandD = band( QUAY - 18, [ [ 1, 7, 1.2 ], [ 3, 3.5, 3.4 ] ], QUAY + 2 );
    this.syLandFill = mixHex( EUCALYPT, '#FFFFFF', 0.26 );

    // ---- the city ----
    // A low run of blocks along the shore, flat-topped and pale — Sydney's front
    // is towers and glass, not the pitched roofs Prague and Tallinn have. Their
    // heights vary a lot and their windows are horizontal bands rather than
    // punched holes, which is the cheapest way to say "modern" at strip scale.
    this.syCity = [];
    const N = 30;
    //
    // The plot jitter is taken from k MODULO N, and the boundary at k = 0 (and
    // therefore at k = N) carries none. That is what makes the row TILE. Without
    // it the last tower ran past x = W and was clipped while the first began at a
    // different offset — and LINE repeats this strip, so the mismatch showed as a
    // hard vertical line every 1472 units. It measured 46 at the join against a
    // typical interior joint of 7.5, and this scene is the one where that matters
    // most: it is a smooth gradient of sky over water, so there is nothing else in
    // the image for a hard edge to hide behind.
    const edge = ( k: number ): number =>
      k * ( this.W / N )
      + ( k % N === 0 ? 0
        : ( fr( ( ( k % N ) + N ) % N * 0.4142 + 0.11 ) - 0.5 ) * ( this.W / N ) * 0.5 );
    //
    // The first and last plots are ONE BUILDING split across the join: plot 0 runs
    // from x = 0 and plot N-1 to x = W, and both take plot 0's height and tone.
    // Pinning the boundary to the canvas edge is not enough on its own — the two
    // towers either side of it are different heights and different tones, so the
    // edge column of one and the edge column of the other disagree by exactly the
    // half pixel the rasteriser blends. Making them the same building removes the
    // disagreement instead of hiding it.
    for ( let i = 0; i < N; i++ ) {
      const seam = i === 0 || i === N - 1;
      const x0 = i === 0 ? 0 : edge( i ) + 0.5;
      const x1 = i === N - 1 ? this.W : edge( i + 1 ) - 0.5;
      const w = x1 - x0;
      const top = QUAY - 8 - Math.pow( fr( ( seam ? 0 : i ) * 0.7548 + 0.2 ), 1.6 ) * 34;
      // Less white than the first pass, which mixed to 0.30..0.60 and produced
      // a row of ghosts: on a pale sky a pale building has no silhouette at all,
      // and a skyline without a silhouette is a smudge along the horizon.
      const fill = mixHex( MID_HARBOUR, '#FFFFFF', 0.02 + fr( ( seam ? 0 : i ) * 0.271 ) * 0.26 );
      // WHAT THE TOP DOES. Every tower used to be a flat-capped rectangle, which
      // is the same failure Prague's burgher houses had and was rejected for the
      // same reason: at phone scale a row of plain boxes is any city on earth. A
      // modern skyline is not made of one shape either — it is setbacks, tapers
      // and the odd mast, and those three read at 4px where a window band does
      // not.
      //
      // Tall plots get the interesting tops. A setback on a short wide block is
      // not a setback, it is a bite out of a shed.
      const tall = QUAY - top > 24;
      const kind = seam ? 0 : ( tall ? Math.floor( fr( i * 0.618 + 0.31 ) * 3 ) : 0 );
      if ( kind === 1 ) {
        // SETBACK: the tower steps in once on its way up, the shape a deco-era
        // office block takes and the one Sydney's older towers actually have.
        const sb = QUAY - ( QUAY - top ) * 0.62;
        const inset = w * 0.22;
        this.syCity.push( {
          d: `M${ f( x0 ) },${ f( QUAY ) } L${ f( x0 ) },${ f( sb ) }`
            + ` L${ f( x0 + inset ) },${ f( sb ) } L${ f( x0 + inset ) },${ f( top ) }`
            + ` L${ f( x1 - inset ) },${ f( top ) } L${ f( x1 - inset ) },${ f( sb ) }`
            + ` L${ f( x1 ) },${ f( sb ) } L${ f( x1 ) },${ f( QUAY ) } Z`,
          fill,
        } );
      } else if ( kind === 2 ) {
        // TAPER + MAST: the crown narrows and carries a thin spire. Two of the
        // three tallest things on this waterfront do exactly this, and a mast is
        // four points.
        const cr = QUAY - ( QUAY - top ) * 0.86;
        this.syCity.push( {
          d: `M${ f( x0 ) },${ f( QUAY ) } L${ f( x0 ) },${ f( cr ) }`
            + ` L${ f( x0 + w * 0.24 ) },${ f( top ) } L${ f( x1 - w * 0.24 ) },${ f( top ) }`
            + ` L${ f( x1 ) },${ f( cr ) } L${ f( x1 ) },${ f( QUAY ) } Z`
            + ` M${ f( ( x0 + x1 ) / 2 - 0.9 ) },${ f( top ) }`
            + ` L${ f( ( x0 + x1 ) / 2 - 0.9 ) },${ f( top - 13 ) }`
            + ` L${ f( ( x0 + x1 ) / 2 + 0.9 ) },${ f( top - 13 ) }`
            + ` L${ f( ( x0 + x1 ) / 2 + 0.9 ) },${ f( top ) } Z`,
          fill,
        } );
      } else {
        this.syCity.push( {
          d: `M${ f( x0 ) },${ f( QUAY ) } L${ f( x0 ) },${ f( top ) }`
            + ` L${ f( x1 ) },${ f( top ) } L${ f( x1 ) },${ f( QUAY ) } Z`,
          fill,
        } );
      }
      // one or two glass bands per tower, in a slightly deeper tone
      for ( let r = 0; r < 2; r++ ) {
        const y = top + 5 + r * 8;
        if ( y > QUAY - 4 ) { break; }
        this.syCity.push( {
          d: `M${ f( x0 + 1.5 ) },${ f( y ) } L${ f( x0 + 1.5 ) },${ f( y + 3 ) }`
            + ` L${ f( x1 - 1.5 ) },${ f( y + 3 ) } L${ f( x1 - 1.5 ) },${ f( y ) } Z`,
          fill: mixHex( DEEP_HARBOUR, '#FFFFFF', 0.52 ),
        } );
      }
    }

    // ---- the landmarks, one per tab gap ----
    //
    // THE OPERA HOUSE IS BACK, in the first gap (owner 2026-08-15). It stood here
    // once, was removed on the owner's call — the scene reading as a postcard
    // rather than as a working harbour — and is asked for again now. Both calls
    // are theirs; what is worth keeping is that the objection was never to the
    // building, it was to the scene being ABOUT the building.
    //
    // So it is drawn as one more thing on the waterfront rather than as the
    // subject: it sits low, its tallest shell stops well under the tower line
    // beside it, and it takes the same pale tone the city does. The bridge two
    // gaps along is the other silhouette, and neither outranks the fleet.
    //
    // Drawn as a FAN OF SHELLS, largest at the back stepping down to the front,
    // all leaning one way. That lean is the whole reading: shells drawn upright
    // and symmetrical are a row of tents, which is the failure the glyph version
    // of this building hit before it was replaced by a boat.
    // Well LEFT of the first tab gap, not on it. Centred on the gap it landed
    // directly behind the fleet's largest yacht — the boat is drawn after the
    // shore and in front of it, so the building was simply covered — and a
    // landmark hidden behind a boat is worse than no landmark.
    // Well LEFT of the first tab gap, not on it. Centred on the gap it landed
    // directly behind the fleet's largest yacht — the boat is drawn after the
    // shore and in front of it, so the building was simply covered — and a
    // landmark hidden behind a boat is worse than no landmark.
    const ox = GAPS[ 0 ] - 78;

    // ONE SHELL, and it leans LEFT.
    //
    // The direction is the whole drawing. The first pass had the apexes leaning
    // RIGHT and the glazing tucked under the back of each shell, and the owner's
    // read was that the glass then makes no sense — which is right, and it is a
    // fact about the building rather than about the drawing. The Opera House's
    // shells are open at ONE end: the glazed wall hangs under the mouth of each
    // shell, facing out over the water. Point the shells away from the viewer and
    // the glass ends up on the hidden side, so painting it there is drawing a wall
    // that could not be seen from where the scene is standing.
    //
    // Leaning left, the mouths face right, down the harbour and toward the fleet.
    // The glazing is then exactly where the eye expects it.
    //
    //   the BACK edge (right) is convex, sweeping up from the foot;
    //   the FRONT edge (left) is CONCAVE, falling away from the apex;
    //   the two meet at a corner rather than over a radius.
    //
    // An earlier pass rounded that apex and let the shells overlap almost
    // entirely, and what came out was a single lumpy mound — an iceberg on the
    // waterfront. A shell without a point is a dune, and a fan without gaps is a
    // drift.
    // `base` is the deck line this shell stands on, which is NOT one value any
    // more: a shell further back sits higher up the receding deck.
    const shellAt = ( bx: number, base: number, w: number, h: number ): string => {
      const ax = bx + w * 0.20, ay = base - h;
      return `M${ f( bx + w ) },${ f( base ) }`
        + ` C${ f( bx + w * 0.96 ) },${ f( base - h * 0.58 ) }`
        + ` ${ f( ax + w * 0.34 ) },${ f( ay + h * 0.20 ) } ${ f( ax ) },${ f( ay ) }`
        + ` C${ f( ax - w * 0.06 ) },${ f( ay + h * 0.30 ) }`
        + ` ${ f( bx + w * 0.06 ) },${ f( base - h * 0.44 ) } ${ f( bx ) },${ f( base ) } Z`;
    };

    // NO GLAZING. The owner's own icon puts a blue glass wedge in each shell and
    // it was drawn here twice — once with the shells leaning right and once
    // mirrored so the glazed mouths face the harbour — and dropped both times
    // (owner 2026-08-16: "yapamıyorsun, kaldır camları").
    //
    // Worth recording so it is not attempted a third time. The wedge needs THREE
    // readable parts inside one shell: the concrete rib, the glass under its mouth
    // and the shadow between them. The tallest shell here is 40 units on a strip
    // that renders about a quarter of a pixel per unit, so the glass lands at
    // roughly two pixels — and two pixels of dark inside a white shape is not a
    // window, it is a smudge that makes the shell look damaged. The icon can carry
    // it because it is a 700px circle with one building in it.
    //
    // What survives at this size is the SILHOUETTE: white shells, a warm podium,
    // and the notches between them. That is the whole drawing now.

    // A FAINT SIDE-ON SILHOUETTE, and that is the whole brief.
    //
    // This was drawn four ways before it got here — with blue glazing, mirrored so
    // the glazing faced the harbour, with per-shell tones and shadow edges, and
    // finally in three-quarter with a receding deck. Every one of them was more
    // building than a 4px-tall landmark can hold, and the owner's call each time
    // was that it looked worse, not better. They were right.
    //
    // What it needs to be is the thing behind the fleet: one pale shape, side-on,
    // low contrast, read in a glance and then forgotten. The detail that the icon
    // carries — glass, ribs, a stepped podium — belongs to a 700px circle with one
    // building in it, not to a strip where this object is thirty pixels wide on a
    // phone. Anything added here has to survive at that size, and none of it did.
    // ONE TONE for both landmarks, and it is BLUE rather than near-white (owner
    // 2026-08-16). At 0.74 toward white the shells were a pale ghost that barely
    // separated from the sky; a silhouette is not a faint object, it is a solid
    // shape in a flatter colour, and on this water that colour is the harbour.
    const LANDMARK = mixHex( MID_HARBOUR, '#FFFFFF', 0.34 );
    const PODIUM_H = 4;
    const QUAY_TOP = QUAY - PODIUM_H;
    this.syCity.push( {
      d: `M${ f( ox - 72 ) },${ f( QUAY ) } L${ f( ox - 72 ) },${ f( QUAY_TOP ) }`
        + ` L${ f( ox + 74 ) },${ f( QUAY_TOP ) } L${ f( ox + 74 ) },${ f( QUAY ) } Z`,
      fill: LANDMARK,
    } );

    // The shells: one tone, one path, side-on, stepping down toward the harbour.
    // Separate paths and alternating tones were tried and they only made the mass
    // busier; at this size the notches between the apexes are the whole drawing.
    // WIDER, and in TWO TONES (owner 2026-08-16: "biraz daha geniş olacaklar,
    // gerçeği öyle").
    //
    // They were taller than they were wide — 30 across by 40 up on the biggest —
    // which is a fin, and it is why the row kept reading as shark fins rather than
    // as a roof. The real shells are broader than they are tall; widening them and
    // taking a few units off the height is the single change that turns the
    // silhouette from a set of blades into a building.
    //
    // SPACED, too. The steps used to be about half a shell's width and the apexes
    // landed on top of each other — at strip scale the notches closed up and the
    // fan read as one serrated lump. Stepping by about three quarters of a width
    // leaves each apex standing clear of its neighbour.
    const SHELLS: [ number, number, number ][] = [
      [ ox - 66, 42, 36 ],
      [ ox - 38, 37, 30 ],
      [ ox - 13, 32, 24 ],
      [ ox + 9, 27, 18 ],
      // the restaurant group, apart from the main hall
      [ ox + 44, 20, 12 ],
      [ ox + 58, 16, 8 ],
    ];
    // A TWO-STOP GRADIENT PER SHELL (owner 2026-08-16: "her biri tek tek gradient,
    // 2 tonlu"). Flat fills were tried twice — one tone for the whole fan, then
    // two alternating — and neither is what a curved concrete shell does with the
    // sky: it is bright where it faces up and cooler where it turns away, and that
    // fall is the only modelling this silhouette gets.
    //
    // The stops live on the component and the def is in objectBoundingBox units,
    // so each shell ramps across ITS OWN box. Every shell is shaded individually
    // without six definitions.
    this.syShellTop = mixHex( MID_HARBOUR, '#FFFFFF', 0.62 );
    this.syShellLow = mixHex( MID_HARBOUR, '#FFFFFF', 0.06 );
    for ( const [ bx, w, h ] of SHELLS ) {
      this.syCity.push( { d: shellAt( bx, QUAY_TOP, w, h ), fill: `url(#${ this.uid }-syshell)` } );
    }

    // Its place used to be taken by the theme's own glyph, which is now a boat —
    // see the fleet below.

    // the HARBOUR BRIDGE, the other silhouette nobody has to be told: a shallow
    // arch between two pylons, with the deck hung under it on verticals.
    const bx = GAPS[ 1 ], SPAN = 116, RISE = 34;
    const arch: string[] = [];
    const archY = ( t: number ): number => QUAY - 16 - Math.sin( t * Math.PI ) * RISE;
    for ( let i = 0; i <= 40; i++ ) {
      arch.push( `${ f( bx - SPAN / 2 + ( i / 40 ) * SPAN ) },${ f( archY( i / 40 ) ) }` );
    }
    for ( let i = 40; i >= 0; i-- ) {
      arch.push( `${ f( bx - SPAN / 2 + ( i / 40 ) * SPAN ) },${ f( archY( i / 40 ) + 5 ) }` );
    }
    const bridge: string[] = [ 'M' + arch.join( ' L' ) + ' Z' ];
    // the deck
    bridge.push( `M${ f( bx - SPAN / 2 - 6 ) },${ f( QUAY - 16 ) } L${ f( bx + SPAN / 2 + 6 ) },${ f( QUAY - 16 ) }`
      + ` L${ f( bx + SPAN / 2 + 6 ) },${ f( QUAY - 12 ) } L${ f( bx - SPAN / 2 - 6 ) },${ f( QUAY - 12 ) } Z` );
    // the two pylons
    for ( const sx of [ -1, 1 ] ) {
      const px = bx + sx * ( SPAN / 2 + 10 );
      bridge.push( `M${ f( px - 7 ) },${ f( QUAY ) } L${ f( px - 7 ) },${ f( QUAY - 40 ) }`
        + ` L${ f( px + 7 ) },${ f( QUAY - 40 ) } L${ f( px + 7 ) },${ f( QUAY ) } Z` );
    }
    // A SILHOUETTE, in the same flat harbour tone the Opera House takes (owner
    // 2026-08-16). It used to be drawn in a near-black blue with ten hangers strung
    // under the arch, which made it the darkest and busiest thing in a scene whose
    // subject is a fleet of white boats — the one object on the waterfront that
    // demanded to be looked at.
    //
    // The hangers went with the colour. At this size they were a comb of one-pixel
    // verticals, and a comb is texture: it made the arch look dirty rather than
    // engineered. Arch, deck and two pylons is the whole bridge at thirty pixels.
    this.syCity.push( { d: bridge.join( ' ' ), fill: LANDMARK } );

    // a pair of SAILBOATS in the third gap — the only thing in the scene actually
    // ON the water rather than beside it, which is why they go on their own layer
    this.syOnWater = [];
    // ---- the fleet ----
    //
    // THREE boats, and they are the theme's OWN glyph rather than the hand-drawn
    // triangles this strip carried before. That matters more than it sounds: the
    // strip used to draw its own little sloops while the tab drew a building, so
    // the two assets had nothing to do with each other. Now the boat in the tab bar
    // is the same boat sailing past.
    //
    // One takes the first gap, where the Opera House used to be, and it is the
    // biggest — that gap needs something to hold it. The other two sail together in
    // the third, at different sizes and different distances out.
    const FLEET: [ number, number, number, number ][] = [
      // [ gap index, x offset, y offset below the quay, scale ]
      [ 0, 96, 16, 0.62 ], [ 2, -34, 12, 0.50 ], [ 2, 44, 24, 0.34 ],
    ];
    for ( const [ g, dx, dy, sc ] of FLEET ) {
      // anchored on (57, 94) — the boat's own waterline, a little above the keel —
      // so the hull sits IN the water rather than on top of it
      const place = `translate(${ GAPS[ g ] + dx },${ QUAY + dy })`
        + ` scale(${ sc }) translate(-57,-94)`;
      // the further out, the paler, and the smallest one is sails only: at 34 the
      // hull is four pixels of dark under a white triangle and reads as a shadow
      const hull = mixHex( DEEP_HARBOUR, '#FFFFFF', 0.18 + ( 0.62 - sc ) * 0.5 );
      if ( sc > 0.40 ) {
        this.syOnWater.push( { d: YACHT_HULL, fill: hull, transform: place } );
        this.syOnWater.push( { d: YACHT_MAST, fill: hull, transform: place } );
      }
      this.syOnWater.push( { d: YACHT_MAIN, fill: '#FFFFFF', transform: place } );
      this.syOnWater.push( {
        d: YACHT_JIB, fill: mixHex( '#FFFFFF', MID_HARBOUR, 0.18 ), transform: place,
      } );
    }

    // a LIGHTHOUSE on the headland in the fourth gap
    const lx = GAPS[ 3 ];
    this.syCity.push( {
      d: `M${ lx - 5 },${ QUAY } L${ lx - 3.4 },${ QUAY - 26 } L${ lx + 3.4 },${ QUAY - 26 }`
        + ` L${ lx + 5 },${ QUAY } Z`
        + ` M${ lx - 5.5 },${ QUAY - 26 } L${ lx - 5.5 },${ QUAY - 31 } L${ lx + 5.5 },${ QUAY - 31 }`
        + ` L${ lx + 5.5 },${ QUAY - 26 } Z`
        + ` M${ lx - 3 },${ QUAY - 31 } L${ lx },${ QUAY - 38 } L${ lx + 3 },${ QUAY - 31 } Z`,
      fill: '#FFFFFF',
    } );
    this.syCity.push( {
      d: `M${ lx - 5.5 },${ QUAY - 31 } L${ lx - 5.5 },${ QUAY - 26 } L${ lx + 5.5 },${ QUAY - 26 }`
        + ` L${ lx + 5.5 },${ QUAY - 31 } Z`,
      fill: TERRA,
    } );

    // ---- the quay ----
    // A thin dark band along the waterline. Without it every tower in the scene
    // rises straight out of the harbour, which reads as a flooded city rather
    // than as a shore.
    this.syCity.push( {
      d: `M0,${ QUAY - 4 } L${ this.W },${ QUAY - 4 } L${ this.W },${ QUAY } L0,${ QUAY } Z`,
      fill: mixHex( DEEP_HARBOUR, '#FFFFFF', 0.42 ),
    } );

    // ---- the harbour ----
    this.syWaterD = `M0,${ QUAY } L${ this.W },${ QUAY } L${ this.W },${ this.H } L0,${ this.H } Z`;
    this.syWaterTop = mixHex( HARBOUR, DEEP_HARBOUR, 0.22 );
    this.syWaterLow = lineTabFill( p.key, p.appearance );

    // ---- ripples ----
    // Flat pale ellipses scattered over the water, exactly the device the theme's
    // own icon uses. They get wider and fainter toward the bottom, which is what
    // perspective does to them and what stops the field reading as a texture.
    this.syRipples = [];
    for ( let i = 0; i < 60; i++ ) {
      const x = fr( i * 0.6180339887 + 0.31 ) * this.W;
      const t = fr( i * 0.7548776662 + 0.17 );
      const y = QUAY + 6 + t * 38;
      const rx = 12 + t * 26 + fr( i * 0.4142 ) * 12;
      // Drawn again on the far side when it crosses an edge. A ripple is up to 50
      // units wide on a canvas LINE repeats, so one cut at x = W leaves a hard
      // vertical edge in the middle of open water — which on this scene is the
      // worst place for one, since the water is an otherwise smooth gradient with
      // nothing for an edge to hide behind.
      const o = +( 0.34 - t * 0.18 ).toFixed( 2 );
      const ell = ( cx: number ): string =>
        `M${ f( cx - rx ) },${ f( y ) } a${ f( rx ) },2.2 0 1,0 ${ f( rx * 2 ) },0`
        + ` a${ f( rx ) },2.2 0 1,0 ${ f( -rx * 2 ) },0 Z`;
      this.syRipples.push( { d: ell( x ), o } );
      if ( x + rx > this.W ) { this.syRipples.push( { d: ell( x - this.W ), o } ); }
      if ( x - rx < 0 ) { this.syRipples.push( { d: ell( x + this.W ), o } ); }
    }
  }

  private buildPrague( p: Product ): void {
    const f = ( v: number ) => +v.toFixed( 1 );
    const fr = ( v: number ) => v - Math.floor( v );

    // The theme's own palette (base-themes/cities/prague-theme), by name.
    const MIDNIGHT = '#0D1D2E';
    const DEEP_BLUE = '#123151';
    const CASTLE_BLUE = '#113E6B';
    const SKY_BLUE = '#74ADF8';
    const TERRACOTTA = '#DF865B';
    const AMBER = '#EFBA5A';
    const WHEAT = '#E3CA77';
    const PARCHMENT = '#DCCF9D';

    /** The eaves of the ordinary town. Everything routine lives below it. */
    const EAVE = 88;
    /** Where the bank ends and the river begins. */
    const BANK = 118;
    const GAPS = [ 294, 589, 883, 1178 ];

    this.pgSkyTop = mixHex( MIDNIGHT, '#03080F', 0.5 );
    this.pgSkyLow = mixHex( DEEP_BLUE, CASTLE_BLUE, 0.4 );

    // ---- stars ----
    // Few, small and only in the upper half. A dense starfield behind a tab row
    // reads as dust on the screen.
    this.pgStars = [];
    for ( let i = 0; i < 44; i++ ) {
      // inset from both edges: a star is a 2-unit disc, so wrapping one buys
      // nothing, but a HALF one on the tile seam is a visible nick in the sky
      const x = 4 + fr( i * 0.6180339887 + 0.13 ) * ( this.W - 8 );
      const y = 20 + fr( i * 0.7548776662 + 0.41 ) * 46;
      this.pgStars.push( {
        cx: f( x ), cy: f( y ),
        r: f( 0.8 + fr( i * 0.271 ) * 1.1 ),
        o: +( 0.22 + fr( i * 0.4142 ) * 0.4 ).toFixed( 2 ),
      } );
    }

    // ---- the moon ----
    // In a TAB GAP, which is the only place something this bright is allowed
    // inside the glyph band.
    // The moon moves to the LEFT of the strip (owner 2026-08-15). It sat in the
    // last gap while the lamp held the first, which balanced the strip's two bright
    // marks across it; with the lamp gone that left everything lit bunched at the
    // right end. Not ON a gap, a little right of one: parked over a gap it lines up
    // with whatever tab sits there and reads as part of the tab bar, not as sky.
    this.pgMoonX = GAPS[ 0 ] + 92;
    this.pgMoonY = 42;
    this.pgMoonR = 15;

    // ---- clouds ----
    // Thin, flat and low-contrast: the icon has two or three drifting across its
    // moon and they are what stops the sky reading as a flat field.
    this.pgClouds = [];
    for ( let i = 0; i < 7; i++ ) {
      const cx = 60 + fr( i * 0.618 + 0.27 ) * ( this.W - 120 );
      const cy = 26 + fr( i * 0.4142 + 0.6 ) * 44;
      const rx = 46 + fr( i * 0.7548 ) * 70;
      const ry = 4 + fr( i * 0.271 ) * 3;
      // drawn again on the far side when it crosses an edge, so a cloud running
      // off the right comes back on the left instead of being cut at the seam
      const o = +( 0.10 + fr( i * 0.3819 ) * 0.10 ).toFixed( 2 );
      const ellipse = ( x: number ): string =>
        `M${ f( x - rx ) },${ f( cy ) } a${ f( rx ) },${ f( ry ) } 0 1,0 ${ f( rx * 2 ) },0`
        + ` a${ f( rx ) },${ f( ry ) } 0 1,0 ${ f( -rx * 2 ) },0 Z`;
      this.pgClouds.push( { d: ellipse( cx ), o } );
      if ( cx + rx > this.W ) { this.pgClouds.push( { d: ellipse( cx - this.W ), o } ); }
      if ( cx - rx < 0 ) { this.pgClouds.push( { d: ellipse( cx + this.W ), o } ); }
    }

    // ---- THE CASTLE ----
    //
    // This was a TERRACE of burgher houses and it is now Prague Castle, because
    // the terrace never worked. It went through plain gables, then steeper ones,
    // then dormers and stepped and baroque gables, and the owner's read stayed the
    // same: it does not feel like Prague. They were right, and the reason is one
    // of scale rather than of drawing. A burgher house is made of things — window
    // proportions, a cornice, a door — that are all under two pixels once this
    // 1472-unit strip lands on a ~360px phone. What survives at that size is
    // SILHOUETTE, and a row of little gabled boxes has the silhouette of any town
    // in Europe.
    //
    // The castle survives it. Prague's defining view is the hrad on its ridge over
    // the river, and what makes it legible is exactly what a strip can carry: a
    // long crenellated wall, round towers with steep conical caps standing off it,
    // and one block taller than the rest. Three shapes, all readable at 4px.
    //
    // It is also the honest answer to the cathedral this scene started with and
    // dropped: the owner rejected a cross-topped spire as a religious emblem, and
    // a castle is the same skyline without the emblem.
    const windows: string[] = [];
    // The castle is INSET from both ends (owner 2026-08-15: leave the left and
    // right a little empty). It went 110 -> 55 -> 28: on a strip 1472 wide showing
    // at ~360px a margin only has to be a few pixels to register as one, and 110
    // read as a hole in the scene rather than as breathing room.
    //
    // The inset is also what makes the strip TILE. LINE repeats this image, and
    // with clear river and sky at both ends there is nothing to line up across the
    // join — the two margins simply meet as one wider gap.
    const INSET = 28;
    const STONE = mixHex( PARCHMENT, MIDNIGHT, 0.58 );
    const STONE_LIT = mixHex( PARCHMENT, MIDNIGHT, 0.46 );
    const ROOF = mixHex( TERRACOTTA, MIDNIGHT, 0.52 );
    /** The wall walk, and the battlement standing on it. */
    const WALL_TOP = 97, MERLON_H = 5, MERLON_W = 7, CRENEL_W = 5;
    const X0 = INSET, X1 = this.W - INSET;

    // the curtain wall, one block from end to end of the run
    this.pgLit.push( {
      d: `M${ f( X0 ) },${ f( BANK - 2 ) } L${ f( X0 ) },${ f( WALL_TOP ) }`
        + ` L${ f( X1 ) },${ f( WALL_TOP ) } L${ f( X1 ) },${ f( BANK - 2 ) } Z`,
      fill: STONE,
    } );
    // its battlement: merlons with the crenels between them left open to the sky.
    // Drawn as one path so the whole run is a single silhouette at small size.
    const merlons: string[] = [];
    for ( let mx = X0; mx < X1 - MERLON_W; mx += MERLON_W + CRENEL_W ) {
      merlons.push( `M${ f( mx ) },${ f( WALL_TOP ) } L${ f( mx ) },${ f( WALL_TOP - MERLON_H ) }`
        + ` L${ f( Math.min( mx + MERLON_W, X1 ) ) },${ f( WALL_TOP - MERLON_H ) }`
        + ` L${ f( Math.min( mx + MERLON_W, X1 ) ) },${ f( WALL_TOP ) } Z` );
    }
    this.pgLit.push( { d: merlons.join( ' ' ), fill: STONE } );

    // ---- the towers ----
    // Nine of them, unevenly spaced, and every one a different height: an evenly
    // spaced row of equal towers is a fence, which is the note this scene has now
    // collected three times in three different subjects.
    //
    // The KEEP is the tallest and it is deliberately not in the middle — a
    // symmetrical castle is a chess piece.
    //
    // COLLISION IS CHECKED, and it has to be: the two big towers further down
    // stand at GAPS[1] and GAPS[2], and the wall towers are placed by a formula
    // that knows nothing about them. The first pass had a wall tower growing out
    // of the gate tower's shoulder at both of those spots (owner 2026-08-15: the
    // towers overlapped). Every tower now claims an interval and any that cannot
    // find clear wall is simply dropped — nine is a target, not a quota, and a
    // wall with eight towers on it looks like a wall.
    const taken: [ number, number ][] = [
      [ GAPS[ 2 ] - 21, GAPS[ 2 ] + 21 ],
    ];
    const TOWERS: { x: number, w: number, h: number, cap: boolean, keep?: boolean }[] = [];
    for ( let t = 0; t < 9; t++ ) {
      const span = ( X1 - X0 );
      const x = X0 + span * ( t + 0.5 + ( fr( t * 0.4142 + 0.2 ) - 0.5 ) * 0.55 ) / 9;
      const keep = t === 3;
      const w = keep ? 30 : 17 + fr( t * 0.618 + 0.4 ) * 9;
      // SHORTER (owner 2026-08-15). They ran 22..38 above the wall with a 46-unit
      // keep, and at that height the caps pushed into the glyph band and the row
      // read as a palisade of spikes rather than as a wall with towers on it. A
      // castle's towers only have to clear its battlement to be towers.
      const h = keep ? 34 : 16 + fr( t * 0.7548 + 0.15 ) * 12;
      // the claim includes the CAP, which overhangs the shaft by 3 a side and is
      // the part that actually collided
      const a = x - w / 2 - 8, b = x + w / 2 + 8;
      if ( taken.some( ( iv ) => a < iv[ 1 ] && b > iv[ 0 ] ) ) { continue; }
      taken.push( [ a, b ] );
      TOWERS.push( { x, w, h, cap: keep || fr( t * 0.271 + 0.6 ) > 0.35, keep } );
    }
    for ( let t = 0; t < TOWERS.length; t++ ) {
      const tw = TOWERS[ t ];
      const tx0 = tw.x - tw.w / 2, tx1 = tw.x + tw.w / 2;
      const top = WALL_TOP - tw.h;
      this.pgMasonry.push( {
        d: `M${ f( tx0 ) },${ f( BANK - 2 ) } L${ f( tx0 ) },${ f( top ) }`
          + ` L${ f( tx1 ) },${ f( top ) } L${ f( tx1 ) },${ f( BANK - 2 ) } Z`,
        fill: STONE_LIT,
      } );
      if ( tw.cap ) {
        // a STEEP conical cap, two thirds of the tower's own width in height. A
        // shallow cap is a hat; this roofline is the one thing that says the
        // towers are towers and not chimneys.
        const over = 3;
        this.pgMasonry.push( {
          d: `M${ f( tx0 - over ) },${ f( top ) } L${ f( tw.x ) },${ f( top - tw.w * 0.95 ) }`
            + ` L${ f( tx1 + over ) },${ f( top ) } Z`,
          fill: ROOF,
        } );
      } else {
        // the uncapped ones are crenellated instead, so the row has two kinds of
        // top rather than one repeated
        const ms: string[] = [];
        for ( let mx = tx0; mx < tx1 - 4; mx += 9 ) {
          ms.push( `M${ f( mx ) },${ f( top ) } L${ f( mx ) },${ f( top - 4 ) }`
            + ` L${ f( Math.min( mx + 5, tx1 ) ) },${ f( top - 4 ) }`
            + ` L${ f( Math.min( mx + 5, tx1 ) ) },${ f( top ) } Z` );
        }
        this.pgMasonry.push( { d: ms.join( ' ' ), fill: STONE_LIT } );
      }
      // ARROW SLITS: SYMMETRIC PAIRS about the tower's centre line, and a tower
      // either carries them ALL THE WAY UP or carries none (owner 2026-08-15:
      // "ya tepeye kadar olsun ya da olmasın, arası yok").
      //
      // Both halves of that rule fix the same failure. The throw used to be made
      // per slit, which lit one side of a row and not the other — on a masonry
      // tower that does not read as a dark window, it reads as a lopsided tower.
      // Moving the throw to the row fixed the left-right case and left the
      // vertical one: a tower lit at the bottom and dark at the top reads as
      // half-built. So the throw moved up once more, to the TOWER, and a lit tower
      // is lit from the wall walk to the cap.
      //
      // The rows are counted off the tower's own height rather than fixed at two
      // or three, which is what "to the top" actually requires when the towers
      // vary from 16 to 34 units above the wall.
      if ( fr( t * 0.6180339887 + 0.12 ) >= 0.32 ) {
        const off = Math.max( 4.2, tw.w * 0.22 );
        const rowsN = Math.max( 1, Math.floor( ( tw.h - 10 ) / 11 ) );
        for ( let r = 0; r < rowsN; r++ ) {
          const wy = top + 7 + r * 11;
          for ( const side of [ -1, 1 ] ) {
            const wx = tw.x + side * off - 1.4;
            windows.push( `M${ f( wx ) },${ f( wy ) } L${ f( wx ) },${ f( wy + 6.5 ) }`
              + ` L${ f( wx + 2.8 ) },${ f( wy + 6.5 ) } L${ f( wx + 2.8 ) },${ f( wy ) } Z` );
          }
        }
      }
    }
    // slits along the curtain wall itself, between the towers
    for ( let k = 0; k < 26; k++ ) {
      const wx = X0 + 10 + fr( k * 0.6180339887 + 0.44 ) * ( X1 - X0 - 20 );
      if ( fr( k * 0.4142 + 0.3 ) < 0.35 ) { continue; }
      windows.push( `M${ f( wx ) },${ f( WALL_TOP + 6 ) } L${ f( wx ) },${ f( WALL_TOP + 12 ) }`
        + ` L${ f( wx + 2.6 ) },${ f( WALL_TOP + 12 ) } L${ f( wx + 2.6 ) },${ f( WALL_TOP + 6 ) } Z` );
    }

    // ---- the landmarks, one per tab gap ----
    // Each a different silhouette, which is the rule Tallinn's build settled: at
    // strip scale four towers of the same outline is a fence.
    //
    // They are pushed onto pgMasonry AFTER the houses, so they stand in FRONT of
    // the terrace rather than behind it. In the first pass they went in first and
    // the cathedral — the theme's own hero object — was almost entirely hidden by
    // a row of houses, which is a strange thing to do to the subject.
    //
    // The first is the theme's OWN glyph — the gas street lamp — standing on the
    // terrace where it belongs. It REPLACED a cathedral: the strip used to put the
    // Home tab's gothic spire here with its lancets lit, and the owner read a
    // cross-topped tower as a religious emblem rather than as a city.
    //
    // The swap turns out to be better composition as well as a better subject. A
    // cathedral is a big silhouette that has to be given a gap to itself; a lamp is
    // slim, so the terrace can have a ROW of them along it — which is what the
    // embankment actually looks like at this hour, and what the icon's own row of
    // gold lights already is.
    //
    // NO LAMP ON THE EMBANKMENT (owner 2026-08-15: delete the one near the left).
    //
    // The history ran a full circle. The strip first put the Home tab's gothic
    // spire here and the owner read a cross-topped tower as a religious emblem; it
    // became FIVE lamps along the bank, which came back as "biraz fazla yoğun";
    // those were cut to ONE tall lamp in the first tab gap as a landmark.
    //
    // That last one is gone too, and it failed for the reason the five did: this
    // strip is 1472 units wide and LINE shows the middle slice of it, so a single
    // object parked at GAPS[0] is not a landmark in a composition — it is whatever
    // happens to be at the left edge of the phone. The scene it left behind is the
    // one that was always doing the work: a lit terrace over a dark river with the
    // moon for its one bright mark.
    //
    // The lamp keeps every other slot it had — the Home tab, the four passcode
    // dots and the chat wallpaper all still draw it.

    // ---- the two big towers ----
    // These were a baroque DOME on a drum and a gothic bridge tower, drawn when
    // this scene was a river town. Neither belongs on a medieval castle — a dome
    // is three hundred years too late for the wall it now stands on — so both are
    // rebuilt as castle work: one broad round GATE TOWER and one tall slim
    // WATCHTOWER, which are the two towers a hrad actually has.
    //
    // They keep their old jobs: two silhouettes distinct from the nine on the wall
    // and from each other, standing in tab gaps where there is room for them.

    // NO GATE TOWER (owner 2026-08-15: delete the biggest one in the middle).
    //
    // It was the broadest thing on the wall and it sat near the centre, which made
    // it the one object the eye landed on — and a gatehouse is not what this scene
    // is about. What is left is a wall with towers along it and ONE tall
    // watchtower, so the silhouette has a single high point instead of competing
    // for two.
    //
    // Its reservation in `taken` went with it, so the wall towers are free to use
    // that stretch again rather than leaving a hole where it stood.

    // the WATCHTOWER: the tallest thing on the wall and the slimmest, with the
    // steepest cap. It is what the gate tower is not, which is the whole reason
    // there are two of them.
    const tx = GAPS[ 2 ];
    this.pgMasonry.push( {
      d: `M${ tx - 11 },${ BANK - 2 } L${ tx - 11 },${ EAVE - 30 } L${ tx + 11 },${ EAVE - 30 }`
        + ` L${ tx + 11 },${ BANK - 2 } Z`,
      fill: mixHex( PARCHMENT, MIDNIGHT, 0.5 ),
    } );
    this.pgMasonry.push( {
      d: `M${ tx - 15 },${ EAVE - 30 } L${ tx },${ EAVE - 66 } L${ tx + 15 },${ EAVE - 30 } Z`,
      fill: ROOF,
    } );
    // its lit clock face, the one gold thing high above the terrace band
    // the watchtower's own slits, on the same rule as the wall towers': symmetric
    // pairs, all the way up. It carried ONE wide square window left over from the
    // gothic bridge tower this replaced, which was the only asymmetric opening in
    // the scene.
    for ( let r = 0; r < 3; r++ ) {
      const wy = EAVE - 24 + r * 11;
      for ( const side of [ -1, 1 ] ) {
        const wx = tx + side * 4.6 - 1.4;
        windows.push( `M${ f( wx ) },${ f( wy ) } L${ f( wx ) },${ f( wy + 6.5 ) }`
          + ` L${ f( wx + 2.8 ) },${ f( wy + 6.5 ) } L${ f( wx + 2.8 ) },${ f( wy ) } Z` );
      }
    }

    this.pgWindowD = windows.join( ' ' );
    this.pgWindowFill = AMBER;

    // ---- the tree line ----
    // A dark scalloped band along the bank. It is the icon's own device for
    // separating the town from the water, and it does the same job here: without
    // it the buildings stand directly in the river.
    const tree: string[] = [];
    for ( let i = 0; i < 92; i++ ) {
      const cx = i * ( this.W / 92 ) + ( fr( i * 0.618 ) - 0.5 ) * 8;
      // LOW. At a radius of 7 to 13 these humps stood 15 units up the facades and
      // ate the ground floor of every house on the terrace. A tree line's job here
      // is to stop the buildings standing directly in the river, and eight units
      // does that.
      const r = 5 + fr( i * 0.4142 + 0.3 ) * 4;
      const hump = ( x: number ): string =>
        `M${ f( x - r ) },${ f( BANK + 2 ) } a${ f( r ) },${ f( r * 1.3 ) } 0 0,1 ${ f( r * 2 ) },0 Z`;
      tree.push( hump( cx ) );
      // the wrap copy, so a hump crossing the canvas edge comes back on the far
      // side instead of being a half-bush at the tile seam
      if ( cx + r > this.W ) { tree.push( hump( cx - this.W ) ); }
      if ( cx - r < 0 ) { tree.push( hump( cx + this.W ) ); }
    }
    tree.push( `M0,${ BANK - 2 } L${ this.W },${ BANK - 2 } L${ this.W },${ BANK + 3 } L0,${ BANK + 3 } Z` );
    this.pgTreeD = tree.join( ' ' );
    this.pgTreeFill = mixHex( MIDNIGHT, '#000000', 0.35 );

    // ---- the river ----
    this.pgRiverD = `M0,${ BANK } L${ this.W },${ BANK } L${ this.W },${ this.H } L0,${ this.H } Z`;
    this.pgRiverTop = mixHex( MIDNIGHT, CASTLE_BLUE, 0.3 );
    this.pgRiverLow = lineTabFill( p.key, p.appearance );

    // ---- lamplight on the water ----
    // FLAT horizontal streaks, never vertical columns: still water breaks a
    // reflection into lines lying across it, and a vertical smear is what a
    // moving river does. The icon draws them flat and so does this.
    this.pgGlints = [];
    for ( let i = 0; i < 54; i++ ) {
      const x = fr( i * 0.6180339887 + 0.37 ) * this.W;
      const y = BANK + 5 + fr( i * 0.7548776662 + 0.2 ) * 24;
      const w = 16 + fr( i * 0.4142 + 0.6 ) * 54;
      const o = +( 0.30 - ( y - BANK ) / 24 * 0.2 ).toFixed( 2 );
      const streak = ( sx: number ): string =>
        `M${ f( sx ) },${ f( y ) } L${ f( sx + w ) },${ f( y ) }`
        + ` L${ f( sx + w ) },${ f( y + 1.8 ) } L${ f( sx ) },${ f( y + 1.8 ) } Z`;
      this.pgGlints.push( { d: streak( x ), o } );
      // a streak runs RIGHT from its origin, so only the right edge can cut one
      if ( x + w > this.W ) { this.pgGlints.push( { d: streak( x - this.W ), o } ); }
    }
  }

  private buildSalda( p: Product ): void {
    const f = ( v: number ) => +v.toFixed( 1 );
    const fr = ( v: number ) => v - Math.floor( v );

    // The theme's own palette (base-themes/nature/salda-theme), by name.
    const SAND_WHITE = '#FDFCFA';
    const SAND = '#FDF6E7';
    const OATMEAL = '#ECE1C9';
    const STONE = '#A59379';
    const DRIFTWOOD = '#8F6055';
    const SHALLOW = '#B8E4EC';
    const LAKE = '#1397AE';
    const SLATE_BLUE = '#44849F';
    const DEEP_TEAL = '#277878';

    /**
     * Where the sky ends. LOW, at y 48 — the first pass put it at 92 to keep the
     * whole lake out of the glyph band, and what came back was a strip that was
     * sixty percent empty sky with the water squeezed into a 32-unit ribbon along
     * the bottom. A theme about a lake cannot be mostly sky.
     *
     * The water goes up into the glyph band instead, and it is the WATER that is
     * held pale there rather than the composition that is held out of it. That is
     * Tallinn's answer to the same problem — its roofs are inside the band and the
     * tab colour was measured against a pantile — and it is the better one.
     */
    const SKY_END = 44;
    /**
     * Below this the water is allowed to be the colour it actually is.
     *
     * NINETY-SIX, three units under the glyph band's floor. The first pass put it
     * at 92 and the band immediately above it — a mid turquoise — measured 2.28:1
     * against the active glyph. The sky had been measured and the water had not.
     *
     * The correction to THAT correction matters just as much. Simply pushing every
     * coloured band below 96 left the top two thirds of the strip an empty pale
     * wash, which is worse than the fault it fixed: a theme about a turquoise lake
     * cannot be mostly nothing. What fills that band instead is the thing that
     * belongs in it — see the shoals below.
     */
    const DEEP_FROM = 96;
    /** Where the water gives way to the beach. */
    const SHORE = 130;
    const GAPS = [ 294, 589, 883, 1178 ];

    this.saSkyTop = mixHex( SAND_WHITE, '#FFFFFF', 0.6 );
    this.saSkyLow = mixHex( SHALLOW, '#FFFFFF', 0.62 );

    /** A band whose top edge waves at integer frequency (so the strip tiles) and
     *  whose bottom runs flat to `to`. */
    const band = ( base: number, waves: [ number, number, number ][], to: number ): string => {
      const pts: string[] = [];
      for ( let i = 0; i <= 96; i++ ) {
        const t = i / 96, x = t * this.W;
        let y = base;
        for ( const [ k, amp, ph ] of waves ) { y += Math.sin( t * Math.PI * 2 * k + ph ) * amp; }
        pts.push( `${ f( x ) },${ f( y ) }` );
      }
      return `M0,${ f( to ) } L` + pts.join( ' L' ) + ` L${ this.W },${ f( to ) } Z`;
    };

    // ---- the far range ----
    // Low, pale and BLUE-GREY rather than green: it is the far side of a lake seen
    // through twenty kilometres of air, and a hill painted in its own local colour
    // reads as a hill two metres away.
    this.saHills = [
      { d: band( SKY_END - 14, [ [ 1, 6, 0.4 ], [ 3, 3, 2.1 ] ], SKY_END + 3 ),
        fill: mixHex( SLATE_BLUE, '#FFFFFF', 0.76 ) },
      { d: band( SKY_END - 7, [ [ 2, 5, 1.7 ], [ 5, 2, 0.3 ] ], SKY_END + 3 ),
        fill: mixHex( SLATE_BLUE, '#FFFFFF', 0.62 ) },
    ];

    // ---- the water ----
    // The two bands INSIDE the glyph band are held very pale — measured, not
    // judged: the active sand reads 3.9 and 3.5 on them. Everything with any
    // strength lives under y 96. Each band's top edge waves at its own frequency,
    // so no two boundaries run parallel; parallel bands read as a flag.
    this.saWater = [
      { d: band( SKY_END, [ [ 1, 2, 0.9 ] ], SHORE + 8 ),
        fill: mixHex( SHALLOW, '#FFFFFF', 0.66 ) },
      { d: band( SKY_END + 22, [ [ 2, 4, 2.6 ], [ 4, 1.5, 0.7 ] ], SHORE + 8 ),
        fill: mixHex( SHALLOW, '#FFFFFF', 0.38 ) },
      { d: band( DEEP_FROM, [ [ 1, 5, 4.2 ], [ 3, 2, 1.2 ] ], SHORE + 8 ),
        fill: mixHex( SHALLOW, LAKE, 0.42 ) },
      { d: band( DEEP_FROM + 12, [ [ 2, 4, 0.2 ], [ 5, 2, 3.3 ] ], SHORE + 8 ),
        fill: LAKE },
      { d: band( DEEP_FROM + 24, [ [ 1, 6, 2.2 ], [ 3, 2.5, 5.1 ] ], SHORE + 8 ),
        fill: mixHex( LAKE, DEEP_TEAL, 0.26 ) },
    ];

    /** A shoal: a closed wobbly outline, NOT an ellipse.
     *
     *  The wobble is applied to X and Y SEPARATELY, and to Y in ABSOLUTE units —
     *  these are eight times wider than they are tall, so a proportional wobble on
     *  the short axis is a single unit and changes nothing. The outline is then
     *  smoothed through the points rather than joined by straight lines. */
    const shoal = ( cx: number, cy: number, rx: number, ry: number, seed: number ): string => {
      const N = 22;
      const pts: [ number, number ][] = [];
      for ( let i = 0; i < N; i++ ) {
        const a2 = ( i / N ) * Math.PI * 2;
        const wx = 1 + Math.sin( a2 * 2 + seed * 1.7 ) * 0.20 + Math.sin( a2 * 3 + seed * 2.9 ) * 0.12;
        const wy = Math.sin( a2 * 3 + seed * 1.3 ) * 2.2 + Math.sin( a2 * 5 + seed * 0.7 ) * 1.3;
        pts.push( [ cx + Math.cos( a2 ) * rx * wx, cy + Math.sin( a2 ) * ry + wy ] );
      }
      let d = `M${ f( pts[ 0 ][ 0 ] ) },${ f( pts[ 0 ][ 1 ] ) }`;
      for ( let i = 0; i < N; i++ ) {
        const p0 = pts[ ( i - 1 + N ) % N ], p1 = pts[ i ];
        const p2 = pts[ ( i + 1 ) % N ], p3 = pts[ ( i + 2 ) % N ];
        d += ` C${ f( p1[ 0 ] + ( p2[ 0 ] - p0[ 0 ] ) / 6 ) },${ f( p1[ 1 ] + ( p2[ 1 ] - p0[ 1 ] ) / 6 ) }`
          + ` ${ f( p2[ 0 ] - ( p3[ 0 ] - p1[ 0 ] ) / 6 ) },${ f( p2[ 1 ] - ( p3[ 1 ] - p1[ 1 ] ) / 6 ) }`
          + ` ${ f( p2[ 0 ] ) },${ f( p2[ 1 ] ) }`;
      }
      return `${ d } Z`;
    };

    // ---- the shoals ----
    // Salda's BEYAZ ADALAR, and this is where the composition is actually solved.
    //
    // The glyph band has to stay pale, and a pale band with nothing in it is an
    // empty strip. The white shoals ARE pale — they are banks of white sand lying
    // in shallow water, so a pale mark on a pale ground is not a compromise here,
    // it is the subject. They fill y 56..92 across the whole width, in clusters
    // that are biggest in the TAB GAPS, and they are what this theme is known for.
    //
    // The two under-96 clusters keep their old place in the coloured water, so the
    // shoals read as running from the shore out into the lake rather than as a
    // decorative row at one depth.
    this.saIslands = [];
    for ( let g = 0; g < GAPS.length; g++ ) {
      // the pale ones, up in the glyph band
      for ( let i = 0; i < 3; i++ ) {
        const j = g * 7 + i;
        this.saIslands.push( {
          d: shoal(
            GAPS[ g ] + ( i - 1 ) * 62 + ( fr( j * 0.618 ) - 0.5 ) * 30,
            58 + fr( j * 0.4142 + 0.3 ) * 30,
            26 + fr( j * 0.7548 + 0.1 ) * 30,
            4 + fr( j * 0.271 + 0.5 ) * 3,
            j ),
          fill: mixHex( '#FFF6E8', SHALLOW, 0.18 + fr( j * 0.3819 ) * 0.14 ),
        } );
      }
      // and the ones out in the lake, where they show as bright banks
      const n = 2 + ( g % 2 );
      for ( let i = 0; i < n; i++ ) {
        const j = g * 5 + i + 40;
        this.saIslands.push( {
          d: shoal(
            GAPS[ g ] + ( i - ( n - 1 ) / 2 ) * 58 + ( fr( j * 0.618 ) - 0.5 ) * 22,
            // LOWER, so the shallows band at DEEP_FROM+24 actually washes over
            // their bottom half. At +4..+24 they sat entirely ABOVE that band and
            // the overlay did nothing — which is why they came back as hard white
            // cut-outs floating on the lake rather than as sand under water.
            DEEP_FROM + 16 + fr( j * 0.4142 + 0.3 ) * 16,
            24 + fr( j * 0.7548 + 0.1 ) * 24,
            5 + fr( j * 0.271 + 0.5 ) * 4,
            j ),
          // CREAM rather than white. Salda's shoals are magnesium sand, which is
          // pale and warm; pure white against turquoise is spilled milk.
          fill: mixHex( '#FFF3E2', SHALLOW, 0.10 + fr( j * 0.3819 ) * 0.16 ),
        } );
      }
    }

    // ---- foam ----
    // Thin white edges along two of the boundaries, and the shore. They are what
    // makes the bands read as WATER rather than as stripes of colour.
    const foamLine = ( base: number, waves: [ number, number, number ][], th: number ): string => {
      const top: string[] = [], bot: string[] = [];
      for ( let i = 0; i <= 96; i++ ) {
        const t = i / 96, x = t * this.W;
        let y = base;
        for ( const [ k, amp, ph ] of waves ) { y += Math.sin( t * Math.PI * 2 * k + ph ) * amp; }
        const w = th * ( 0.45 + 0.55 * Math.abs( Math.sin( t * Math.PI * 3 + 1.1 ) ) );
        top.push( `${ f( x ) },${ f( y - w / 2 ) }` );
        bot.push( `${ f( x ) },${ f( y + w / 2 ) }` );
      }
      return 'M' + top.join( ' L' ) + ' L' + bot.reverse().join( ' L' ) + ' Z';
    };
    this.saFoam = [
      { d: foamLine( DEEP_FROM, [ [ 1, 5, 4.2 ], [ 3, 2, 1.2 ] ], 2.4 ), o: 0.45 },
      { d: foamLine( DEEP_FROM + 24, [ [ 1, 6, 2.2 ], [ 3, 2.5, 5.1 ] ], 3.2 ), o: 0.6 },
      { d: foamLine( SHORE, [ [ 1, 4, 1.4 ], [ 3, 2, 2.8 ] ], 4.6 ), o: 0.9 },
    ];

    // the shallows, laid OVER the lake shoals at partial opacity — a shoal drawn
    // on top of finished water is a sticker; one with the shallows washing over
    // its lower edge is sand under water
    this.saWaterFront = [
      { d: band( DEEP_FROM + 24, [ [ 1, 6, 2.2 ], [ 3, 2.5, 5.1 ] ], SHORE + 8 ),
        fill: mixHex( SHALLOW, LAKE, 0.22 ), o: 0.78 },
    ];

    // ---- the beach ----
    this.saSandD = band( SHORE, [ [ 1, 4, 1.4 ], [ 3, 2, 2.8 ] ], this.H );
    // WARM cream at the waterline, not near-white. The bottom stop reads from
    // lineTabFill and the owner's screenshots turned that into a SALMON input bar,
    // so a near-white top left the beach jumping from snow to coral over about
    // twenty units — a band, not a shore. Starting warm makes the same ramp read
    // as wet sand drying out toward the bar.
    this.saSandTop = '#FBEEDF';
    this.saSandLow = lineTabFill( p.key, p.appearance );

    // ---- the shell at the waterline ----
    // The theme's OWN glyph, big, lying half in the shallows in the second tab
    // gap. Every other scene in this run puts its hero object into its own strip —
    // Prague's cathedral, Sydney's opera house, Vienna's violin, Etna's cone,
    // Fuji's pagoda — and Salda was the one that did not. Its strip was a
    // landscape with the subject missing from it.
    //
    // The WATERLINE is where it goes rather than up the dry beach, because that is
    // where shells actually are and because the beach is only twenty units tall.
    // Half in the water also lets the foam run over its lower edge, which is the
    // same trick that makes the shoals read as sand rather than as stickers.
    this.saShore = [];
    const SHELL_X = GAPS[ 1 ];
    for ( const [ dx, sc, rot, fill ] of [
      [ 0, 0.62, -8, '#FBE7CE' ],
      [ -46, 0.34, 16, '#F3CDC2' ],
      [ 40, 0.28, -22, '#F7DFC4' ],
    ] as [ number, number, number, string ][] ) {
      const shellAt = `translate(${ SHELL_X + dx },${ SHORE + 6 }) rotate(${ rot })`
        + ` scale(${ sc }) translate(-60,-100)`;
      this.saShore.push( {
        d: SHELL,
        fill,
        // anchored on the shell's own HINGE (60, 100), so it rests on the sand
        // rather than floating at whatever height its bounding box implies
        transform: shellAt,
      } );
      // its grooves. The beach shells had none — they were flat silhouettes on a
      // flat beach, which is exactly the "fan rather than shell" the owner caught
      // on the passcode.
      this.saShore.push( {
        d: SHELL_RIBS,
        fill: mixHex( fill, '#9A6E3E', 0.42 ),
        transform: shellAt,
      } );
      // A shadow, so a cream shell on cream sand still has an edge — but a TIGHT,
      // PALE one. The first attempt drew it at 55% of the shore's stone and a
      // tenth wider than the shell, and it came back as a grey oval sitting apart
      // from the shell like a separate pebble. A shell resting on dry sand casts
      // almost nothing; what it has is a hairline of shade where it meets the
      // ground.
      this.saShore.push( {
        d: `M-30,0 a30,4 0 1,0 60,0 a30,4 0 1,0 -60,0 Z`,
        fill: mixHex( OATMEAL, STONE, 0.26 ),
        transform: `translate(${ SHELL_X + dx },${ SHORE + 3 }) scale(${ sc * 0.86 },${ sc })`,
      } );
    }

    // ---- what is lying on the beach ----
    // A few shells — the theme's OWN glyph — and flat pebbles between them. The
    // shells are the reason this reads as Salda's beach rather than as any beach:
    // the object in the tab bar is lying in the sand under it.
    for ( let i = 0; i < 26; i++ ) {
      const x = 24 + fr( i * 0.6180339887 + 0.21 ) * ( this.W - 48 );
      const y = SHORE + 9 + fr( i * 0.7548776662 + 0.44 ) * 13;
      if ( i % 4 === 0 ) {
        // A shell, placed by transform because it is the shared glyph in its own
        // 120 box. The box's own centre of mass is near (60, 64) — NOT (60, 60)
        // and certainly not the hinge at (60, 100), which is where the first pass
        // anchored it and lifted every shell 13 units clear of the sand.
        // BIGGER than the first two passes' 0.13 and 0.20. A shell at 0.20 of its
        // box is 19 units across on a 1472-wide strip, which is a pebble with a
        // texture nobody can see; the point of putting the theme's own glyph in
        // the sand is that it is recognisable as that glyph.
        const sc = +( 0.34 + fr( i * 0.271 ) * 0.14 ).toFixed( 3 );
        // the same soft shadow the big one gets: a cream shell on cream sand has
        // no edge of its own, and this is the cheapest one to give it
        this.saShore.push( {
          d: `M-30,0 a30,4 0 1,0 60,0 a30,4 0 1,0 -60,0 Z`,
          fill: mixHex( OATMEAL, STONE, 0.22 ),
          transform: `translate(${ f( x ) },${ f( y + 1 ) }) scale(${ sc * 0.82 },${ sc })`,
        } );
        const litterAt = `translate(${ f( x ) },${ f( y ) })`
          + ` rotate(${ Math.round( ( fr( i * 0.3819 ) - 0.5 ) * 46 ) })`
          + ` scale(${ sc }) translate(-60,-64)`;
        this.saShore.push( {
          d: SHELL_RIBS,
          fill: mixHex( '#E8C296', '#9A6E3E', 0.34 ),
          transform: litterAt,
        } );
        this.saShore.push( {
          // The FULL shell, ears and all nine flutes — not the simple one. At
          // this scale the strip renders it at about 22px on a phone, which is
          // exactly the size the nine-lobe version was drawn for; the five-lobe
          // simplification is for the wallpaper's smallest grains, and used here
          // it came back as a small brown cloud.
          d: SHELL,
          // TRUE SHELL COLOURS. The first pass mixed these toward the theme's
          // driftwood brown and put the darkest thing on the whole theme onto its
          // white shore, where it read as dirt. Cream, warm beige and a pearly
          // pink now, with the sand's own stone only as the deepest of them —
          // exactly the four the passcode lights up in.
          fill: [ '#FFF3E2', '#F7DFC4', '#F3CDC2', '#E8C296' ][ i % 4 ],
          transform: `translate(${ f( x ) },${ f( y ) })`
            + ` rotate(${ Math.round( ( fr( i * 0.3819 ) - 0.5 ) * 46 ) })`
            + ` scale(${ sc }) translate(-60,-64)`,
        } );
      } else {
        const rx = 2.4 + fr( i * 0.4142 + 0.7 ) * 3.2;
        this.saShore.push( {
          d: `M${ f( x - rx ) },${ f( y ) } a${ f( rx ) },${ f( rx * 0.62 ) } 0 1,0 ${ f( rx * 2 ) },0`
            + ` a${ f( rx ) },${ f( rx * 0.62 ) } 0 1,0 ${ f( -rx * 2 ) },0 Z`,
          // the pebbles keep the shore's own stone, so the shells are the pale
          // things on the beach and the pebbles the ones with any weight
          fill: mixHex( OATMEAL, STONE, 0.30 + fr( i * 0.5497 ) * 0.35 ),
        } );
      }
    }
  }

  private buildOcean( p: Product ): void {
    const f = ( v: number ) => +v.toFixed( 1 );
    const fr = ( v: number ) => v - Math.floor( v );

    // The theme's own palette (base-themes/nature/ocean-theme), by name.
    const ABYSS = '#102B2E';
    const DEEP = '#18383C';
    const SHELF = '#1C4247';
    const KELP_GREEN = '#5AB590';
    const LAGOON = '#3FB4D8';
    const CORAL_ROSE = '#DC7783';
    const CORAL_RED = '#EE8EBF';
    const CORAL_TAN = '#EAC089';
    const BIOLUM = '#5AC8B2';

    /** Where the floor's silt begins, and the deepest the garden can grow. */
    const FLOOR = 118;
    /** The four tab GAPS — the only places anything is allowed to stand tall. */
    const GAPS = [ 294, 589, 883, 1178 ];

    // ---- the water ----
    // SHALLOWS, rebuilt from the owner's screenshots. This was an abyss — three
    // stops walking from #04171B down to the shelf — chosen when the skin was
    // believed to be deep water. The skin is a near-white body over a bright teal
    // bar, and a near-black band sitting between the two would have read as a
    // hole punched in the phone.
    //
    // So the light is at the TOP now, the ordinary way round, and the strip opens
    // just under the body colour so there is no edge where the scene begins. The
    // garden below did not have to change with it: dark plants against lit water
    // is what a sunlit seabed actually looks like, and those blades were already
    // mixed toward the abyss.
    this.ocSkyTop = '#DFF6F3';
    this.ocSkyMid = '#B8E9E2';
    this.ocSkyLow = '#8ADCD2';

    // ---- the bioluminescent currents ----
    // The icon's signature, and the one element that has to be held back. Each is
    // a broad band whose thickness AND opacity taper to nothing at both ends, so
    // the seam is invisible when LINE repeats the strip — the same shape the
    // aurora ribbons use. They cross the glyph band, so none of them goes past
    // 0.22 opacity: a bright ribbon behind an active tab would undo the whole
    // reason this scene has empty water up there.
    // On the abyss these were bioluminescence and they were drawn in the theme's
    // own lit mint. On shallows a mint band over pale water is nothing, so they
    // are DEEPER than the water instead of brighter — bands of colder, denser
    // current seen from the side. Same three sweeps, same restraint: they cross
    // the glyph band, and the glyphs are dark marks now, so none goes past 0.22.
    this.ocCurrents = [];
    const CURRENTS: { k: number, base: number, amp: number, th: number, ph: number, o: number, c: string }[] = [
      { k: 1, base: 44, amp: 15, th: 13, ph: 0.2, o: 0.10, c: LAGOON },
      { k: 1, base: 74, amp: 19, th: 18, ph: 2.4, o: 0.16, c: mixHex( KELP_GREEN, SHELF, 0.5 ) },
      { k: 2, base: 99, amp: 11, th: 13, ph: 4.1, o: 0.22, c: mixHex( SHELF, LAGOON, 0.35 ) },
    ];
    for ( const cur of CURRENTS ) {
      const N = 48;
      const top: string[] = [];
      const bot: string[] = [];
      for ( let i = 0; i <= N; i++ ) {
        const t = i / N;
        const x = t * this.W;
        const y = cur.base + Math.sin( t * Math.PI * 2 * cur.k + cur.ph ) * cur.amp;
        // thickness tapers to zero at BOTH ends, so the band is a discrete sweep
        // rather than a stripe running off the edge into its own repeat
        const th = cur.th * Math.sin( Math.PI * t );
        top.push( `${ f( x ) },${ f( y - th / 2 ) }` );
        bot.push( `${ f( x ) },${ f( y + th / 2 ) }` );
      }
      this.ocCurrents.push( {
        d: 'M' + top.join( ' L' ) + ' L' + bot.reverse().join( ' L' ) + ' Z',
        fill: cur.c, o: cur.o,
      } );
    }

    // ---- the floor ----
    // A shallow integer-frequency roll, and it ramps DOWN into the tab bar's own
    // fill so the strip has no edge under it at all. That is the trap this family
    // keeps walking into ([[line-tab-fill-single-source]]) and the reason the last
    // stop is read from lineTabFill rather than typed here.
    //
    // Its TOP stop is the part worth stating, and the failure it guards against
    // survived the skin flip. The first pass took it from the theme's own shelf
    // and deep tokens, which put it within a shade of the water right above it —
    // so the floor did not exist, and every blade of grass read as hanging in
    // mid-water with its base cut off. On shallows the same rule points the other
    // way: the shelf has to be DEEPER than the water above it, and it lands a
    // step off the tab fill it ramps into.
    const floorPts: string[] = [];
    for ( let i = 0; i <= 60; i++ ) {
      const t = i / 60, x = t * this.W;
      const y = FLOOR + Math.sin( t * Math.PI * 2 + 0.7 ) * 4 + Math.sin( t * Math.PI * 4 + 2.1 ) * 2;
      floorPts.push( `${ f( x ) },${ f( y ) }` );
    }
    this.ocFloorD = `M0,${ this.H } L` + floorPts.join( ' L' ) + ` L${ this.W },${ this.H } Z`;
    this.ocFloorTop = '#57C3B2';
    this.ocFloorLow = lineTabFill( p.key, p.appearance );

    // Pebbles along the shelf: a dozen low humps sitting ON the floor line, in a
    // tone a step off it. They cost almost nothing and they are what makes the
    // floor a SURFACE rather than a colour change — an edge with nothing resting
    // on it reads as a gradient stop, which is exactly what the first pass got.
    const pebbles: string[] = [];
    for ( let i = 0; i < 26; i++ ) {
      const t = fr( i * 0.6180339887 + 0.37 );
      const x = 20 + t * ( this.W - 40 );
      const y = FLOOR + Math.sin( t * Math.PI * 2 + 0.7 ) * 4
        + Math.sin( t * Math.PI * 4 + 2.1 ) * 2 + 2;
      // FLAT and wide. Rounder humps in the first pass read as one more clump of
      // grass in the same green — the eye had no reason to call them stone.
      const rx = 7 + fr( i * 0.4142 + 0.2 ) * 13;
      const ry = rx * ( 0.20 + fr( i * 0.271 ) * 0.10 );
      pebbles.push( `M${ f( x - rx ) },${ f( y ) } a${ f( rx ) },${ f( ry ) } 0 0,1 ${ f( rx * 2 ) },0 Z` );
    }
    this.ocPebblesD = pebbles.join( ' ' );
    // Off the floor's own hue, not deeper in it: stone on a green shelf is
    // GREYER than the shelf, and matching the grass's green is what made these
    // read as vegetation in the pass before this one.
    this.ocPebbleFill = mixHex( SHELF, '#9FB2B0', 0.30 );

    // ---- seagrass ----
    // Two ranks: a hazy back rank across the whole strip that never reaches the
    // glyph band, and a lit front rank clustered in the tab gaps. Drawn as
    // tapered ribbons rather than strokes — a stroked blade is the first thing to
    // vanish when LINE scales the asset down.
    //
    // The WIDTHS are the fix that mattered. The first pass gave every blade the
    // same 5.5 or 7 units, and a field of one-width blades is a lawn, not a
    // meadow of seagrass: what a seabed actually shows is broad kelp straps with
    // fine eelgrass between them. The spread here runs four to one, and the
    // broadest blades also get the deepest bend, because a wide strap is heavier.
    this.ocGrass = [];
    const blade = ( x: number, h: number, lean: number, w: number ): string => {
      const N = 14;
      const l: string[] = [], r: string[] = [];
      for ( let i = 0; i <= N; i++ ) {
        const t = i / N;
        const y = FLOOR + 6 - h * t;
        // a blade bends more the further it is from its root, which is what makes
        // it read as growing rather than as a leaning stick
        const cx = x + lean * t * t;
        // The taper holds its width for the first third and then goes, and the
        // last tenth rounds off — a blade that tapers evenly from the root is a
        // triangle, and one cut square at the tip is a ribbon of tape.
        const keep = t < 0.34 ? 1 : 1 - ( ( t - 0.34 ) / 0.66 ) * 0.9;
        const round = t > 0.9 ? Math.sqrt( Math.max( 0, 1 - Math.pow( ( t - 0.9 ) / 0.1, 2 ) ) ) : 1;
        const half = w * keep * round / 2;
        l.push( `${ f( cx - half ) },${ f( y ) }` );
        r.push( `${ f( cx + half ) },${ f( y ) }` );
      }
      return 'M' + l.join( ' L' ) + ' L' + r.reverse().join( ' L' ) + ' Z';
    };
    // back rank: 52 blades, everywhere, short and hazy
    // THIRTY-EIGHT, not the fifty-two the pass before had. At that density the
    // back rank closed into a continuous fringe along the whole strip and the
    // clumps in the tab gaps stopped being anything in particular — the water
    // between the gaps has to stay open for the garden to read as a garden.
    // TUFTS, not blades. Two things were wrong with the scattered rank: every
    // blade was 3 units wide, which on a 1472-unit strip landing on a ~360px
    // phone is three quarters of a pixel, and each one stood ALONE in open water.
    // A lone thin upright is a thorn however it is coloured — grass only reads as
    // grass in clumps. Nine tufts of three, broad, fanning off one root patch.
    for ( let i = 0; i < 27; i++ ) {
      const tuft = Math.floor( i / 3 ), within = ( i % 3 ) - 1;
      const x = 60 + fr( tuft * 0.6180339887 + 0.13 ) * ( this.W - 120 ) + within * 11;
      const h = 18 + fr( i * 0.7548776662 + 0.31 ) * 24;
      const w = 7 + fr( i * 0.271 + 0.44 ) * 8;
      // Washed toward the WATER, not toward the abyss. Distance reads as haze,
      // and haze on lit shallows is paler — the blades were mixed 0.66 into
      // #102B2E for the deep-water scene, and on this water that came out as a
      // row of black thorns along the whole strip.
      this.ocGrass.push( {
        d: blade( x, h, ( fr( i * 0.3819660112 + 0.7 ) - 0.35 ) * ( 12 + w * 2 ), w ),
        fill: mixHex( KELP_GREEN, '#8ADCD2', 0.50 ), o: 0.9,
      } );
    }
    // front rank: clumps in the tab gaps, tall enough to be seen and lit. A clump
    // is not a row — the blades share a root patch and fan out of it, so their
    // bases are bunched and their tips are not.
    for ( let g = 0; g < GAPS.length; g++ ) {
      for ( let i = 0; i < 9; i++ ) {
        const j = g * 9 + i;
        const spread = ( i - 4 ) / 4;                      // -1 .. 1 across the clump
        const x = GAPS[ g ] + spread * 26 + ( fr( j * 0.618 + 0.2 ) - 0.5 ) * 10;
        const h = 30 + fr( j * 0.4142 + 0.6 ) * 34;
        const w = 7 + fr( j * 0.7548 + 0.05 ) * 11;
        this.ocGrass.push( {
          // blades lean AWAY from the middle of their own clump, which is what a
          // fan does and what a row of parallel blades never looks like
          // The front rank keeps its depth, but a third of what it had: mixed a
          // third of the way into the shelf rather than half into #18383C, so a
          // near blade is a deep sea green and still plainly a plant.
          d: blade( x, h, spread * ( 16 + w * 1.6 ) + ( fr( j * 0.3819 ) - 0.5 ) * 8, w ),
          fill: mixHex( KELP_GREEN, SHELF, 0.18 + fr( j * 0.271 ) * 0.26 ), o: 1,
        } );
      }
    }

    // ---- the coral heads ----
    // One per tab gap, each a different colour and a different build, because
    // four of the same coral in a row is wallpaper.
    //
    // The first pass grew these as straight bars forking off a fat trunk with a
    // ball on every tip, and what came out was a bare TREE with berries on it —
    // the wrong plant and the wrong register. Three things fix it, and all three
    // are what separates a coral from a tree:
    //
    //   the BASE IS SHORT and barely thicker than the branches (a coral is a
    //   colony that fans from the rock, it has no trunk to speak of);
    //   the BRANCHES CURVE, each one bending further out as it goes, and they
    //   bend AWAY from the centre so the head opens like a fan;
    //   the TIPS ARE JUST THE TAPER rounded off, not knobs. A ball on a stick is
    //   a berry, and four berried trees in a row was the whole problem.
    this.ocCorals = [];
    const limbs: string[] = [];
    /** One limb: a ribbon that leaves (x,y) at `ang`, bends by `curve` radians per
     *  step, and tapers from w0 to w1 with its tip rounded off. Returns the point
     *  and angle it ended at, so the next generation grows out of it. */
    const limb = ( x: number, y: number, len: number, ang: number, curve: number,
                   w0: number, w1: number ): { x: number, y: number, a: number } => {
      const N = 8;
      const l: string[] = [], r: string[] = [];
      let px = x, py = y, a = ang;
      for ( let i = 0; i <= N; i++ ) {
        const t = i / N;
        const w = ( w0 + ( w1 - w0 ) * t );
        const round = t > 0.82 ? Math.sqrt( Math.max( 0, 1 - Math.pow( ( t - 0.82 ) / 0.18, 2 ) ) ) : 1;
        const half = w * round / 2;
        const nx = Math.cos( a ) * half, ny = Math.sin( a ) * half;
        l.push( `${ f( px - nx ) },${ f( py - ny ) }` );
        r.push( `${ f( px + nx ) },${ f( py + ny ) }` );
        if ( i === N ) { break; }
        px += Math.sin( a ) * ( len / N );
        py -= Math.cos( a ) * ( len / N );
        a += curve / N;
      }
      limbs.push( 'M' + l.join( ' L' ) + ' L' + r.reverse().join( ' L' ) + ' Z' );
      return { x: px, y: py, a };
    };
    const grow = ( x: number, y: number, len: number, ang: number, w: number,
                   depth: number, seed: number ): void => {
      // branches bend away from vertical, and further with every generation
      const curve = ( ang >= 0 ? 1 : -1 ) * ( 0.30 + fr( seed * 0.618 ) * 0.26 );
      const w2 = w * 0.66;
      const end = limb( x, y, len, ang, curve, w, w2 );
      if ( depth === 0 ) { return; }
      const spread = 0.40 + fr( seed * 0.4142 ) * 0.24;
      grow( end.x, end.y, len * 0.78, end.a - spread, w2, depth - 1, seed * 3 + 1 );
      grow( end.x, end.y, len * 0.84, end.a + spread, w2, depth - 1, seed * 3 + 2 );
      // a third branch straight on, on the fuller heads only — this is what makes
      // a colony dense rather than a candelabra
      if ( depth > 1 && fr( seed * 0.7548 ) > 0.35 ) {
        grow( end.x, end.y, len * 0.66, end.a + ( fr( seed ) - 0.5 ) * 0.24, w2 * 0.86,
              depth - 1, seed * 3 + 3 );
      }
    };
    /** A whole head: a short base, then three or four colonies fanning off it. */
    const head = ( x: number, len: number, w: number, depth: number, tilt: number,
                   arms: number, seed: number ): void => {
      const base = limb( x, FLOOR + 8, 9, tilt, tilt * 0.4, w * 1.25, w );
      for ( let k = 0; k < arms; k++ ) {
        const a = tilt + ( ( k + 0.5 ) / arms - 0.5 ) * 1.16;
        grow( base.x, base.y, len, a, w * 0.9, depth, seed * 7 + k + 1 );
      }
    };
    const CORALS: { x: number, len: number, w: number, depth: number, tilt: number,
                    arms: number, c: string }[] = [
      { x: GAPS[ 0 ], len: 15, w: 6.5, depth: 2, tilt: -0.10, arms: 3, c: CORAL_ROSE },
      { x: GAPS[ 1 ], len: 12, w: 5.5, depth: 3, tilt: 0.08, arms: 4, c: CORAL_TAN },
      { x: GAPS[ 2 ], len: 16, w: 7, depth: 2, tilt: 0.12, arms: 4,
        c: mixHex( CORAL_RED, CORAL_ROSE, 0.5 ) },
      { x: GAPS[ 3 ], len: 13, w: 6, depth: 3, tilt: -0.06, arms: 3,
        c: mixHex( CORAL_TAN, CORAL_ROSE, 0.45 ) },
    ];
    for ( let i = 0; i < CORALS.length; i++ ) {
      const c = CORALS[ i ];
      limbs.length = 0;
      head( c.x, c.len, c.w, c.depth, c.tilt, c.arms, i + 1 );
      this.ocCorals.push( { d: limbs.join( ' ' ), fill: c.c } );
      // a smaller companion beside each, offset and deeper in tone — a coral head
      // standing alone in the middle of a gap reads as a specimen on a plinth
      limbs.length = 0;
      head( c.x + ( i % 2 ? -36 : 35 ), c.len * 0.62, c.w * 0.66, Math.max( 1, c.depth - 1 ),
            c.tilt - 0.22, 3, i + 9 );
      this.ocCorals.push( { d: limbs.join( ' ' ), fill: mixHex( c.c, ABYSS, 0.38 ) } );
    }

    // ---- bubbles ----
    // Rising, and rising in COLUMNS rather than scattered: bubbles leave the floor
    // from the same few vents, which is what the icon draws and what stops them
    // reading as dust. They get smaller and fainter as they go up, and none of
    // them is bright enough to compete with a tab glyph.
    this.ocBubbles = [];
    const VENTS = [ 128, 372, 470, 700, 968, 1090, 1268, 1400 ];
    for ( let v = 0; v < VENTS.length; v++ ) {
      const n = 5 + Math.floor( fr( v * 0.618 + 0.4 ) * 4 );
      for ( let i = 0; i < n; i++ ) {
        const t = i / n;
        const j = v * 11 + i;
        this.ocBubbles.push( {
          // The WANDER is what stops a column reading as a string of pearls: a
          // bubble drifts further off its vent the higher it gets, and the ones
          // near the floor are still nearly over it.
          cx: f( VENTS[ v ] + Math.sin( t * 4.2 + v * 1.7 ) * ( 6 + t * 22 )
            + ( fr( j * 0.7548 ) - 0.5 ) * 9 ),
          cy: f( FLOOR - 6 - t * ( 74 + fr( j * 0.414 ) * 22 ) - fr( j * 0.618 ) * 8 ),
          // A wider size spread for the same reason — bubbles leave a vent in a
          // mixture of sizes and the big ones rise fastest, so a column is never
          // one bead repeated.
          r: f( 4.2 - t * 2.6 + fr( j * 0.271 ) * 1.6 ),
          o: +( 0.30 - t * 0.16 ).toFixed( 2 ),
        } );
      }
    }

    // ---- kawaii reef life ----
    // The strip had a garden and no inhabitants, which is why it read as empty
    // water over a row of spikes (owner 2026-08-15). Three families, and they are
    // three rather than one on purpose: sponges give the garden VERTICALS that
    // are not blades, starfish give the FLOOR something resting on it, and the
    // fish give the water a reason to be water.
    //
    // Everything here is round. That is the whole register — the corals and the
    // grass are the pointed shapes in this scene, so the animals have to be the
    // soft ones or the strip stays prickly.
    this.ocSponges = [];
    this.ocStars = [];
    this.ocFish = [];

    /** An ellipse as a path, because these arrays carry `d` and not cx/cy/r. */
    const ell = ( cx: number, cy: number, rx: number, ry: number ): string =>
      `M${ f( cx - rx ) },${ f( cy ) }`
      + ` a${ f( rx ) },${ f( ry ) } 0 1,0 ${ f( rx * 2 ) },0`
      + ` a${ f( rx ) },${ f( ry ) } 0 1,0 ${ f( -rx * 2 ) },0 Z`;

    // TUBE SPONGES — the "tüp gibi şeyler". A tube is a straight-sided column
    // with a DOMED top and a darker mouth sunk into it, and both halves are
    // load-bearing: without the dome it is a bar, without the mouth it is a
    // finger. They stand in threes at different heights, never level — three
    // tubes of one height is a fence.
    const SPONGE_HUES = [ '#E58FA8', '#D9A0C8', '#EFA98B', '#C98FBE' ];
    for ( let g = 0; g < GAPS.length; g++ ) {
      const base = GAPS[ g ] + ( g % 2 ? 62 : -64 );
      const hue = SPONGE_HUES[ g % SPONGE_HUES.length ];
      const mouth = mixHex( hue, SHELF, 0.42 );
      for ( let k = 0; k < 3; k++ ) {
        const j = g * 5 + k;
        const w = 11 + fr( j * 0.618 + 0.2 ) * 6;
        const h = 22 + fr( j * 0.4142 + 0.5 ) * 22;
        const x = base + ( k - 1 ) * ( w + 5 ) + ( fr( j * 0.271 ) - 0.5 ) * 5;
        const r = w / 2;
        const top = FLOOR + 6 - h;
        this.ocSponges.push( {
          d: `M${ f( x - r ) },${ f( FLOOR + 8 ) } L${ f( x - r ) },${ f( top + r ) }`
            + ` A${ f( r ) },${ f( r ) } 0 0,1 ${ f( x + r ) },${ f( top + r ) }`
            + ` L${ f( x + r ) },${ f( FLOOR + 8 ) } Z`,
          fill: hue,
        } );
        this.ocSponges.push( { d: ell( x, top + r * 0.72, r * 0.60, r * 0.34 ), fill: mouth } );
      }
    }

    // STARFISH — five rounded arms, resting flat on the shelf. Rounded because a
    // geometric five-point star is a RATING, not an animal: the corners have to
    // be turned into knuckles before the eye reads it as something alive.
    const star = ( cx: number, cy: number, R: number, r: number, rot: number ): string => {
      const pts: [ number, number ][] = [];
      for ( let i = 0; i < 10; i++ ) {
        const a = rot + i * Math.PI / 5;
        const rad = i % 2 === 0 ? R : r;
        // squashed vertically: this is a star lying on the floor, seen at a
        // glancing angle, not one pinned to a wall
        pts.push( [ cx + Math.cos( a ) * rad, cy + Math.sin( a ) * rad * 0.62 ] );
      }
      const mid = ( a: [ number, number ], b: [ number, number ] ): [ number, number ] =>
        [ ( a[ 0 ] + b[ 0 ] ) / 2, ( a[ 1 ] + b[ 1 ] ) / 2 ];
      let m = mid( pts[ 0 ], pts[ 1 ] );
      let d = `M${ f( m[ 0 ] ) },${ f( m[ 1 ] ) }`;
      for ( let i = 1; i <= 10; i++ ) {
        const cur = pts[ i % 10 ], nxt = pts[ ( i + 1 ) % 10 ];
        m = mid( cur, nxt );
        d += ` Q${ f( cur[ 0 ] ) },${ f( cur[ 1 ] ) } ${ f( m[ 0 ] ) },${ f( m[ 1 ] ) }`;
      }
      return d + ' Z';
    };
    const STAR_HUES = [ '#F2A15E', '#E98BA4', '#F0B972', '#DE97C4', '#F09A76' ];
    const STAR_AT = [ 168, 402, 520, 742, 1012, 1150, 1344 ];
    for ( let i = 0; i < STAR_AT.length; i++ ) {
      const R = 15 + fr( i * 0.618 + 0.3 ) * 7;
      const cx = STAR_AT[ i ] + ( fr( i * 0.4142 ) - 0.5 ) * 18;
      const cy = FLOOR + 12 + fr( i * 0.271 + 0.6 ) * 12;
      const hue = STAR_HUES[ i % STAR_HUES.length ];
      this.ocStars.push( {
        d: star( cx, cy, R, R * 0.44, fr( i * 0.7548 ) * 1.26 ),
        fill: hue,
      } );
      // the paler disc every seastar has in the middle, so it is not one flat
      // silhouette — at this size it is the only interior detail that survives
      this.ocStars.push( { d: ell( cx, cy, R * 0.26, R * 0.17 ), fill: mixHex( hue, '#FFFFFF', 0.45 ) } );
    }

    // CLOWNFISH — small, and drifting in the TAB GAPS rather than across the
    // whole strip: the gaps are the only horizontal band with no label over it,
    // and a fish behind the word "Chat" is a smudge.
    //
    // Six parts each and none of them optional: tail, body, two white bands, eye,
    // pupil. Drop the bands and it is a goldfish; drop the eye and it is a leaf.
    const ORANGE = '#F0894A', ORANGE_DEEP = '#D96C33';
    const BAND = '#FFF3E6', PUPIL = '#2B3A3A';
    const fish = ( x: number, y: number, s: number, flip: boolean ): void => {
      const d = flip ? -1 : 1;
      const rx = s * 0.62, ry = s * 0.42;
      // tail first, so the body's edge covers where the two meet
      this.ocFish.push( {
        d: `M${ f( x - d * rx * 0.5 ) },${ f( y ) }`
          + ` L${ f( x - d * rx * 1.75 ) },${ f( y - ry * 0.95 ) }`
          + ` Q${ f( x - d * rx * 1.25 ) },${ f( y ) } ${ f( x - d * rx * 1.75 ) },${ f( y + ry * 0.95 ) } Z`,
        fill: ORANGE_DEEP,
      } );
      // a small dorsal, which is what stops the body reading as an egg
      this.ocFish.push( {
        d: `M${ f( x - d * rx * 0.35 ) },${ f( y - ry * 0.80 ) }`
          + ` Q${ f( x - d * rx * 0.05 ) },${ f( y - ry * 1.55 ) } ${ f( x + d * rx * 0.35 ) },${ f( y - ry * 0.72 ) } Z`,
        fill: ORANGE_DEEP,
      } );
      this.ocFish.push( { d: ell( x, y, rx, ry ), fill: ORANGE } );
      // the two bands: narrow ellipses standing inside the body, the rear one
      // shorter because the body tapers there
      this.ocFish.push( { d: ell( x + d * rx * 0.10, y, rx * 0.15, ry * 0.90 ), fill: BAND } );
      this.ocFish.push( { d: ell( x - d * rx * 0.55, y, rx * 0.12, ry * 0.66 ), fill: BAND } );
      // eye, well forward and big — the one thing that makes this kawaii rather
      // than a fish-shaped mark
      this.ocFish.push( { d: ell( x + d * rx * 0.52, y - ry * 0.18, s * 0.13, s * 0.13 ), fill: BAND } );
      this.ocFish.push( { d: ell( x + d * rx * 0.56, y - ry * 0.16, s * 0.075, s * 0.075 ), fill: PUPIL } );
    };
    // ONE per gap, plus a smaller companion held well away from it. Two and three
    // to a gap was the first pass and they overlapped into a shoal — a clownfish
    // is a solitary fish over its own patch of reef, and two of them nose to tail
    // read as one confused animal at this scale.
    for ( let g = 0; g < GAPS.length; g++ ) {
      const j = g * 7;
      fish( GAPS[ g ] + ( fr( j * 0.618 + 0.15 ) - 0.5 ) * 70,
            74 + fr( j * 0.4142 + 0.35 ) * 20,
            24 + fr( j * 0.271 + 0.7 ) * 7,
            fr( j * 0.7548 ) > 0.5 );
      // the companion sits BETWEEN this gap and the next, smaller and higher up,
      // so the eye reads depth rather than a pair
      const mid = GAPS[ g ] + ( g < GAPS.length - 1 ? ( GAPS[ g + 1 ] - GAPS[ g ] ) / 2 : 150 );
      fish( mid + ( fr( j * 0.4142 + 0.6 ) - 0.5 ) * 40,
            64 + fr( j * 0.618 + 0.2 ) * 16,
            15 + fr( j * 0.7548 + 0.1 ) * 4,
            fr( j * 0.271 ) > 0.5 );
    }
  }

  private buildTallinn( p: Product ): void {
    const fr = ( v: number ) => v - Math.floor( v );
    const f = ( v: number ) => +v.toFixed( 1 );

    // The theme's own palette (base-themes/cities/tallinn-theme), by name.
    const BALTIC_MORNING = '#EDF2FA';
    const WINTER_SKY = '#D0DCEF';
    const LIMESTONE_BLUE = '#A9B9DA';
    const TERRACOTTA = '#C17777';
    const WEATHERED = '#B1544B';
    const BRICK_LANE = '#B6564B';
    const RUST = '#C0564E';
    const INDIGO = '#324979';
    const MIDNIGHT = '#0F244F';
    const AMBER = '#C39A4E';

    const GAP = 16;
    const SPAN = this.W - 2 * GAP;
    /** The snow the wall stands in. */
    const GROUND = 126;
    // The top of the curtain wall, and the eaves of the houses behind it. The
    // wall is TALLER than the first pass had it (96 → 88) and the houses are
    // SHORTER, because the wall is the subject: from outside the gate you see a
    // long band of limestone with the roofs just breaking over it, not a town
    // with a low fence in front. The real thing is 11 to 16 m of wall against
    // three-storey houses, which is roughly this ratio.
    const WALL_TOP = 88;
    const EAVE = 88;

    // ---- sky: overcast, pale, and emptiest exactly where the glyphs land ----
    this.taSkyTop = mixHex( BALTIC_MORNING, '#FFFFFF', 0.5 );
    this.taSkyMid = mixHex( BALTIC_MORNING, WINTER_SKY, 0.45 );
    this.taSkyLow = mixHex( WINTER_SKY, LIMESTONE_BLUE, 0.3 );

    /** Snow in shadow: blue-violet, not cyan — it is lit by skylight only, and at
     *  this latitude the low sky is pink-lilac rather than blue. */
    const SNOW_SHADE = mixHex( LIMESTONE_BLUE, WINTER_SKY, 0.45 );
    // Limestone, and DARKER than the first pass. At 0.16 of amber the wall
    // measured 1.27:1 against the snow it stands in and the two simply merged
    // into one pale band; at 0.34 it is 1.47 and the wall is an object.
    const STONE = mixHex( BALTIC_MORNING, AMBER, 0.34 );
    const STONE_DARK = mixHex( STONE, INDIGO, 0.22 );

    const snow: string[] = [];
    const windows: string[] = [];
    this.taWalls = [];
    this.taRoofs = [];

    /**
     * One roof slope, drawn as terracotta with snow STRIPES down it rather than
     * as a white shape: a clean cap on the ridge, a lip at the eaves, and pans of
     * snow between bare tile rolls. That striping is the whole difference between
     * "a snowy roof" and "a white triangle".
     */
    const tiledGable = ( x0: number, x1: number, eave: number, ridge: number, roof: string ): void => {
      const mid = ( x0 + x1 ) / 2;
      this.taRoofs.push( {
        d: `M${ f( x0 - 3 ) },${ f( eave ) } L${ f( mid ) },${ f( ridge ) }`
          + ` L${ f( x1 + 3 ) },${ f( eave ) } Z`,
        fill: roof,
      } );
      // the ridge cap: an unbroken white line, the most legible snow at this size
      snow.push( `M${ f( mid ) },${ f( ridge ) } L${ f( mid - 9 ) },${ f( ridge + 8.5 ) }`
        + ` L${ f( mid ) },${ f( ridge + 4.5 ) } L${ f( mid + 9 ) },${ f( ridge + 8.5 ) } Z` );
      // the eaves lip, overhanging a touch past the wall
      snow.push( `M${ f( x0 - 3.4 ) },${ f( eave - 2.4 ) } L${ f( x1 + 3.4 ) },${ f( eave - 2.4 ) }`
        + ` L${ f( x1 + 3.4 ) },${ f( eave ) } L${ f( x0 - 3.4 ) },${ f( eave ) } Z` );
      // TILE COURSES, in bordeaux — not snow stripes (owner 2026-08-14:
      // "çatılardaki beyaz çizgiler olmayacak. yapabiliyorsan kiremit tasarımı
      // bordo çizgiler olsun"). Three lines parallel to the eaves, each clipped to
      // the triangle's width at its own height, in the roof colour taken down
      // toward midnight. That is what a pantile roof actually shows from across a
      // street: courses running level, not stripes running down the slope. The
      // white version this replaces also had the roofs reading as candy awnings.
      for ( let k = 1; k <= 3; k++ ) {
        const t = k / 4;
        const half = ( x1 - x0 ) / 2 * t + 3 * t;
        const cy = ridge + ( eave - ridge ) * t;
        this.taRoofs.push( {
          d: `M${ f( mid - half ) },${ f( cy ) } L${ f( mid + half ) },${ f( cy ) }`
            + ` L${ f( mid + half ) },${ f( cy + 1.3 ) } L${ f( mid - half ) },${ f( cy + 1.3 ) } Z`,
          fill: mixHex( roof, MIDNIGHT, 0.3 ),
        } );
      }
    };

    // ---- the town behind the wall ----
    // Narrow plots with the gable to the street, the Hanseatic pattern. Widths are
    // jittered, because a street of identical frontages is a terrace built at once
    // and this one grew.
    // The facades of Raekoja plats and Pikk: Tallinn's old town is painted, not
    // stone-faced — ochre-yellow, powder blue, dusty pink, terracotta and green,
    // house by house (owner 2026-08-14: "farklı renklerde binalar mavi sarı
    // kırmızı falan tallinn old town'da var"). Seven of them against twenty plots,
    // coprime, so no colour ever lands twice in a row or lines up with a tower.
    const WALL_FACES = [
      mixHex( '#FFFFFF', AMBER, 0.42 ),              // ochre yellow
      mixHex( '#FFFFFF', '#377CC1', 0.32 ),          // powder blue
      '#ECC2C2',                                     // Pink Facade, the theme's own
      mixHex( '#FFFFFF', RUST, 0.34 ),               // faded terracotta red
      mixHex( BALTIC_MORNING, LIMESTONE_BLUE, 0.4 ), // limestone grey-blue
      mixHex( '#FFFFFF', '#548A64', 0.26 ),          // courtyard green
      mixHex( '#FFFFFF', AMBER, 0.22 ),              // pale cream
    ];
    const ROOFS = [ TERRACOTTA, WEATHERED, BRICK_LANE, RUST, mixHex( WEATHERED, TERRACOTTA, 0.5 ) ];
    // TWENTY plots, not twelve. The owner's note is about pitch — "bina çatıları
    // çok yayık olmuş biraz daha dik olacak" — and pitch is a ratio, so the fix is
    // as much in the width as in the height: a 120-unit frontage under a 20-unit
    // rise is a 18-degree roof, which is a bungalow. At 68 units wide and a 24 to
    // 31 rise these run 35 to 42 degrees, which is the shallow end of the real
    // thing (45-60) and as steep as the glyph band overhead allows.
    const N = 20;
    const edge = ( k: number ): number =>
      GAP + k * ( SPAN / N ) + ( fr( k * 0.4142 + 0.11 ) - 0.5 ) * ( SPAN / N ) * 0.36;

    for ( let i = 0; i < N; i++ ) {
      const x0 = edge( i ) + 2, x1 = edge( i + 1 ) - 2;
      // The eaves sit ABOVE the wall's coping, so a band of painted facade shows
      // between the two (owner: "binalar biraz daha uzun olacak ki biraz binalar
      // da görünecek"). Twelve to sixteen units of it, which at strip scale is
      // one storey and a window.
      const eave = EAVE - 12 - fr( i * 0.7548 + 0.2 ) * 4;
      const ridge = eave - 24 - fr( i * 0.618 + 0.5 ) * 7;
      // the facade, which the wall in front will cut off at its own top
      this.taWalls.push( {
        d: `M${ f( x0 ) },${ f( GROUND ) } L${ f( x0 ) },${ f( eave ) }`
          + ` L${ f( x1 ) },${ f( eave ) } L${ f( x1 ) },${ f( GROUND ) } Z`,
        fill: WALL_FACES[ i % WALL_FACES.length ],
      } );
      tiledGable( x0, x1, eave, ridge, ROOFS[ i % ROOFS.length ] );
      // one row of windows in the band of facade that clears the wall — two per
      // plot, which is what a narrow Hanseatic frontage carries
      for ( let c = 0; c < 2; c++ ) {
        const wx = x0 + 8 + c * ( ( x1 - x0 - 16 ) / 2 ) + 1;
        windows.push( `M${ f( wx ) },${ f( eave + 4 ) } L${ f( wx ) },${ f( eave + 11 ) }`
          + ` L${ f( wx + 5.5 ) },${ f( eave + 11 ) } L${ f( wx + 5.5 ) },${ f( eave + 4 ) } Z` );
      }
      // CROW STEPS on every third house: the older Hanseatic gables are stepped,
      // and at this size the steps are the one detail that says Baltic rather than
      // generic old town. Drawn as three blocks climbing each slope.
      if ( i % 3 === 1 ) {
        for ( let k = 1; k <= 3; k++ ) {
          const t = k / 4.2;
          const lx = x0 + ( ( x0 + x1 ) / 2 - x0 ) * t;
          const ly = eave - ( eave - ridge ) * t;
          const w = 5.5, h = 4.2;
          this.taRoofs.push( {
            d: `M${ f( lx - w ) },${ f( ly + h ) } L${ f( lx - w ) },${ f( ly ) }`
              + ` L${ f( lx + w ) },${ f( ly ) } L${ f( lx + w ) },${ f( ly + h ) } Z`,
            fill: ROOFS[ i % ROOFS.length ],
          } );
          this.taRoofs.push( {
            d: `M${ f( x1 - ( lx - x0 ) - w ) },${ f( ly + h ) } L${ f( x1 - ( lx - x0 ) - w ) },${ f( ly ) }`
              + ` L${ f( x1 - ( lx - x0 ) + w ) },${ f( ly ) } L${ f( x1 - ( lx - x0 ) + w ) },${ f( ly + h ) } Z`,
            fill: ROOFS[ i % ROOFS.length ],
          } );
          snow.push( `M${ f( lx - w ) },${ f( ly - 1.8 ) } L${ f( lx + w ) },${ f( ly - 1.8 ) }`
            + ` L${ f( lx + w ) },${ f( ly ) } L${ f( lx - w ) },${ f( ly ) } Z` );
          snow.push( `M${ f( x1 - ( lx - x0 ) - w ) },${ f( ly - 1.8 ) } L${ f( x1 - ( lx - x0 ) + w ) },${ f( ly - 1.8 ) }`
            + ` L${ f( x1 - ( lx - x0 ) + w ) },${ f( ly ) } L${ f( x1 - ( lx - x0 ) - w ) },${ f( ly ) } Z` );
        }
      }
      // a chimney with its own cap of snow, on two houses in three
      if ( i % 3 !== 2 ) {
        const cx = x0 + ( x1 - x0 ) * 0.72;
        this.taWalls.push( {
          d: `M${ f( cx - 3 ) },${ f( eave ) } L${ f( cx - 3 ) },${ f( ridge + 6 ) }`
            + ` L${ f( cx + 3 ) },${ f( ridge + 6 ) } L${ f( cx + 3 ) },${ f( eave ) } Z`,
          fill: STONE_DARK,
        } );
        snow.push( `M${ f( cx - 3.8 ) },${ f( ridge + 3.6 ) } L${ f( cx + 3.8 ) },${ f( ridge + 3.6 ) }`
          + ` L${ f( cx + 3.8 ) },${ f( ridge + 6 ) } L${ f( cx - 3.8 ) },${ f( ridge + 6 ) } Z` );
      }
    }

    // ---- ST OLAF'S, the one needle ----
    // The spire is about twice the height of the masonry below it. Nothing else in
    // Tallinn has that proportion, and it is the whole reason this is the landmark
    // worth the space. Copper, which reads near-black at distance.
    const OL_X = 589;
    this.taRoofs.push( {
      d: `M${ f( OL_X - 11 ) },${ f( GROUND ) } L${ f( OL_X - 11 ) },${ f( 54 ) }`
        + ` L${ f( OL_X + 11 ) },${ f( 54 ) } L${ f( OL_X + 11 ) },${ f( GROUND ) } Z`,
      fill: STONE,
    } );
    this.taRoofs.push( {
      d: `M${ f( OL_X - 11 ) },${ f( 54 ) } L${ f( OL_X ) },${ f( 17 ) }`
        + ` L${ f( OL_X + 11 ) },${ f( 54 ) } Z`,
      fill: mixHex( INDIGO, MIDNIGHT, 0.5 ),
    } );
    // the four corner turrets at the spire's foot, the detail that stops it being
    // a plain cone
    for ( const dx of [ -11, 11 ] ) {
      this.taRoofs.push( {
        d: `M${ f( OL_X + dx - 3 ) },${ f( 54 ) } L${ f( OL_X + dx ) },${ f( 42 ) }`
          + ` L${ f( OL_X + dx + 3 ) },${ f( 54 ) } Z`,
        fill: mixHex( INDIGO, MIDNIGHT, 0.5 ),
      } );
    }
    snow.push( `M${ f( OL_X ) },${ f( 17 ) } L${ f( OL_X - 3.2 ) },${ f( 32 ) }`
      + ` L${ f( OL_X - 1.3 ) },${ f( 32 ) } L${ f( OL_X ) },${ f( 24 ) }`
      + ` L${ f( OL_X + 1.3 ) },${ f( 32 ) } L${ f( OL_X + 3.2 ) },${ f( 32 ) } Z` );
    windows.push( `M${ f( OL_X - 3 ) },${ f( 64 ) } L${ f( OL_X - 3 ) },${ f( 78 ) }`
      + ` L${ f( OL_X + 3 ) },${ f( 78 ) } L${ f( OL_X + 3 ) },${ f( 64 ) } Z` );

    // ---- the curtain wall ----
    // One band across the whole strip, with a snow cap along its walk. It is drawn
    // AFTER the houses so it cuts their facades off, which is what puts the town
    // behind it rather than beside it.
    this.taWalls.push( {
      d: `M0,${ f( GROUND ) } L0,${ f( WALL_TOP ) } L${ this.W },${ f( WALL_TOP ) }`
        + ` L${ this.W },${ f( GROUND ) } Z`,
      fill: STONE,
    } );
    snow.push( `M0,${ f( WALL_TOP - 3.2 ) } L${ this.W },${ f( WALL_TOP - 3.2 ) }`
      + ` L${ this.W },${ f( WALL_TOP + 1 ) } L0,${ f( WALL_TOP + 1 ) } Z` );
    // NO blind arcading. A run of arches along the curtain was drawn here and cut
    // the same day (owner 2026-08-14: "her yere kapı yaptın. 1 tane sola yakın
    // kapı olsun yeter") — at strip scale a repeated arch reads as a row of
    // doorways, and a wall with fifteen gates is not a defence. The coursing below
    // does the job the arcading was there for, which was to stop 38 units of flat
    // stone reading as a strip of card.
    // COURSED, block by block (owner 2026-08-14: "duvarlarda çizgiler olacak
    // tuğla tuğla"). Six courses down the wall's 38 units, with the vertical
    // joints staggered half a block from course to course — that stagger is the
    // whole difference between masonry and graph paper, and it costs nothing here
    // because the joints are one path.
    const joints: string[] = [];
    const COURSE = 6.2, BLOCK = 21;
    for ( let k = 0; ; k++ ) {
      const cy = WALL_TOP + 4 + k * COURSE;
      if ( cy > GROUND - 4 ) { break; }
      joints.push( `M0,${ f( cy ) } L${ this.W },${ f( cy ) }`
        + ` L${ this.W },${ f( cy + 0.9 ) } L0,${ f( cy + 0.9 ) } Z` );
      for ( let x = ( k % 2 ) * BLOCK / 2; x < this.W; x += BLOCK ) {
        joints.push( `M${ f( x ) },${ f( cy ) } L${ f( x + 0.9 ) },${ f( cy ) }`
          + ` L${ f( x + 0.9 ) },${ f( cy + COURSE ) } L${ f( x ) },${ f( cy + COURSE ) } Z` );
      }
    }
    this.taRoofs.push( { d: joints.join( ' ' ), fill: mixHex( STONE, INDIGO, 0.16 ) } );
    // the wall's own shadow line where it meets the snow, so it stands on the
    // ground instead of floating on it
    this.taRoofs.push( {
      d: `M0,${ f( GROUND - 3 ) } L${ this.W },${ f( GROUND - 3 ) }`
        + ` L${ this.W },${ f( GROUND ) } L0,${ f( GROUND ) } Z`,
      fill: SNOW_SHADE,
    } );

    /**
     * A wall tower, TRACED FROM THE THEME'S OWN ICON (owner 2026-08-14: "benim
     * tallinn icon svg'ye bak ordan alabilirsin duvarları falan"), the way
     * Istanbul's Maiden's Tower and Autumn's maple were.
     *
     * Measured off tallinn-theme/icon.png at 500x500, on the left gate tower:
     *   cone      apex y 180 → base y 236, base x 121..174   → 53 wide, 56 tall
     *   drum      x 137..181, top y 236                      → 44 wide
     *   finial    x 156..158, y 150..180                     → 3 wide, 30 tall
     *   snow      draped over the RIGHT slope, x 156..190, running from the apex
     *             down past the eaves — not a symmetric cap
     * so as ratios of the drum's width: cone 1.20 wide and 1.27 tall, finial 0.68
     * tall, and the cone overhangs the drum by about a tenth on each side.
     *
     * Three things from that trace are load-bearing:
     *  - The CONE IS WIDER THAN THE DRUM and clearly taller than it is wide. A
     *    cone drawn flush with the drum reads as a pencil.
     *  - The FINIAL. Two thirds of the drum's width tall, three units thin. It is
     *    the first thing to vanish at strip scale and the tower stops reading as
     *    a tower when it does.
     *  - The SNOW IS ON ONE SIDE. The icon drapes it over one slope with a
     *    scalloped lower edge, which is what snow on a cone actually does; the
     *    symmetric cap this file had before read as a party hat.
     */
    const gateTower = ( cx: number, drumW: number, capTop: number, roof: string ): void => {
      const halfW = drumW / 2;
      const coneW = drumW * 1.20;
      const coneH = drumW * 1.27;
      const drumTop = capTop + coneH;
      const coneL = cx - coneW / 2, coneR = cx + coneW / 2;

      // drum — in the ROOFS layer, because the template paints every wall before
      // every roof and a drum in the walls layer ends up behind the houses
      this.taRoofs.push( {
        d: `M${ f( cx - halfW ) },${ f( GROUND ) } L${ f( cx - halfW ) },${ f( drumTop ) }`
          + ` L${ f( cx + halfW ) },${ f( drumTop ) } L${ f( cx + halfW ) },${ f( GROUND ) } Z`,
        fill: mixHex( STONE, '#FFFFFF', 0.12 ),
      } );
      // the drum's shaded right flank, which is what makes it read as round
      this.taRoofs.push( {
        d: `M${ f( cx + halfW * 0.42 ) },${ f( GROUND ) } L${ f( cx + halfW * 0.42 ) },${ f( drumTop ) }`
          + ` L${ f( cx + halfW ) },${ f( drumTop ) } L${ f( cx + halfW ) },${ f( GROUND ) } Z`,
        fill: mixHex( STONE, INDIGO, 0.1 ),
      } );
      // cone
      this.taRoofs.push( {
        d: `M${ f( coneL ) },${ f( drumTop + 1 ) } L${ f( cx ) },${ f( capTop ) }`
          + ` L${ f( coneR ) },${ f( drumTop + 1 ) } Z`,
        fill: roof,
      } );
      // the snow, draped over the right slope with a scalloped lower edge
      const sx = ( t: number ) => cx + ( coneR - cx ) * t;
      const sy = ( t: number ) => capTop + ( drumTop - capTop ) * t;
      snow.push( `M${ f( cx ) },${ f( capTop ) }`
        + ` L${ f( sx( 0.98 ) ) },${ f( sy( 0.98 ) ) }`
        + ` L${ f( sx( 0.74 ) ) },${ f( sy( 0.80 ) ) }`
        + ` L${ f( sx( 0.60 ) ) },${ f( sy( 0.72 ) ) }`
        + ` L${ f( sx( 0.36 ) ) },${ f( sy( 0.62 ) ) }`
        + ` L${ f( sx( 0.16 ) ) },${ f( sy( 0.30 ) ) } Z` );
      // finial: a thin rod with a small ball, two thirds of the drum's width tall
      const poleH = drumW * 0.68;
      this.taRoofs.push( {
        d: `M${ f( cx - 1.3 ) },${ f( capTop ) } L${ f( cx - 1.3 ) },${ f( capTop - poleH ) }`
          + ` L${ f( cx + 1.3 ) },${ f( capTop - poleH ) } L${ f( cx + 1.3 ) },${ f( capTop ) } Z`
          + ` M${ f( cx - 2.6 ) },${ f( capTop - poleH ) } L${ f( cx - 2.6 ) },${ f( capTop - poleH - 2.6 ) }`
          + ` L${ f( cx + 2.6 ) },${ f( capTop - poleH - 2.6 ) } L${ f( cx + 2.6 ) },${ f( capTop - poleH ) } Z`,
        fill: mixHex( INDIGO, MIDNIGHT, 0.4 ),
      } );
      // the icon's two small windows, one over the other, and the string course
      for ( const dy of [ 14, 30 ] ) {
        windows.push( `M${ f( cx - 2.4 ) },${ f( drumTop + dy ) } L${ f( cx - 2.4 ) },${ f( drumTop + dy + 9 ) }`
          + ` L${ f( cx + 2.4 ) },${ f( drumTop + dy + 9 ) } L${ f( cx + 2.4 ) },${ f( drumTop + dy ) } Z` );
      }
      snow.push( `M${ f( cx - halfW ) },${ f( drumTop + 2.2 ) } L${ f( cx + halfW ) },${ f( drumTop + 2.2 ) }`
        + ` L${ f( cx + halfW ) },${ f( drumTop + 4.2 ) } L${ f( cx - halfW ) },${ f( drumTop + 4.2 ) } Z` );
    };

    /** Fat Margaret: the anti-tower. 25 m across against 20 m high, and FLAT on
     *  top — it has a roof terrace, not a cone. It is the one silhouette on this
     *  wall that is broader than it is tall. */
    const fatMargaret = ( cx: number ): void => {
      const top = 66, halfW = 30;
      this.taRoofs.push( {
        d: `M${ f( cx - halfW ) },${ f( GROUND ) } L${ f( cx - halfW ) },${ f( top ) }`
          + ` L${ f( cx + halfW ) },${ f( top ) } L${ f( cx + halfW ) },${ f( GROUND ) } Z`,
        fill: mixHex( STONE, '#FFFFFF', 0.06 ),
      } );
      this.taRoofs.push( {
        d: `M${ f( cx + halfW * 0.45 ) },${ f( GROUND ) } L${ f( cx + halfW * 0.45 ) },${ f( top ) }`
          + ` L${ f( cx + halfW ) },${ f( top ) } L${ f( cx + halfW ) },${ f( GROUND ) } Z`,
        fill: mixHex( STONE, INDIGO, 0.1 ),
      } );
      // the parapet, a step proud of the drum, with snow lying flat on it
      this.taRoofs.push( {
        d: `M${ f( cx - halfW - 3 ) },${ f( top ) } L${ f( cx - halfW - 3 ) },${ f( top - 5 ) }`
          + ` L${ f( cx + halfW + 3 ) },${ f( top - 5 ) } L${ f( cx + halfW + 3 ) },${ f( top ) } Z`,
        fill: STONE_DARK,
      } );
      snow.push( `M${ f( cx - halfW - 3 ) },${ f( top - 8 ) } L${ f( cx + halfW + 3 ) },${ f( top - 8 ) }`
        + ` L${ f( cx + halfW + 3 ) },${ f( top - 5 ) } L${ f( cx - halfW - 3 ) },${ f( top - 5 ) } Z` );
      for ( const dx of [ -17, 0, 17 ] ) {
        windows.push( `M${ f( cx + dx - 2.6 ) },${ f( top + 13 ) } L${ f( cx + dx - 2.6 ) },${ f( top + 23 ) }`
          + ` L${ f( cx + dx + 2.6 ) },${ f( top + 23 ) } L${ f( cx + dx + 2.6 ) },${ f( top + 13 ) } Z` );
      }
    };

    // Four landmarks, one per tab gap, and every one a different silhouette: a
    // squat flat drum, a needle, a single fat cone, a matched pair. Three
    // interchangeable cones would have been a fence.
    fatMargaret( 883 );
    // THE VIRU GATE: an arch through the curtain with a tower on either side of it
    // (owner 2026-08-14: "kale duvarında kapı olacak kapının iki yanında kule
    // olacak"). That pairing is the one piece of Tallinn everybody has a
    // photograph of, and it is what the theme's own icon draws.
    //
    // ONE gate, and it sits LEFT (owner: "1 tane sola yakın kapı olsun yeter") at
    // x 294 — a tab GAP, so the towers rise into the glyph band where no glyph
    // stands, and the arch itself is below the band entirely.
    const GATE_X = 294;
    gateTower( GATE_X - 40, 26, 34, WEATHERED );
    gateTower( GATE_X + 40, 26, 34, BRICK_LANE );
    // the opening: a round-headed arch cut down to the snow, with a band of stone
    // over it and the dark of the passage inside
    this.taRoofs.push( {
      d: `M${ f( GATE_X - 21 ) },${ f( GROUND ) } L${ f( GATE_X - 21 ) },${ f( WALL_TOP + 20 ) }`
        + ` Q${ f( GATE_X ) },${ f( WALL_TOP + 1 ) } ${ f( GATE_X + 21 ) },${ f( WALL_TOP + 20 ) }`
        + ` L${ f( GATE_X + 21 ) },${ f( GROUND ) } Z`,
      fill: mixHex( INDIGO, MIDNIGHT, 0.55 ),
    } );
    // the arch ring, a course of pale stone following the head of the opening
    this.taRoofs.push( {
      d: `M${ f( GATE_X - 25 ) },${ f( WALL_TOP + 22 ) }`
        + ` Q${ f( GATE_X ) },${ f( WALL_TOP - 4 ) } ${ f( GATE_X + 25 ) },${ f( WALL_TOP + 22 ) }`
        + ` L${ f( GATE_X + 21 ) },${ f( WALL_TOP + 22 ) }`
        + ` Q${ f( GATE_X ) },${ f( WALL_TOP + 1 ) } ${ f( GATE_X - 21 ) },${ f( WALL_TOP + 22 ) } Z`,
      fill: mixHex( STONE, '#FFFFFF', 0.3 ),
    } );
    // Kiek in de Kök: one artillery tower, 17 m across and six storeys, so it is
    // much fatter than a wall tower and carries one big cone. It takes the last
    // gap, well clear of the gate.
    gateTower( 1178, 40, 26, RUST );

    this.taSnowD = snow.join( ' ' );
    this.taSnowFill = '#FFFFFF';
    this.taWindowD = windows.join( ' ' );
    this.taWindowFill = mixHex( INDIGO, MIDNIGHT, 0.4 );
    this.taBackD = '';

    // ---- the snow the wall stands in ----
    // Drifted against the foot of the wall and ramping down to the tab bar's own
    // fill, which on this skin is a near-white — so for once the strip ends on
    // exactly what the scene's foreground already is. The drift's top edge is a
    // sine with a whole number of periods across the strip, so the tile seam
    // cannot show a step.
    const pts: string[] = [];
    for ( let x = 0; x <= this.W; x += 16 ) {
      const y = GROUND - 2 + Math.sin( 2 * Math.PI * 3 * x / this.W ) * 3;
      pts.push( `${ x === 0 ? 'M' : 'L' }${ f( x ) },${ f( y ) }` );
    }
    this.taGroundD = pts.join( ' ' ) + ` L${ this.W },${ this.H } L0,${ this.H } Z`;
    this.taGroundTop = mixHex( '#FFFFFF', SNOW_SHADE, 0.35 );
    this.taGroundLow = lineTabFill( p.key, p.appearance );
  }

  /**
   * Rio: GUANABARA BAY AT FIRST LIGHT — the morros standing in the water with
   * the Sugarloaf among them, under a pale dawn sky.
   *
   * The hero split, which is Roraima's: the CRISTO carries the Home tab and the
   * passcode dots, so the strip gets the other half of the theme's own icon —
   * the bay and its rounded green hills. A strip that repeats the tab's glyph
   * leaves the scene nothing of its own to say.
   *
   * TWO constraints decided almost every number here.
   *
   * The first is CONTRAST, and it is the one this family keeps shipping wrong. A
   * classic tab glyph sits at roughly y 45..105 on this 150px strip, straight
   * across the horizon, and Rio's glyphs are DEEP GREEN because the skin is
   * light. So the band behind them has to stay pale: the morros run from a hazy
   * sage at the back to a mid canopy green at the front and never reach the
   * theme's Rainforest, which is the tab's own colour. Drawing "real" dark
   * jungle hills here would put a dark glyph on a dark hill, which is exactly
   * the inversion [[line-tab-glyph-contrast]] records.
   *
   * The second is the SEPARATION FROM ISTANBUL. Both drafts came in as a pale
   * dawn over turquoise water and their sky luminances were 0.837 and 0.798 — one
   * picture in two tints. Istanbul kept the blue and moved its warm band down to
   * the horizon; this one is GREEN, and the green mass of the morros is what
   * tells the two apart at a glance. Its water is a still bay, not a running
   * strait, and there is no bridge.
   *
   * The scene is inset 16 units at both ends, so what meets at the tile seam is
   * plain sky above and plain water below — both of which tile by construction.
   * That is Paris's fix, and it is cheaper and safer than reconstructing a
   * silhouette across the join.
   */
  private buildRio( p: Product ): void {
    const fr = ( v: number ) => v - Math.floor( v );
    const f = ( v: number ) => +v.toFixed( 1 );

    // The theme's own palette by name (base-themes/cities/rio-de-janeiro-theme).
    const MORNING = '#F7FAF6';
    const LAGOON = '#D0ECEF';
    const PALM_SHADE = '#D9EFD2';
    const GARDEN = '#B8E5AD';
    const CANOPY = '#85B778';
    const EMERALD = '#13A166';
    const LEAF_SHADOW = '#2D7B18';
    const TROPICAL = '#41AFBA';
    const IPANEMA = '#ECDA61';

    /** The waterline. */
    const SEA = 104;
    /** Feet go just UNDER it, so no hill ends on a ruled line. */
    const FOOT = SEA + 4;
    const GAP = 16;

    // ---- the sky ----
    // Pale, and warmest LOW where the sun is just up. The top stays cool mint so
    // the strip's upper half reads as morning air rather than as haze.
    this.riSkyTop = mixHex( MORNING, TROPICAL, 0.16 );
    this.riSkyMid = mixHex( MORNING, GARDEN, 0.14 );
    this.riSkyLow = mixHex( MORNING, IPANEMA, 0.30 );
    // The sun sits LEFT of centre and low, close to the water — first light, not
    // midday. Left because the Sugarloaf group stands right of centre and a sun
    // behind the hero would silhouette it, which is Roraima's picture, not this.
    // It sits ABOVE the range, not behind it. The first pass put it at y=96,
    // which is below every crown in the scene, so the disc and its whole glow
    // were painted and then covered by the hills that draw after them.
    this.riSun = { cx: 300, cy: 62, r: 13 };
    this.riSunFill = mixHex( IPANEMA, '#FFFFFF', 0.45 );
    this.riGlowFill = IPANEMA;

    /**
     * One morro: a rounded hump with a shoulder on one side, drawn with cubics
     * so the crown is round. Rio's hills are granite domes worn smooth — a
     * triangle would be Everest's peak and a flat top would be Roraima's tepui,
     * and both of those are already taken in this family.
     */
    const morro = ( cx: number, w: number, h: number, lean: number ): string => {
      const l = cx - w / 2, r = cx + w / 2;
      const top = FOOT - h;
      const px = cx + lean * w * 0.12;
      return `M${ f( l ) },${ f( FOOT ) }`
        + ` C${ f( l + w * 0.10 ) },${ f( FOOT - h * 0.52 ) } ${ f( px - w * 0.26 ) },${ f( top + h * 0.10 ) } ${ f( px ) },${ f( top ) }`
        + ` C${ f( px + w * 0.26 ) },${ f( top + h * 0.10 ) } ${ f( r - w * 0.10 ) },${ f( FOOT - h * 0.52 ) } ${ f( r ) },${ f( FOOT ) } Z`;
    };

    const hills: { d: string; fill: string }[] = [];

    // ---- the far range: the hills across the bay, hazy and low ----
    // Nine of them, spread over the inset span with a little jitter so the row
    // is a range rather than a comb. All PALE: this layer sits highest in the
    // glyph band and is the one a dark glyph is most likely to land on.
    const SPAN = this.W - 2 * GAP;
    // Crowns are kept BELOW the glyph band. Rendered in the preview's own tab
    // bar, the classic glyphs sit at roughly y 25..80 of this 150-unit strip and
    // the labels at y 95..120. So the ranges are capped just under y 78 and the
    // sky, not a hillside, is what every glyph but one stands on.
    //
    // Raised a step on the owner's call (2026-08-14: "tepeleri biraz daha yüksel
    // ve dağıt azıcık ... biraz yükselt ama çok değil"), from 12-18 / 18-26 to
    // 14-20 / 22-29, and spread wider — the jitter went 44 to 66 on the far range
    // and 60 to 84 on the middle one, with one more hill in each so the wider
    // scatter does not open a hole. That cap is what the raise is measured
    // against: another four units and the middle crowns reach the glyphs.
    const farFill = mixHex( this.riSkyMid, CANOPY, 0.30 );
    // SIX, not nine. Nine of them at these widths touched shoulder to shoulder
    // the whole way across and the range read as one caterpillar. A bay is water
    // WITH hills in it, so the gaps between them are half the picture.
    for ( let i = 0; i < 7; i++ ) {
      const cx = GAP + ( i + 0.5 ) * SPAN / 7 + ( fr( i * 0.618 ) - 0.5 ) * 66;
      const w = 150 + fr( i * 0.7548 ) * 80;
      const h = 14 + fr( i * 0.4142 + 0.3 ) * 6;
      hills.push( { d: morro( cx, w, h, fr( i * 0.271 ) - 0.5 ), fill: farFill } );
    }

    // ---- the middle range ----
    // NARROWER THAN THEY ARE TALL is the point of these. Rio's morros are
    // granite domes that stand up out of the water; the first pass drew them 210
    // to 340 wide against 26 to 40 tall and the strip came out as English
    // downland. Nothing about that reads as Rio, and no colour fixes it.
    const midFill = mixHex( farFill, CANOPY, 0.70 );
    for ( let i = 0; i < 6; i++ ) {
      const cx = GAP + ( i + 0.5 ) * SPAN / 6 + ( fr( i * 0.4142 + 0.7 ) - 0.5 ) * 84;
      const w = 110 + fr( i * 0.618 + 0.2 ) * 60;
      const h = 22 + fr( i * 0.7548 + 0.5 ) * 7;
      hills.push( { d: morro( cx, w, h, fr( i * 0.3819 ) - 0.5 ), fill: midFill } );
    }

    // ---- the Sugarloaf group, right of centre ----
    // Pão de Açúcar with MORRO DA URCA at its foot, which is how the pair is
    // always seen from the bay: the smaller dome in front and to the left, about
    // three fifths the height, overlapping so the two read as one massif rather
    // than as two separate hills that happen to be near each other.
    // LIGHTER than it wants to be. The front layer is the one thing in the scene
    // that reaches up among the glyphs, so it is held well above the tab colours
    // rather than painted the honest deep green of a foreground hillside.
    const nearFill = mixHex( CANOPY, LEAF_SHADOW, 0.16 );
    // The Sugarloaf is the TALLEST thing in the strip by a clear margin — 62
    // against the middle range's 26 to 40 — because it is the one silhouette a
    // viewer is meant to pick out, and a hero only a few units above its
    // neighbours is just another hill.
    // NO HERO IN THE STRIP. A Sugarloaf stood here for three passes — steep
    // flanks, broad crown, twice the height of anything near it — and the owner
    // cut it (2026-08-14: "en yüksek tepeyi sil ya ne lazım"). They are right
    // about what it was doing: the strip is 150px of bar seen behind five tab
    // glyphs, and one silhouette rising into that band asks to be looked at in a
    // place where nothing should. What is left is a range, which is what a bar
    // background is for.
    //
    // The near layer keeps three morros, all under 34 units, standing in the TAB
    // GAPS rather than behind the glyphs — LINE puts its five tabs at about x
    // 147, 441, 736, 1030 and 1325, so the gaps fall at 294, 589, 883 and 1178.
    hills.push( { d: morro( 807, 116, 33, 0.2 ), fill: nearFill } );
    hills.push( { d: morro( 294, 150, 28, -0.3 ), fill: nearFill } );
    hills.push( { d: morro( 1178, 128, 26, 0.25 ), fill: nearFill } );
    this.riHills = hills;

    // ---- the haze the range stands in ----
    // A soft band along the waterline: it swallows every foot, so nothing in the
    // scene ends on a hard line against the bay.
    const mist: string[] = [];
    mist.push( `M0,${ f( SEA - 7 ) } L${ this.W },${ f( SEA - 7 ) }`
      + ` L${ this.W },${ f( SEA + 3 ) } L0,${ f( SEA + 3 ) } Z` );
    this.riMistD = mist.join( ' ' );
    this.riMistFill = mixHex( MORNING, LAGOON, 0.55 );

    // ---- the bay ----
    // From the horizon down to the bar. The bottom stop is lineTabFill, read from
    // the ONE source, never a second copy of the hex ([[line-tab-fill-single-source]]).
    this.riWaterD = `M0,${ f( SEA ) } L${ this.W },${ f( SEA ) } L${ this.W },${ this.H } L0,${ this.H } Z`;
    this.riWaterTop = mixHex( LAGOON, TROPICAL, 0.40 );
    const FILL = lineTabFill( p.key, p.appearance );
    // The water darkens toward the near shore rather than running all the way to
    // the bar's colour: the skin's fill is a vivid grass green, and a lagoon that
    // fades into it directly comes out as a muddy teal band across the middle.
    this.riWaterLow = mixHex( mixHex( LAGOON, TROPICAL, 0.40 ), FILL, 0.55 );

    // ---- the near shore ----
    // This is what actually ends the strip on the bar's colour, and it is the
    // answer to a skin whose fill is a SATURATED green: the viewer stands on the
    // forested headland the bay is always photographed from, so the bottom of the
    // picture is allowed to be that green.
    //
    // Its top edge is a sine with a WHOLE number of periods across the strip, so
    // the left and right edges carry the identical height and the tile seam
    // cannot show a step. Three periods: enough to read as a rolling shore, few
    // enough that no single rise draws attention.
    // LOW. LINE draws the tab LABELS across roughly y 95..120 of this strip, and
    // the first pass put the shore's crest at 122 — so every label sat on the
    // boundary between pale water and vivid green and half of each word landed on
    // the green. At 132 the labels keep the water behind them and the shore is
    // the bottom band only, which is all it has to be: its job is to end the
    // strip on the bar's fill, not to be scenery.
    const SHORE = 132;
    const pts: string[] = [];
    for ( let x = 0; x <= this.W; x += 16 ) {
      const y = SHORE + Math.sin( 2 * Math.PI * 3 * x / this.W ) * 4;
      pts.push( `${ x === 0 ? 'M' : 'L' }${ f( x ) },${ f( y ) }` );
    }
    this.riShoreD = pts.join( ' ' )
      + ` L${ this.W },${ this.H } L0,${ this.H } Z`;
    this.riShoreTop = mixHex( FILL, LEAF_SHADOW, 0.22 );
    this.riShoreLow = FILL;

    // ---- the light on the water ----
    // Flat streaks, brightest under the sun and thinning away from it, so the bay
    // has a direction of light without a single gradient or blur in it. They stop
    // well short of both ends for the same reason the scene is inset.
    const shimmer: { d: string; o: number }[] = [];
    // TWELVE, soft. Twenty-two thin ones read as scratches on the strip rather
    // than as light — at this size a hairline is a defect, not a highlight.
    for ( let i = 0; i < 12; i++ ) {
      const t = fr( i * 0.618 + 0.11 );
      const y = SEA + 6 + fr( i * 0.4142 + 0.2 ) * 34;
      const w = 26 + fr( i * 0.7548 + 0.6 ) * 90;
      const cx = GAP + 40 + t * ( SPAN - 80 );
      // Distance from the sun, in strip widths: the near ones are the bright ones.
      const d = Math.min( 1, Math.abs( cx - this.riSun.cx ) / 620 );
      shimmer.push( {
        d: `M${ f( cx - w / 2 ) },${ f( y ) } L${ f( cx + w / 2 ) },${ f( y ) }`
          + ` L${ f( cx + w / 2 - 3 ) },${ f( y + 3.4 ) } L${ f( cx - w / 2 - 3 ) },${ f( y + 3.4 ) } Z`,
        o: +( 0.26 * ( 1 - d ) + 0.04 ).toFixed( 3 ),
      } );
    }
    this.riShimmer = shimmer;
    this.riShimmerFill = mixHex( '#FFFFFF', IPANEMA, 0.18 );
    // EMERALD and PALM_SHADE are the theme's, and are deliberately NOT in this
    // scene: the emerald is the chrome's colour (header, passcode zone) and a
    // saturated green in the strip would compete with the tab glyphs standing on
    // it. Kept named so the next pass sees the choice rather than the omission.
    void EMERALD; void PALM_SHADE;
  }

  /**
   * ISTANBUL — the Bosphorus at first light.
   *
   * The scene is a flat horizon: a cool sky, the warm dawn haze pooled DOWN on
   * the far shore, the old city's domes and minarets standing in it, and the
   * strait in front. The warmth is kept low deliberately (it is also what
   * separates this strip from Rio's draft, which is a pale dawn sky over a low
   * shore with turquoise water — same ingredients, opposite arrangement).
   *
   * VALUE, which is the part that decides whether the strip works. Istanbul's
   * LINE skin is LIGHT and its classic tab fill is #4AC3E0, luminance 0.459 —
   * that is the surface the theme's tab glyphs were chosen against. So the sky
   * across the whole icon band is held near that value (0.30 at the top of the
   * paint, ~0.40 through the middle) instead of being driven pale or dark. This
   * is Roraima's lesson applied forward: a strip that goes PALE where the
   * glyphs sit forces the active glyph to become the darkest mark on the bar,
   * which inverts the theme's own contrast logic. Everything with real value in
   * it — the haze, the silhouettes, the dark waterline — sits at y>=80.
   *
   * TILING (LINE left-aligns this strip and repeats it):
   *  - the sky, the haze band and the water are vertical gradients, uniform in
   *    x, so they cannot seam at all;
   *  - the waterfront is a sum of INTEGER-frequency sines, so its height AND
   *    its slope match at x=0 and x=W (equal heights alone leave a V-notch);
   *  - every drawn object — mosques, minarets, roofs, the moon, the ferries,
   *    the glints, the glow ellipse — lives well inside both edges, so the seam
   *    falls in open sky, flat shoreline and open water. Miami's precedent: a
   *    building sliced by the join reads as damage on a strip this short.
   *
   * The bottom row is exactly lineTabFill('istanbul'), read from the helper.
   */
  private buildIstanbul( p: Product ): void {
    const fr = ( v: number ) => v - Math.floor( v );
    const f = ( v: number ) => +v.toFixed( 1 );

    // The theme's own palette (base-themes/cities/istanbul-theme), by name.
    // Named rather than indexed off compactPalette: the compact set carries
    // only five of the twenty, and this scene needs the mauves and the copper.
    const SEA_GLASS = '#A3DDE5';
    const MIST = '#DBF0F1';
    const TILE = '#91D1DA';
    const IZNIK = '#12ACC1';
    const DEEP_BOS = '#087E8E';
    const MARMARA = '#0887B5';
    const MIDNIGHT = '#083A41';
    const ROSE = '#9C6E7C';
    const TULIP = '#C16979';
    const COPPER = '#B87958';
    const GOLDEN = '#C0AF43';
    const PARCHMENT = '#EFEAD0';

    const WATER = 117;
    this.isWaterTop = WATER;
    // The shore's mean top. The first build put it at 111.5, which left the
    // land 5.5px thick between the city's feet and the water — the owner read
    // it as a line, not as ground ("denizin üzerindeki toprak kısmı biraz daha
    // kalın olsun, şuan çizgi gibi"). Pulled up to 103 it is 14px of ground and
    // the city visibly stands ON something.
    //
    // The buildings had to come up with it: their feet were on WATER, so
    // thickening the band alone would simply have buried them. FOOT is where
    // they stand now — just under the wave, so the shore still swallows every
    // foot and nothing ends on a ruled line.
    const LAND = 103;
    const FOOT = LAND + 8;

    // ---- THE STRAIT ITSELF (owner 2026-08-14: "boğaz köprüsü iki yakayı
    // birleştirecek ve arkasında bina olmayacak, ortada boğaz akacak") ----
    //
    // The first build ran one unbroken city across the whole strip with water
    // below it, which is a waterfront, not a strait — and it left buildings
    // standing behind the bridge. A strait needs TWO SHORES with open water
    // between them, and the bridge has to be the thing that joins them.
    //
    // GAP is that open water. Nothing is built inside it and the shoreline dips
    // BELOW the waterline there, so no land shows and the strait runs from the
    // horizon straight down to the bottom of the strip. The dawn glow already
    // sits at x=640, inside the gap, so the light comes up the channel and the
    // bridge stands against it.
    const GAP_L = 470, GAP_R = 848;
    const GAP_FADE = 90;   // how far the headlands take to fall away
    /** How much the shore is pushed DOWN at x — 0 on the shores, deep inside
     *  the gap, eased across the headlands so they taper instead of ending on a
     *  cliff. */
    const gap = ( gx: number ): number => {
      const d = Math.min( gx - GAP_L, GAP_R - gx );
      if ( d <= -GAP_FADE ) { return 0; }
      if ( d >= 0 ) { return 34; }
      const t = ( d + GAP_FADE ) / GAP_FADE;
      return 34 * t * t * ( 3 - 2 * t );
    };
  
  /** Is x open water? Nothing may be built here — that is the owner's "no
     *  buildings behind the bridge", enforced rather than eyeballed. */
    const inGap = ( gx: number, pad = 0 ): boolean =>
      gx > GAP_L - GAP_FADE * 0.5 - pad && gx < GAP_R + GAP_FADE * 0.5 + pad;

    // ---- sky: a LIGHT blue, and lightest where the glyphs land ----
    // The first build held the icon band at luminance 0.29-0.42 on the argument
    // that the glyphs needed a mid-toned backdrop. That was written before the
    // theme's tab colours existed and it is now backwards (owner 2026-08-14:
    // "gökyüzü biraz daha açık mavi olursa sorun çözülür"). The glyphs came out
    // DARK — the classic strip's fill is the saturated cyan #4AC3E0 and clearing
    // 3:1 on it needs luminance <= 0.127 — so the band behind them has to be
    // light for exactly the same reason they are dark.
    this.isSkyTop = mixHex( IZNIK, TILE, 0.55 );
    this.isSkyMid = mixHex( TILE, SEA_GLASS, 0.6 );
    this.isSkyLow = mixHex( SEA_GLASS, MIST, 0.55 );

    // ---- the dawn haze, and the light coming up behind the shore ----
    // GOLD, not rose. Copper+Tulip over a turquoise sky averages to a dead grey
    // (measured 144,183,178 at y=100); the same copper carried toward Golden
    // Horn instead lands warm. It also frees the theme's mauves to be the CITY,
    // which is the colour story Istanbul actually has: gold light, mauve stone,
    // turquoise water.
    this.isHazeTop = 84;
    this.isHazeFill = mixHex( mixHex( COPPER, GOLDEN, 0.4 ), PARCHMENT, 0.33 );
    this.isGlowFill = mixHex( PARCHMENT, GOLDEN, 0.3 );
    // Left of centre: LINE tiles the strip left-aligned, so the left two thirds
    // is the part every phone shows whatever its width.
    this.isGlow = { cx: 640, cy: 120, rx: 500, ry: 58 };

    // NO MOON. The first build hung a waning crescent over the strait, which
    // put the scene at dawn; the owner's call is that this is DAYTIME and the
    // sky stays clean (2026-08-14: "menu background'da ay olmayacak, gündüz
    // vakti bu, gökyüzü temiz olsun"). The lune geometry that took two passes to
    // get right is deliberately NOT deleted from the component — isMoonD simply
    // goes unset and the template skips it — so another theme can take it.
    this.isMoonD = '';

    // ---- the old city ----
    // Three layers, far to near, each a step darker. The far mass is a run of
    // roofs with a couple of small domed complexes in it; the near row is the
    // authored mosque silhouettes; the waterfront is a low periodic wave that
    // swallows every foot, so nothing has to end on a straight ruled line.
    // The margin the TALL stuff keeps from the join, enforced rather than
    // trusted: a mosque or minaret clipped by the seam is the tiling failure
    // LINE rejects, and a column diff never catches it because both edges still
    // match. Everything authored below goes through inside().
    const EDGE = 34;
    const inside = ( v: number ) => Math.min( this.W - EDGE, Math.max( EDGE, v ) );

    // Istanbul is built on HILLS, and the first pass forgot them: a run of
    // equal-height blocks on one baseline read as a sea wall, not a city. The
    // roofline now rides a slow wave — integer frequencies again, so it is
    // periodic and the seam stays invisible — and skips blocks in places, so
    // the city clusters and thins the way a real skyline does.
    const hill = ( hx: number ) =>
      6.2 * Math.sin( ( 2 * Math.PI * hx ) / this.W + 0.6 )
      + 3.4 * Math.sin( ( 4 * Math.PI * hx ) / this.W + 2.2 )
      + 1.7 * Math.sin( ( 6 * Math.PI * hx ) / this.W + 4.4 );

    // SMALL houses, not blocks. The pass before this used 13-37px boxes and the
    // shore came out as a row of pink warehouses; the old city on these hills is
    // a fine-grained texture, and at phone scale that texture is what the eye
    // reads rather than any single roof. Roughly a third of them get a pitch,
    // which is the other half of what separates an Ottoman hillside from a
    // modern skyline (and from Miami's and Tokyo's, both of which are boxes).
    // These WRAP the join rather than stopping short of it. The tall stuff keeps
    // its margin — a minaret sliced by the seam reads as damage, which is why
    // Miami's skyline stops — but a 10px house does not, and a house drawn in
    // BOTH halves is not sliced at all: whatever crosses x=W is drawn a second
    // time a full width to the left, so tile n's right fragment and tile n+1's
    // left fragment reassemble into the whole building. hill() is periodic in W,
    // so the twin's height matches its own by construction. Leaving a bare gap
    // instead put ~50px of empty shoreline at every join.
    const roofParts: string[] = [];
    let x = 0;
    for ( let i = 0; x < this.W; i++ ) {
      const w = 8 + Math.round( fr( i * 0.6180339887 + 0.31 ) * 17 );
      const h = 3 + Math.round( fr( i * 0.7548776662 + 0.17 ) * 7 + ( hill( x ) + 11.3 ) * 0.55 );
      // A gap here and there: an unbroken picket is the same graveyard the
      // aurora design's pines had to be clustered out of.
      if ( fr( i * 0.5497004779 + 0.08 ) > 0.14 && !inGap( x + w / 2, w ) ) {
        const base = FOOT + gap( x + w / 2 );
        const top = base - h;
        const pitch = fr( i * 0.4142135624 + 0.53 ) < 0.34 ? Math.min( 5, w * 0.34 ) : 0;
        const house = ( ox: number ) =>
          `M${ f( ox ) },${ f( base ) } L${ f( ox ) },${ f( top ) }`
          + ( pitch ? ` L${ f( ox + w / 2 ) },${ f( top - pitch ) }` : '' )
          + ` L${ f( ox + w ) },${ f( top ) } L${ f( ox + w ) },${ f( base ) } Z`;
        roofParts.push( house( x ) );
        if ( x + w > this.W ) { roofParts.push( house( x - this.W ) ); }
      }
      x += w + 2 + Math.round( fr( i * 0.3819660112 + 0.6 ) * 8 );
    }
    this.isRoofD = roofParts.join( ' ' );

    // Far complexes + their minarets, placed in the gaps the near row leaves.
    const farParts: string[] = [];
    for ( const [ cx, w, h ] of [ [ 148, 96, 23 ], [ 1272, 104, 25 ] ] ) {
      farParts.push( this.istMosque( inside( cx ), w, h, FOOT ) );
    }
    for ( const [ mxx, mh ] of [ [ 100, 33 ], [ 200, 33 ], [ 1220, 35 ], [ 1326, 35 ], [ 1030, 28 ], [ 1418, 29 ] ] ) {
      farParts.push( this.istMinaret( inside( mxx ), FOOT, mh ) );
    }
    this.isFarD = farParts.join( ' ' );

    // The near row. The hero is left of centre for the same reason the glow is,
    // and every complex is kept short enough that its dome crown stays below
    // the glyph band's middle — only the minarets climb through it, and they
    // are needles.
    const nearParts: string[] = [];
    // The hero complex is on the LEFT shore, well clear of the channel, with a
    // second on the right so the two banks answer each other.
    for ( const [ cx, w, h ] of [ [ 258, 136, 33 ], [ 1104, 108, 27 ], [ 62, 82, 21 ] ] ) {
      nearParts.push( this.istMosque( inside( cx ), w, h, FOOT ) );
    }
    for ( const [ mxx, mh ] of [
      [ 184, 44 ], [ 332, 44 ], [ 208, 36 ], [ 308, 36 ],
      [ 1050, 36 ], [ 1158, 36 ], [ 1380, 28 ], [ 1440, 28 ],
    ] ) {
      nearParts.push( this.istMinaret( inside( mxx ), FOOT, mh ) );
    }
    this.isNearD = nearParts.join( ' ' );

    // The waterfront: periodic in value AND derivative, so the seam cannot show
    // a step or a kink. It rides just above the waterline and hides every foot.
    const N = 184;
    const shorePts: string[] = [];
    for ( let i = 0; i <= N; i++ ) {
      const sx = ( this.W * i ) / N;
      const sy = LAND + gap( sx )
        + 2.1 * Math.sin( ( 2 * Math.PI * 1 * sx ) / this.W + 0.8 )
        + 1.4 * Math.sin( ( 2 * Math.PI * 2 * sx ) / this.W + 3.1 )
        + 0.8 * Math.sin( ( 2 * Math.PI * 3 * sx ) / this.W + 1.6 );
      shorePts.push( `${ f( sx ) },${ f( sy ) }` );
    }
    this.isShoreD = `M${ shorePts[ 0 ] } L${ shorePts.slice( 1 ).join( ' L' ) }`
      + ` L${ this.W },${ this.H } L0,${ this.H } Z`;

    // FOUR steps back to front, every one of them DARKER than the sky it stands
    // against. The city is backlit by the dawn, so it is a silhouette; the pass
    // that mixed the far blocks halfway to Turquoise Tile put them at the sky's
    // own luminance and the shore dissolved into a pink smear. Aerial
    // perspective is carried by how far each layer is pulled toward Midnight
    // Strait, and warmth by how much of the haze is mixed back in.
    this.isRoofFill = mixHex( mixHex( ROSE, MIDNIGHT, 0.12 ), this.isHazeFill, 0.14 );
    this.isFarFill = mixHex( mixHex( ROSE, MIDNIGHT, 0.24 ), this.isHazeFill, 0.1 );
    this.isNearFill = mixHex( mixHex( ROSE, TULIP, 0.2 ), MIDNIGHT, 0.38 );
    this.isShoreFill = mixHex( ROSE, MIDNIGHT, 0.52 );

    // ---- the strait ----
    // Deep at the far side, brightening toward the viewer and landing on the
    // tab bar's own fill. The ramp is closed in the template at y=148, not 150:
    // the bottom ROW is y=149 and is sampled at its centre, so a ramp ending at
    // the edge never quite reaches its last stop and the exported row comes out
    // a digit off the bar's colour (Roraima's lesson).
    this.isWaterDeep = mixHex( DEEP_BOS, MARMARA, 0.25 );
    this.isWaterMid = mixHex( IZNIK, lineTabFill( p.key, p.appearance ), 0.3 );
    this.isWaterLow = lineTabFill( p.key, p.appearance );

    // Dawn light broken on the water, spreading and fading as it comes toward
    // us. Centred under the glow — a reflection that is not under its source
    // reads as litter on the water.
    this.isGlintFill = PARCHMENT;
    const glints: { x: number, y: number, w: number, h: number, o: number }[] = [];
    for ( let i = 0; i < 14; i++ ) {
      const t = i / 13;
      const gy = WATER + 3 + t * 22;
      const spread = 60 + t * 260;
      const n = 1 + Math.round( t * 2 );
      for ( let k = 0; k < n; k++ ) {
        const j = i * 3 + k;
        const gw = ( 16 + fr( j * 0.6180339887 + 0.2 ) * 52 ) * ( 1 - t * 0.35 );
        const gcx = this.isGlow.cx + ( fr( j * 0.7548776662 + 0.44 ) - 0.5 ) * spread;
        glints.push( { x: f( gcx - gw / 2 ), y: f( gy ), w: f( gw ), h: 1.5,
          o: +( 0.5 - t * 0.32 ).toFixed( 2 ) } );
      }
    }
    this.isGlints = glints;

    // ---- the Bosphorus bridge (owner 2026-08-14: "arada boğaz ve boğaz köprüsü
    // olacak, kırmızı beyaz köprü") ----
    //
    // This is the motif's right home. It was drawn first as a candidate for the
    // Home tab glyph and rejected there on a hard size gate: a suspension bridge
    // is a 6.5:1 subject and a tab glyph is a square box, so the span had to be
    // crushed until the cable read as a straight line. This strip is 1472x150,
    // about 9.8:1 — the one surface in the whole pack the shape actually fits.
    //
    // Placed at the middle of what the phone SHOWS, not the middle of the file:
    // LINE tiles this strip left-aligned, so a device only ever sees its left
    // portion and a span centred on x=736 would be off the screen. Centred here
    // on the dawn glow instead, so the towers stand against the light.
    //
    // NO vertical hangers, which is the icon's own simplification and is
    // load-bearing: a ladder of hangers at this scale aliases into a solid grey
    // band. The icon leaves them out and so does this.
    const bx1 = GAP_L, bx2 = GAP_R;      // one tower on each headland
    // The roadway sits high enough that BOTH ends come down onto land (owner
    // 2026-08-14: "köprü biraz daha yukarıda olacak ki sol ve sağ tarafı karaya
    // otursun"). It was at y=126 and the water starts at 117, so the span ended
    // in the sea at each shore — a bridge to nowhere. The headland under each
    // deck end measures y=110.8 (LAND 103 plus the gap easing at that x), so 112
    // puts the ends ON that ground with the towers still standing in the channel.
    const bDeck = 104;                    // the roadway, at shore height
    const bTop = 74;                       // tower tops
    const bFoot = WATER;                   // the legs stop EXACTLY at the waterline
    const legIn = 5.4;                    // half the gap between a tower's legs
    const bMid = ( GAP_L + GAP_R ) / 2;   // mid-span, where the camber peaks
    // 5.2 was invisible over a 558px span — the owner read the deck as still
    // dead straight ("arc şekli yapmadın köprüyü"). This is the rise at the
    // crown, and at 11 the roadway reads as an arch from across the room.
    const bCamber = 11;
    // EVERY subpath is wound the SAME way. The first version drew the legs
    // bottom-left -> bottom-right -> up and the crossbeams left -> right -> down,
    // i.e. opposite windings, and SVG's default nonzero rule cancels a clockwise
    // against a counter-clockwise where they overlap: the beams punched HOLES
    // through the legs and the tower came out in pieces (owner 2026-08-14:
    // "köprünün bacaklarını sürekli yap, şuan kesik kesik yaptın"). One helper
    // now emits every rectangle, so they cannot disagree.
    const bar = ( x0: number, y0: number, x1: number, y1: number ): string =>
      `M${ f( x0 ) },${ f( y0 ) } L${ f( x1 ) },${ f( y0 ) }`
      + ` L${ f( x1 ) },${ f( y1 ) } L${ f( x0 ) },${ f( y1 ) } Z`;
    const tower = ( tx: number ): string =>
      // Two legs, unbroken from the waterline to the top, joined by two beams.
      bar( tx - legIn - 2.2, bTop, tx - legIn + 2.2, bFoot )
      + ' ' + bar( tx + legIn - 2.2, bTop, tx + legIn + 2.2, bFoot )
      + ' ' + bar( tx - legIn - 2.2, bTop + 6, tx + legIn + 2.2, bTop + 9.4 )
      + ' ' + bar( tx - legIn - 2.2, bDeck - 9, tx + legIn + 2.2, bDeck - 5.8 );
    // The deck: one band, ends tucked behind the shore on the left and running
    // off past the second tower on the right, so it reads as crossing rather
    // than as a floating object.
    this.isBridgeD = tower( bx1 ) + ' ' + tower( bx2 )
      // The roadway CAMBERS — it rises to the middle of the span and falls away
      // to each shore (owner 2026-08-14: "köprü biraz daha kısa olsun ve daha
      // curve olacak yukarı doğru, şuan dümdüz çizdin"). A dead-level deck is
      // the thing that made it read as a bar laid across the water rather than
      // as a bridge carrying a road over it. Drawn as a band: one quadratic out
      // along the top edge and the same curve back a deck-thickness lower.
      + ` M${ f( bx1 - GAP_FADE ) },${ f( bDeck ) }`
      + ` Q${ f( bMid ) },${ f( bDeck - bCamber * 2 ) } ${ f( bx2 + GAP_FADE ) },${ f( bDeck ) }`
      + ` L${ f( bx2 + GAP_FADE ) },${ f( bDeck + 3.4 ) }`
      + ` Q${ f( bMid ) },${ f( bDeck - bCamber * 2 + 3.4 ) } ${ f( bx1 - GAP_FADE ) },${ f( bDeck + 3.4 ) } Z`;
    // The main cable: peaks at each tower top and sags between them, then runs
    // down to the deck outside them. Stroked, not filled — a cable is a line.
    this.isCableD =
      `M${ f( bx1 - GAP_FADE ) },${ f( bDeck ) } Q${ f( bx1 - 46 ) },${ f( bTop + 15 ) } ${ f( bx1 ) },${ f( bTop + 3 ) }`
      // The control point is set from the TOWER, not from the deck: a quadratic
      // passes at (P0 + 2C + P1)/4, so this lands the cable's lowest point about
      // 7 units above the roadway's crown. Tuned against the camber, because a
      // cable that dips through its own deck is the one error nobody forgives.
      + ` Q${ f( bMid ) },${ f( bTop + 22 ) } ${ f( bx2 ) },${ f( bTop + 3 ) }`
      + ` Q${ f( bx2 + 46 ) },${ f( bTop + 15 ) } ${ f( bx2 + GAP_FADE ) },${ f( bDeck ) }`;
    // Red and white, as the owner describes it and as the icon draws it: the
    // structure in the icon's own rust red, the roadway edged in a near-white so
    // the pair reads as the lit bridge rather than as one red mass.
    this.isBridgeFill = '#B5544F';
    this.isCableFill = '#9B4642';
    this.isDeckLine = '#FAFDFD';
    this.isDeckLineD = `M${ f( bx1 - GAP_FADE ) },${ f( bDeck + 0.8 ) }`
      + ` Q${ f( bMid ) },${ f( bDeck - bCamber * 2 + 0.8 ) } ${ f( bx2 + GAP_FADE ) },${ f( bDeck + 0.8 ) }`;

    // Two ferries crossing, the near one bigger. Both well inside the margins.
    const ferryFill = mixHex( MIDNIGHT, DEEP_BOS, 0.32 );
    this.isFerries = [
      { d: this.istFerry( 588, 132, 15 ), fill: ferryFill },
      { d: this.istFerry( 742, 141, 19 ), fill: ferryFill },
    ];
    // Foam is COOL and the sun's broken light is WARM — one fill for both was
    // what made the wakes read as more glints trailing the boat.
    this.isWakeFill = mixHex( SEA_GLASS, PARCHMENT, 0.25 );
    this.isWakes = [
      { x: 540, y: 133.4, w: 44, o: 0.3 },
      { x: 528, y: 135.2, w: 30, o: 0.2 },
      { x: 716, y: 142.2, w: 58, o: 0.3 },
      { x: 700, y: 144.4, w: 38, o: 0.2 },
    ];
  }

  private buildTepui( p: Product ): void {
    const fr = ( v: number ) => v - Math.floor( v );
    const f = ( v: number ) => +v.toFixed( 1 );

    // The strip ends on exactly what LINE paints below it (see lineTabFill).
    this.roGroundLow = lineTabFill( p.key, p.appearance );

    // A SUNSET, in the theme's own colours (owner 2026-08-13: "gün batımı hissi
    // olsun, temanın renklerini kullan", and the pass before this one "çok koyu
    // oldu"). The ramp runs the theme's icon: dusky violet at the top, plum
    // through the middle, amber along the horizon where the sun is going down.
    //
    // Dark at the TOP and warm at the BOTTOM is not a style choice, it is what
    // lets the tab glyphs work: they sit around y=30..110 where the sky is at
    // its deepest, so the light pink ON and light yellow OFF read against it,
    // while all the brightness sits low behind the ridges.
    const t: any = p.designTokens || {};
    const cp = ( p.compactPalette?.length ? p.compactPalette : p.palette ) || [];
    const eb = t.editorBackground || '#1E1A1B';
    const violet = cp[ 4 ] || '#8F78B7';
    const pink = cp[ 3 ] || '#ED8B8B';
    const coral = cp[ 1 ] || '#D17458';
    const gold = cp[ 2 ] || '#DDBE6D';

    // LIGHTER, and the violet carried toward an evening sunset PINK/RED (owner
    // 2026-08-14: "gökyüzü rengini biraz daha aç ve mor kısmı daha akşam gün
    // batımı pembesine kızılına çek"). The old ramp held a near-black dusk
    // through the icon band (luminance 0.089) because the glyphs were LIGHT then
    // and needed a dark backdrop. The idle glyph is now a deep violet, so the
    // logic inverts: the band lifts to about 0.165 and the glyph goes dark to
    // meet it.
    //
    // The top is no longer a violet mixed with black but a violet already more
    // than half way to Pink Quartz, and the middle carries that pink on toward
    // Coral Canyon — so the ramp reads violet-into-rose-into-fire rather than
    // black-into-plum.
    // VIOLET, and light (owner 2026-08-14: "gökyüzü mora çalacak"). The tab
    // glyphs are Pink Quartz and a faded, darker Pink Quartz — both in the same
    // warm hue — so a rose sky was competing with them rather than sitting behind
    // them. Turning the SKY toward the theme's Orchid Mist instead separates the
    // row by CHROMA and leaves the buttons in the theme's own pink, which is the
    // trade the other direction could not make: darkening the glyphs to win
    // against a warm sky kills their colour.
    //
    // Fire is kept for the horizon only, where the sun actually is and where no
    // glyph sits.
    this.roSkyTop = mixHex( eb, mixHex( violet, pink, 0.18 ), 0.92 );   // orchid dusk
    this.roSkyMid = mixHex( eb, mixHex( violet, pink, 0.48 ), 0.88 );   // violet into rose
    // The horizon runs RED, not orange (owner 2026-08-14: "turuncu gün batımı
    // kısmı da kızıla çalsın"). Coral Canyon carried toward the theme's own
    // Sunset Mesa rather than up into Savanna Gold, with only a little gold left
    // in it — the gold is what was reading as orange.
    this.roSkyLow = mixHex( mixHex( coral, '#CC654E', 0.55 ), gold, 0.18 );  // red on the horizon
    // Well over to the LEFT (owner 2026-08-13), in the gap the range leaves
    // between the mesas at 200 and 560 — and the left is also the part of the
    // strip a phone always shows, since LINE tiles it left-aligned.
    this.roSun = { cx: 375, cy: 124, r: 34 };
    this.roSunFill = mixHex( gold, '#FFF4D6', 0.4 );
    this.roGlowFill = mixHex( coral, gold, 0.4 );
    this.roCrownFill = mixHex( coral, '#FFFFFF', 0.35 );

    // A ridge that is periodic in VALUE and in DERIVATIVE: only whole numbers of
    // cycles across the strip's width, so the seam cannot show a step or a kink.
    const ridge = ( base: number, terms: [ number, number, number ][] ): string => {
      const N = 184;
      const at = ( x: number ) => base + terms.reduce(
        ( sum, [ k, amp, ph ] ) => sum + amp * Math.sin( ( 2 * Math.PI * k * x ) / this.W + ph ), 0 );
      const pts: string[] = [];
      for ( let i = 0; i <= N; i++ ) {
        const x = ( this.W * i ) / N;
        pts.push( `${ f( x ) },${ f( at( x ) ) }` );
      }
      return `M${ pts[ 0 ] } L${ pts.slice( 1 ).join( ' L' ) } L${ this.W },${ this.H } L0,${ this.H } Z`;
    };

    // Warm, not gold: mixed toward the theme's Dry Grass this haze came out
    // olive against the brown and read as a field of grass under the mountains.
    this.roMistFill = mixHex( eb, pink, 0.30 );
    this.roMistFill2 = mixHex( eb, coral, 0.22 );
    // The ground comes UP to meet them (owner 2026-08-13: "arkadaki dağlar
    // tabana oturmuş görünmüyor, toprak daha yükseğe çıkmalı"). Every mesa's
    // foot now sits inside the haze plane rather than above it, which is the
    // whole difference between a range standing on a floor and a row of shapes
    // floating over one.
    this.roMistD = ridge( 113, [ [ 1, 5.0, 0.4 ], [ 2, 3.0, 2.1 ], [ 3, 1.8, 4.0 ] ] );
    this.roMistD2 = ridge( 122, [ [ 1, 3.6, 2.6 ], [ 2, 2.4, 0.9 ], [ 4, 1.2, 3.3 ] ] );
    this.roGroundD = ridge( 131, [ [ 1, 3.2, 1.9 ], [ 2, 2.0, 0.3 ], [ 3, 1.1, 2.6 ] ] );
    this.roGroundTop = mixHex( eb, coral, 0.16 );

    // Three depth bands. Far rows are LIGHTER, lifted toward the sky the way
    // distance actually lifts them, so the range recedes instead of stacking.
    // PALE rock, never black. Two passes were too dark — silhouettes that read
    // as holes cut in the strip rather than as mountains (owner: "çok koyu",
    // then "daha soluk olacak"). Every band is the editor background carried a
    // long way toward the theme's Pink Quartz, so the range recedes through a
    // dusty rose scale that sits close to the sky it stands against. That
    // closeness is also what keeps the five tab glyphs legible over it.
    const band = [
      mixHex( eb, pink, 0.66 ),
      mixHex( eb, pink, 0.52 ),
      mixHex( eb, pink, 0.38 ),
    ];
    // Set BACK (owner 2026-08-13: "dağlar daha arkaplanda ve uzakta olsun"):
    // shorter, their feet higher up the strip and their tones close to the sky,
    // so the range sits behind the sunset instead of standing on top of it.
    //
    // Every mesa is kept fully inside the strip with a margin. One that runs off
    // an edge is the tiling failure LINE rejected Aurora for — half of it appears
    // at the seam with no other half to meet it, and a column diff never catches
    // it because both edges still match.
    // profile, x, width, height, foot, depth band
    const MESAS: [ number, number, number, number, number, number ][] = [
      [ 4, 1180, 130, 26, 117, 0 ],
      [ 0, 700, 190, 32, 118, 0 ],
      [ 2, 1030, 200, 40, 121, 1 ],
      [ 1, 200, 150, 44, 121, 1 ],
      [ 3, 560, 230, 46, 125, 2 ],
      [ 5, 1330, 180, 42, 125, 2 ],
    ];
    this.roWalls = MESAS.map( ( [ shape, x, w, h, foot, d ] ) => ( {
      d: this.roMesa( shape, x, w, h, foot ),
      fill: band[ d ],
    } ) );

    // MORICHE PALMS in the savanna at the foot of the range (owner 2026-08-13:
    // "roraima dağı çevresindeki ağaçlardan olsun, çam ağacı çizdin"). The first
    // pass drew rounded cones, which read as conifers — wrong continent. Roraima
    // stands over the Gran Sabana and the trees below it are fan palms, so it is
    // a slim leaning trunk under a crown of arching fronds.
    //
    // Kept well inside both edges so none is clipped at the seam.
    this.roTrees = [];
    const shade = mixHex( eb, coral, 0.2 );
    for ( let i = 0; i < 22 && this.roTrees.length < 9; i++ ) {
      const x = Math.round( 70 + fr( i * 0.6180339887 + 0.15 ) * ( this.W - 140 ) );
      if ( Math.abs( x - this.roSun.cx ) < this.roSun.r + 26 ) { continue; }
      const h = 15 + fr( i * 0.3819660112 + 0.4 ) * 11;
      const y = Math.round( 121 + fr( i * 0.7548776662 + 0.5 ) * 7 );
      const lean = ( fr( i * 0.5497004779 + 0.2 ) - 0.5 ) * h * 0.3;
      const pm = palmPaths( x, y, h, lean );
      this.roTrees.push( { trunk: pm.trunk, fronds: pm.fronds, fill: shade, sw: Math.max( 1.2, h * 0.075 ) } );
    }

    this.roStars = [];
    for ( let i = 0; i < 22; i++ ) {
      this.roStars.push( {
        cx: Math.round( 70 + fr( i * 0.6180339887 + 0.2 ) * ( this.W - 140 ) ),
        cy: Math.round( 26 + fr( i * 0.7548776662 + 0.4 ) * 34 ),
        r: +( 0.8 + fr( i * 0.3819660112 ) * 0.8 ).toFixed( 2 ),
        fill: [ '#FFF3DE', '#FFFFFF', mixHex( '#FFFFFF', violet, 0.35 ) ][ i % 3 ],
        o: +( 0.28 + fr( i * 0.45 ) * 0.38 ).toFixed( 2 ),
      } );
    }
  }

  private buildFall( p: Product ): void {
    const cp = ( p.compactPalette?.length ? p.compactPalette : p.palette ) || [];
    const ember = cp[ 1 ] || '#BE553E';
    const bark = cp[ 2 ] || '#B0633A';
    const gold = cp[ 3 ] || '#DEA51D';
    // Sky stays light all the way down; the SOIL band, not the sky, is what
    // carries the strip into the tab bar's fill (owner 2026-08-13: "menu
    // background altına toprak ekle ve doğru renge giden gradient yap").
    this.auSkyTopF = '#FDEBD3';
    this.auSkyLowF = '#FFF4E4';
    this.fallSoilTop = '#B57C42';      // earth at the ground line
    this.fallSoilLow = lineTabFill( p.key, p.appearance ); // ...landing exactly on the bar

    const fr = ( v: number ) => v - Math.floor( v );
    // Crown tints. Carried a step deeper than the provisional pass, because the
    // strip now lands on the skin's near-white tab bar rather than on a peach:
    // at the old mixes the canopies all but vanished against it. Still pale
    // enough that the glyph band above keeps its contrast.
    const tints = [
      mixHex( '#FFFFFF', gold, 0.55 ),
      mixHex( '#FFFFFF', ember, 0.46 ),
      mixHex( '#FFFFFF', bark, 0.42 ),
      mixHex( '#FFFFFF', gold, 0.42 ),
    ];
    this.fallTrees = [];
    // A MAPLE, not a generic tree (owner 2026-08-13: the round canopies this
    // replaces "look like normal trees"). The crown is a rosette of the theme's
    // own traced maple leaf — seven of them, stems meeting at the crown's centre
    // and blades fanned outward — so the canopy's outline is genuinely made of
    // maple lobes and sinuses. Nothing else at this size reads as maple: a
    // smooth blob is a blob whatever colour it is painted.
    //
    // THREE leaves, not a ring of them. A seven-leaf rosette was the first try
    // and it came out as a flower: at this size each leaf was ~10px, too small
    // for a single lobe to register, and perfect radial symmetry reads as a
    // bloom or a sun, never as a tree. Three big ones — one upright, two swung
    // out and shorter — keep every lobe legible and leave the outline lopsided,
    // which is what a crown looks like.
    //
    // The leaf is authored in the shared 120 box with its stem base at (58,109)
    // and its tip at (60,8), so each transform lands the STEM where the branches
    // fork and rotation swings the blade out from there.
    // Five leaves now, and BROAD rather than tall (owner 2026-08-13: bigger
    // trees). Height is the constrained axis — the five tab glyphs occupy about
    // y=30..110 and a crown that climbs into them buries the icons — so the
    // extra size goes sideways, into two more leaves swung out past horizontal.
    // That is also what a maple actually looks like: a wide, low, spreading
    // crown, not a cone.
    const CROWN: [ number, number ][] = [
      [ 0, 1 ], [ -55, 0.9 ], [ 57, 0.86 ], [ -108, 0.74 ], [ 110, 0.7 ],
    ];
    const tree = ( x: number, base: number, r: number, ti: number, o: number ) => {
      const f = ( v: number ) => +v.toFixed( 1 );
      const cy = base - r * 0.55;                 // crown centre
      const s = r / 101;                          // blade spans y 8..109 in the box
      // Each leaf is pinned by its MIDDLE, not by its stem, and pushed a little
      // way out along its own axis. That is what makes the trunk read as one
      // piece (owner 2026-08-13): pinned by the stem, all five stems fanned out
      // below the canopy like splayed fingers over the trunk. Pinned by the
      // middle they point the other way, cross each other inside the crown, and
      // vanish into its fill — leaving one trunk and one canopy.
      const leaves = CROWN.map( ( [ a, k ] ) => {
        const rad = ( a * Math.PI ) / 180;
        const off = r * k * 0.22;
        return `translate(${ f( x + Math.sin( rad ) * off ) },${ f( cy - Math.cos( rad ) * off ) })`
          + ` rotate(${ a }) scale(${ +( s * k ).toFixed( 4 ) }) translate(-58,-70)`;
      } );
      // ONE trunk: a single tapered piece from inside the soil up into the
      // canopy, wider at the root than at the crown.
      const w = Math.max( 2, r * 0.11 );
      this.fallTrees.push( {
        trunk: `M${ f( x - w ) },${ f( cy ) } L${ f( x + w ) },${ f( cy ) }`
          + ` L${ f( x + w * 1.55 ) },${ f( base + 8 ) } L${ f( x - w * 1.55 ) },${ f( base + 8 ) } Z`,
        leaves,
        fill: tints[ ti % tints.length ],
        o,
      } );
    };
    // SMALL and LOW. The first pass put canopies of r=24..34 with their bases at
    // y≈130, which centred them right in the glyph band (the icons occupy about
    // y=30..110) and the tab icons landed inside the trees. These sit below it.
    // Fewer and bigger. Twelve trees instead of fourteen, so the larger crowns
    // have room to spread without merging into a hedge, and sizes alternate
    // hard (front row / back row) rather than drifting, which is what stops a
    // treeline reading as a fence. Bases sit ON the soil line, so the trunks
    // are planted rather than floating over it.
    // Bigger again, and fewer, so the crowns have room. Composited against the
    // real exported tab glyphs the canopies are still the quieter shape: the
    // icons are solid rust and taupe at full strength, the crowns pale and
    // hazed, so they read as depth behind the icons rather than as clutter.
    const spots: [ number, number, number ][] = [
      [ 84, 122, 47 ], [ 208, 128, 31 ], [ 330, 121, 50 ], [ 452, 128, 32 ],
      [ 578, 122, 48 ], [ 700, 128, 30 ], [ 822, 121, 49 ], [ 944, 128, 32 ],
      [ 1068, 122, 47 ], [ 1190, 128, 31 ], [ 1312, 121, 50 ], [ 1424, 127, 34 ],
    ];
    spots.forEach( ( [ x, base, r ], i ) => tree( x, base, r, i, 0.9 - ( i % 3 ) * 0.06 ) );

    // A few of the theme's own maple leaves drifting where Everest puts sparkles.
    // Leaves stay high, in the empty part of the sky, and small — they are the
    // sparkle in Everest's scene, not the subject.
    this.fallLeaves = [];
    for ( let i = 0; i < 7; i++ ) {
      const x = Math.round( 120 + fr( i * 0.6180339887 + 0.2 ) * ( this.W - 240 ) );
      const y = Math.round( 28 + fr( i * 0.7548776662 + 0.4 ) * 26 );
      const rot = Math.round( 20 + ( fr( i * 0.5497004779 ) - 0.5 ) * 130 );
      const sc = +( 0.11 + fr( i * 0.3819660112 ) * 0.07 ).toFixed( 3 );
      this.fallLeaves.push( {
        transform: `translate(${ x },${ y }) rotate(${ rot }) scale(${ sc }) translate(-60,-60)`,
        // Light oranges lead here too, so the strip and the chat wallpaper drift
        // the same weather (owner 2026-08-13 asked for it on the wallpaper).
        fill: [ '#EFA83A', gold, '#F2B45C' ][ i % 3 ],
        o: +( 0.5 + fr( i * 0.45 ) * 0.3 ).toFixed( 2 ),
      } );
    }
    // A THICK rolling bank of soil (owner 2026-08-13), about a fifth of the
    // strip's height rather than the four-pixel lip it was. Edge heights match
    // at x=0 and x=W so the strip still tiles when LINE repeats it.
    this.fallGroundD = 'M 0 130 C 240 124 400 134 640 128 C 880 122 1080 133 1300 127'
      + ' C 1390 124 1440 128 1472 130 L 1472 150 L 0 150 Z';
  }

  // ---- Sequoia kawaii ("forest") scene ----

  /**
   * Sequoia's redwood forest, built on Tokyo's skyline logic: flat silhouettes
   * standing along the bottom of the strip, receding in three rows, over a sky
   * that lands on the tab bar's own colour so the strip melts into it.
   *
   * Same constraint as every other menu background: the five tab glyphs are
   * drawn over this, so the canopy stays LOW and the sky above it quiet. Rows
   * carry equal heights at x=0 and x=W and no tree touches an edge, so the strip
   * tiles seamlessly when LINE repeats it, and the hero sits in the left ~880px,
   * which is all a phone shows.
   */
  private buildForest( p: Product ): void {
    const t: any = p.designTokens || {};
    const cp = ( p.compactPalette?.length ? p.compactPalette : p.palette ) || [];
    const moss = cp[ 0 ] || '#405133';
    const leaf = cp[ 1 ] || '#6E9F56';
    // The sky lands on the TAB BAR's own fill, so the strip melts into the bar
    // instead of ending on a visible edge — the same rule Tokyo's skyline
    // follows. That fill is #198444: across Sakura, Tokyo, Everest and Aurora it
    // is always exactly the chat screen's bottom input bar, and Sequoia's is that
    // deep green. The turn to green is kept LOW (see the gradient stops in the
    // template) so the icon band stays pale and the glyphs keep their contrast.
    // A DAYLIGHT grove: the theme's own night forest was drawn for a dark skin
    // this theme turned out not to use.
    this.seqSkyTop = '#CFE0BE';
    this.seqSkyLow = '#198444';
    this.seqGroundFill = '#198444';

    // One conical redwood: three tiers over a short trunk, apexes softened.
    const tree = ( x: number, base: number, h: number ): string => {
      const w = h * 0.42;
      const tier = ( apex: number, halfW: number, foot: number ) =>
        `M${ ( x - halfW ).toFixed( 1 ) },${ foot.toFixed( 1 ) }`
        + ` L${ ( x - halfW * 0.16 ).toFixed( 1 ) },${ ( apex + h * 0.06 ).toFixed( 1 ) }`
        + ` Q${ x },${ ( apex - h * 0.04 ).toFixed( 1 ) } ${ ( x + halfW * 0.16 ).toFixed( 1 ) },${ ( apex + h * 0.06 ).toFixed( 1 ) }`
        + ` L${ ( x + halfW ).toFixed( 1 ) },${ foot.toFixed( 1 ) } Z`;
      const top = base - h;
      return [
        tier( top, w * 0.5, top + h * 0.34 ),
        tier( top + h * 0.2, w * 0.74, top + h * 0.62 ),
        tier( top + h * 0.42, w, base ),
        `M${ ( x - h * 0.05 ).toFixed( 1 ) },${ base } L${ ( x + h * 0.05 ).toFixed( 1 ) },${ base }`
        + ` L${ ( x + h * 0.05 ).toFixed( 1 ) },${ ( base + h * 0.1 ).toFixed( 1 ) }`
        + ` L${ ( x - h * 0.05 ).toFixed( 1 ) },${ ( base + h * 0.1 ).toFixed( 1 ) } Z`,
      ].join( ' ' );
    };
    const fr = ( v: number ) => v - Math.floor( v );
    // Three rows: hazy and short at the back, tall and dark at the front. Each
    // row starts well inside the strip and ends well inside it, so the seam is
    // always empty sky.
    const row = ( n: number, base: number, hMin: number, hMax: number, seed: number ) => {
      const out: string[] = [];
      for ( let i = 0; i < n; i++ ) {
        const j = fr( ( i + seed ) * 0.6180339887 );
        const x = 40 + ( ( this.W - 80 ) * ( i + 0.15 + j * 0.7 ) ) / n;
        out.push( tree( Math.round( x ), base, Math.round( hMin + fr( ( i + seed ) * 0.7548776662 ) * ( hMax - hMin ) ) ) );
      }
      return out.join( ' ' );
    };
    // PALE trees, not dark ones (owner 2026-08-13: the tab glyphs vanished
    // against them). This strip exists behind five icons, so every element on it
    // has to stay low-contrast — the same reason Everest's peaks and Sakura's
    // clouds are barely tinted. Aerial perspective still separates the rows, it
    // just happens inside a narrow, light range.
    this.seqRows = [
      { d: row( 15, 124, 34, 52, 0.3 ), fill: '#DCE9CE', o: 0.95 },
      { d: row( 12, 136, 46, 72, 1.7 ), fill: '#C6DCB2', o: 0.95 },
      { d: row( 9, 150, 62, 96, 2.9 ), fill: '#AECE96', o: 0.95 },
    ];

    // No stars, no fireflies: the skin turned this into daylight.
    this.seqStars = [];
    this.seqFireflies = [];
    // The floor the trunks stand on; edge heights match so the strip tiles.
    this.seqGroundD = 'M 0 138 C 220 132 380 142 620 136 C 860 130 1060 142 1290 136'
      + ' C 1390 133 1440 136 1472 138 L 1472 150 L 0 150 Z';
  }

  // ---- Aurora kawaii ("arctic") scene ----

  /**
   * Aurora's kawaii Arctic night, replacing the photoreal panorama it shared
   * with every other theme (owner 2026-08-12: it should be cute, like Sakura's,
   * Tokyo's and Everest's).
   *
   * The recipe those three share: FLAT shapes, thick rounded strokes, no
   * atmospheric gradients. So the northern lights become chunky ribbons drawn as
   * fat round-capped strokes with a lighter inner stroke, over a round moon and
   * plain dot stars, on rolling snow hills.
   *
   * The front hill is painted in the SAME near-black as the tab bar behind it,
   * so only its white snow crest draws the silhouette and the strip melts into
   * the bar instead of ending on a visible edge.
   *
   * Tiling-safe (LINE repeats this strip): both hills carry equal heights at
   * x=0 and x=W, and no ribbon, star, pine or moon comes near an edge. The hero
   * sits in the left ~880px, which is all a phone shows (see buildEverest).
   */
  /** Which arctic experiment to draw. Flipped while trying layouts out. */
  static ARCTIC_VARIANT: 'a' | 'b' | 'c' = 'c';

  private buildArctic( p: Product ): void {
    const t: any = p.designTokens || {};
    const cp = ( p.compactPalette?.length ? p.compactPalette : p.palette ) || [];
    const blue = cp[ 1 ] || t.primaryColor || '#549EFF';
    const teal = cp[ 2 ] || t.secondaryColor || '#1ECFB6';
    const lav = cp[ 4 ] || '#BAA5FF';

    this.auSkyTop = '#122A36';
    this.auSkyLow = '#131313';        // == the tab fill, so the bottom blends away
    this.auHillBackFill = '#1B3743';
    // The front hill IS the tab bar's own fill, from the one shared source.
    // It used to be hard-coded '#16232B', a dark teal, while this scene's own
    // docblock and field default both said near-black — so Aurora shipped with
    // the very seam the docblock promises it avoids.
    this.auHillFrontFill = lineTabFill( p.key, p.appearance );
    // No moon (owner 2026-08-12): it read as a second subject competing with the
    // lights, and the tab glyphs already crowd the strip.
    this.auCraters = [];

    // Ribbons: [x0,y0, x1,y1, x2,y2, x3,y3] cubic through the sky, thick round
    // stroke + a lighter core. Three in the phone zone, two as wide-screen filler.
    // ---- the lights ----
    // HORIZONTAL bands, layered and overlapping (owner 2026-08-12: anything that
    // stands upright reads as wrong for an aurora on this strip). Four earlier
    // layouts were tried and dropped — thin arcs on the horizon (a coloured
    // smear), many small rays and tall curtains standing between the tab glyphs
    // (both read as upright objects, not light), and one thick sweep (a wedge).
    //
    // Each band is a centre line y = base + amp * sin(2πkx/W + phase) with an
    // INTEGER k, so its height and thickness are identical at x=0 and x=W and
    // the strip tiles seamlessly however many times LINE repeats it.
    //
    // They are drawn blurred and at low opacity ON PURPOSE: the five tab glyphs
    // sit on top of this strip, so the bands have to pass behind them without
    // ever putting an icon on a solid block of colour.
    // Integer frequencies alone are NOT enough to tile, and this is what LINE
    // rejected the theme for (2026-08-13, "both sides of the image line up to
    // tile seamlessly > i_11"). The maths was periodic, but the GEOMETRY still
    // stopped dead at x=0 and x=W, and these bands are blurred: with nothing
    // beyond either end for the blur to carry, both edges faded toward
    // transparent. Each edge matched the other, so a column-by-column check
    // called it seamless — yet tiled, the pair made a dark vertical stripe every
    // 1472px, which is exactly what the reviewer saw.
    //
    // So the band is drawn PAST both ends and the viewBox clips it back. The
    // sine has period W, so the overhang is a true continuation of the opposite
    // edge — the blur now samples real light on both sides and stays flat right
    // across the seam.
    const PAD = 48;                              // 8 x the filter's stdDeviation
    const band = ( base: number, amp: number, k: number, phase: number,
                   th: number, color: string, o: number ) => {
      const span = this.W + 2 * PAD;
      const N = Math.round( 240 * ( span / this.W ) );
      const f = ( v: number ) => +v.toFixed( 1 );
      const top: string[] = [];
      const bot: string[] = [];
      for ( let i = 0; i <= N; i++ ) {
        const x = -PAD + ( span * i ) / N;
        const y = base + amp * Math.sin( ( 2 * Math.PI * k * x ) / this.W + phase );
        // A second integer-frequency wave swells the band instead of leaving it
        // a uniform stripe.
        const h = ( th / 2 ) * ( 1 + 0.34 * Math.sin( ( 2 * Math.PI * ( k + 1 ) * x ) / this.W + phase * 1.7 ) );
        top.push( `${ f( x ) },${ f( y - h ) }` );
        bot.push( `${ f( x ) },${ f( y + h ) }` );
      }
      return { d: `M${ top.join( ' L' ) } L${ bot.reverse().join( ' L' ) } Z`, fill: color, o };
    };
    this.auBands = [
      // A wide, faint wash UNDER the others. The crisp bands are sine waves, so
      // wherever their phases happen to part company the sheet tears open a dark
      // hole; this fills the body of the sheet without adding structure of its
      // own (owner 2026-08-12, twice: "ortada boşluk var").
      band( 66, 7, 1, 1.6, 52, blue, 0.12 ),
      band( 44, 12, 1, 0.4, 20, teal, 0.24 ),
      band( 58, 15, 1, 2.3, 24, lav, 0.2 ),
      // Fifth band, phase-shifted so it sits low exactly where the k=1 pair
      // rides high (owner 2026-08-12: the sheet tore open in the middle there).
      band( 68, 13, 1, 4.0, 24, blue, 0.24 ),
      band( 78, 11, 2, 1.1, 22, blue, 0.28 ),
      band( 90, 9, 2, 3.4, 15, teal, 0.15 ),
    ];
    this.auCurtains = [];
    this.auCurtainColors = [];
    this.auGlowD = '';
    this.auRibbons = [];
    this.auBandD = '';
    this.auBandHaloD = '';
    this.auBandStops = [];

    // Plain dot stars, kept clear of the ribbons' band and the hills.
    const fr = ( v: number ) => v - Math.floor( v );
    this.auStars = [];
    for ( let i = 0; i < 26; i++ ) {
      const x = Math.round( 40 + fr( i * 0.6180339887 + 0.17 ) * ( this.W - 80 ) );
      const y = Math.round( 24 + fr( i * 0.7548776662 + 0.29 ) * 72 );
      this.auStars.push( {
        x, y,
        r: +( 1.1 + fr( i * 0.3819660112 + 0.5 ) * 1.5 ).toFixed( 1 ),
        o: +( 0.4 + fr( i * 0.4501836112 ) * 0.5 ).toFixed( 2 ),
      } );
    }

    // A few four-point sparkles for the cute register (same shape Everest uses).
    const spark = ( x: number, y: number, r: number, fill: string, o: number ) => ( {
      fill, o,
      d: `M ${ x } ${ y - r } L ${ x + r * 0.28 } ${ y - r * 0.28 } L ${ x + r } ${ y }`
        + ` L ${ x + r * 0.28 } ${ y + r * 0.28 } L ${ x } ${ y + r }`
        + ` L ${ x - r * 0.28 } ${ y + r * 0.28 } L ${ x - r } ${ y } L ${ x - r * 0.28 } ${ y - r * 0.28 } Z`,
    } );
    this.auSparkles = [
      spark( 232, 34, 4, '#FFFFFF', 0.95 ),
      spark( 418, 92, 3.2, mixHex( '#FFFFFF', teal, 0.4 ), 0.85 ),
      spark( 668, 32, 4.4, '#FFFFFF', 0.9 ),
      spark( 900, 74, 3, mixHex( '#FFFFFF', lav, 0.4 ), 0.8 ),
      spark( 1180, 36, 3.4, '#FFFFFF', 0.75 ),
    ];

    // Rolling hills. Their end HEIGHTS matched before, which is why they looked
    // safe, but their end SLOPES did not — the curve arrived at x=W falling and
    // left x=0 climbing, so tiling put a soft V-notch in the ridge at every
    // seam. Equal heights are only half of tiling.
    //
    // Sums of integer-frequency sines instead: periodic in value AND derivative,
    // so the ridge runs through the seam without a kink. Same rolling shape and
    // the same heights it had, just honestly periodic.
    const ridge = ( base: number, terms: [ number, number, number ][] ): string => {
      const N = 184;
      const pts: string[] = [];
      for ( let i = 0; i <= N; i++ ) {
        const x = ( this.W * i ) / N;
        let y = base;
        for ( const [ amp, k, phase ] of terms ) {
          y += amp * Math.sin( ( 2 * Math.PI * k * x ) / this.W + phase );
        }
        pts.push( `${ +x.toFixed( 1 ) },${ +y.toFixed( 1 ) }` );
      }
      return `M${ pts.join( ' L' ) }`;
    };
    this.auHillBackD = ridge( 113, [ [ 6, 2, 0.6 ], [ 3.4, 3, 2.1 ] ] )
      + ' L 1472 150 L 0 150 Z';
    const frontTop = ridge( 130, [ [ 4.6, 2, 2.4 ], [ 2.4, 3, 0.3 ] ] );
    this.auHillFrontD = frontTop + ' L 1472 150 L 0 150 Z';
    // No snow on the ground at all (owner 2026-08-12): the tab icons sit on this
    // band, and every pale detail down here is one more thing for a glyph to
    // collide with. The hills stay as bare silhouettes.
    this.auCaps = [];
    this.auCrestD = frontTop;

    // Tiny flat pines on the back hill, never near the seam.
    const pine = ( x: number, y: number, s: number, o: number ) => ( {
      o,
      d: `M ${ x } ${ y - s * 2.1 } L ${ x + s * 0.78 } ${ y - s * 0.9 } L ${ x + s * 0.34 } ${ y - s * 0.9 }`
        + ` L ${ x + s * 1.05 } ${ y } L ${ x - s * 1.05 } ${ y }`
        + ` L ${ x - s * 0.34 } ${ y - s * 0.9 } L ${ x - s * 0.78 } ${ y - s * 0.9 } Z`,
    } );
    this.auPines = [
      pine( 264, 121, 8, 0.95 ), pine( 292, 123, 6, 0.9 ),
      pine( 592, 119, 7.5, 0.95 ), pine( 618, 122, 5.5, 0.88 ),
      pine( 1044, 120, 7, 0.85 ), pine( 1300, 122, 6, 0.8 ),
    ];
  }

  /**
   * A cubic Bézier [x0,y0,x1,y1,x2,y2,x3,y3] turned into a closed band whose
   * half-width follows sin(pi*t)^`ease` — nothing at either tip, full thickness
   * between. `ease` below 1 flattens the profile: at 0.32 the band is near full
   * width for most of its length and pinches only at the very ends, which is
   * what keeps a strip-wide sweep reading as one THICK line rather than a wedge.
   * (The realistic aurora design has its own `taperedBand`, tapering along x.)
   */
  private curveBand( p: number[], w: number, ease = 1 ): string {
    const N = 26;
    const at = ( t: number ) => {
      const u = 1 - t;
      const b = [ u * u * u, 3 * u * u * t, 3 * u * t * t, t * t * t ];
      return [
        b[ 0 ] * p[ 0 ] + b[ 1 ] * p[ 2 ] + b[ 2 ] * p[ 4 ] + b[ 3 ] * p[ 6 ],
        b[ 0 ] * p[ 1 ] + b[ 1 ] * p[ 3 ] + b[ 2 ] * p[ 5 ] + b[ 3 ] * p[ 7 ],
      ];
    };
    const f = ( v: number ) => +v.toFixed( 1 );
    const top: string[] = [];
    const bot: string[] = [];
    for ( let i = 0; i <= N; i++ ) {
      const t = i / N;
      const [ x, y ] = at( t );
      const [ nx, ny ] = at( Math.min( 1, t + 0.001 ) );
      const dx = nx - x, dy = ny - y;
      const len = Math.hypot( dx, dy ) || 1;
      // Normal to the curve, scaled by the taper.
      const h = ( w / 2 ) * Math.pow( Math.sin( Math.PI * t ), ease );
      const ox = ( -dy / len ) * h, oy = ( dx / len ) * h;
      top.push( `${ f( x + ox ) },${ f( y + oy ) }` );
      bot.push( `${ f( x - ox ) },${ f( y - oy ) }` );
    }
    return `M${ top[ 0 ] } L${ top.slice( 1 ).join( ' L' ) } L${ bot.reverse().join( ' L' ) } Z`;
  }

  // ---- Everest kawaii scene ----

  /**
   * Everest's cloudy Japanese-cute panorama: pastel icy sky, a soft peach
   * sunrise band, a Fuji-silhouette Everest (concave slopes, rounded summit)
   * with a scalloped snowcap, hazy sister peaks, puffy cluster clouds, tiny
   * four-point sparkles, and a rolling snow plain grounding the tab icons.
   * Tiling-safe: the ground's edge heights match, the sunrise band's edge
   * stops are equal, and no cloud/sparkle/peak touches the strip edges.
   */
  private buildEverest( p: Product ): void {
    const t: any = p.designTokens || {};
    const blue = t.primaryColor || '#1A6D9F';
    const orange = '#ED7E5A'; // everest's sunset orange (10-swatch palette)

    this.evSkyTop = mixHex( '#FFFFFF', blue, 0.05 );
    this.evSkyLow = mixHex( '#FFFFFF', blue, 0.16 );
    this.evPeach = mixHex( '#FFFFFF', orange, 0.42 );
    this.evMainFill = mixHex( '#FFFFFF', blue, 0.48 );
    const haze = mixHex( '#FFFFFF', blue, 0.26 );
    this.evGroundFill = mixHex( '#FFFFFF', blue, 0.09 );
    // Everest's snowfield ends ON the tab fill. Derived from the theme's blue
    // it came out #ECF3F8 against a pure-white bar: a faint blue-grey band
    // across the bottom of the phone, and on the very theme the rule cites as
    // its evidence.
    this.evPlainFill = lineTabFill( p.key, p.appearance );

    // LINE renders this strip LEFT-ALIGNED and scales it UP to the bar height
    // (~137%), so a phone shows only the left ~880px (~60%) — calibrated from
    // a real iPhone capture of the live aurora (green crest 33% / blue crest
    // 84% of the screen both solve to ~880 visible px). Hero composition
    // therefore lives in the LEFT ~880px; everything beyond is wide-screen
    // filler.
    // Main Everest (centre ~470 = mid-phone): concave Fuji slopes, round summit.
    this.evMainD = 'M 300 136 C 374 126 410 88 444 50 Q 470 36 496 50 C 530 88 566 126 640 136 Z';
    // Scalloped snowcap hugging the summit.
    this.evCapD = 'M 424 66 C 435 49 456 39 470 38 C 484 39 505 49 516 66'
      + ' Q 506 74 497 65 Q 488 77 476 67 Q 464 79 452 66 Q 441 75 424 66 Z';

    // Hazy sister peaks (left + right of the hero, inside the phone window).
    this.evPeaks = [
      { d: 'M 60 136 C 112 129 140 106 168 80 Q 180 72 192 80 C 220 106 248 129 300 136 Z', fill: haze, o: 0.85 },
      { d: 'M 696 136 C 748 130 772 110 798 88 Q 810 80 822 88 C 848 110 872 130 924 136 Z', fill: haze, o: 0.7 },
    ];

    // Puffy kawaii clouds: overlapping circles + a flat belly, drawn as a
    // GROUP with one opacity so the overlaps never band. `front` clouds float
    // over the peaks, the rest drift behind.
    const cloud = ( cx: number, cy: number, s: number, o: number, front: boolean ) => ( {
      o, front,
      circles: [
        { cx: cx - 1.30 * s, cy: cy + 0.05 * s, r: 0.55 * s },
        { cx: cx - 0.35 * s, cy: cy - 0.45 * s, r: 0.75 * s },
        { cx: cx + 0.60 * s, cy: cy - 0.12 * s, r: 0.62 * s },
        { cx: cx + 1.35 * s, cy: cy + 0.12 * s, r: 0.45 * s },
        { cx, cy: cy + 0.28 * s, r: 0.95 * s },
      ],
    } );
    this.evClouds = [
      // behind the peaks (phone zone + wide-screen fillers past x=880)
      cloud( 120, 58, 11, 0.80, false ),
      cloud( 860, 52, 12, 0.80, false ),
      cloud( 1150, 60, 12, 0.75, false ),
      cloud( 1360, 70, 9, 0.70, false ),
      // in front (hero zone; one drifts across the hero's right slope)
      cloud( 250, 90, 10, 0.95, true ),
      cloud( 615, 60, 14, 0.95, true ),
      cloud( 530, 102, 9, 0.95, true ),
      cloud( 1000, 90, 9, 0.85, true ),
    ];

    // Tiny four-point sparkles (soft peach + white).
    const spark = ( x: number, y: number, r: number, fill: string, o: number ) => ( {
      fill, o,
      d: `M ${ x } ${ y - r } L ${ x + r * 0.28 } ${ y - r * 0.28 } L ${ x + r } ${ y }`
        + ` L ${ x + r * 0.28 } ${ y + r * 0.28 } L ${ x } ${ y + r }`
        + ` L ${ x - r * 0.28 } ${ y + r * 0.28 } L ${ x - r } ${ y } L ${ x - r * 0.28 } ${ y - r * 0.28 } Z`,
    } );
    this.evSparkles = [
      spark( 180, 44, 3.5, this.evPeach, 0.9 ),
      spark( 345, 66, 3, '#FFFFFF', 0.95 ),
      spark( 560, 40, 4.5, this.evPeach, 0.85 ),
      spark( 740, 68, 3, '#FFFFFF', 0.9 ),
      spark( 960, 48, 3, this.evPeach, 0.75 ),
      spark( 1240, 80, 2.5, '#FFFFFF', 0.7 ),
    ];

    // Rolling snow plain (edge heights match at x=0 / x=W for seamless tiling).
    this.evGroundD = 'M 0 129 C 180 122 320 133 500 128 C 700 122 850 134 1040 128'
      + ' C 1220 123 1360 133 1472 129 L 1472 150 L 0 150 Z';
  }

  // ---- Sakura kawaii scene ----

  /**
   * Sakura's cherry-blossom park: a pastel pink sky over a soft rose horizon
   * glow, puffy white clouds, a big blossoming sakura tree (a layered pink
   * puff canopy with peeking leaves + accent dots on a warm trunk), hazy
   * blossom bushes on a gentle green hill, drifting petals, a few five-petal
   * blossoms and tiny sparkles. The hero tree sits in the LEFT ~880px window
   * LINE actually shows (see the calibration note in buildEverest); everything
   * past that is wide-screen filler. Tiling-safe: the hill's edge heights
   * match at x=0 / x=W, the horizon band's edge stops are equal, and no petal
   * or blossom touches the strip edges.
   */
  private buildSakura( p: Product ): void {
    const t: any = p.designTokens || {};
    const cp = ( p.compactPalette?.length ? p.compactPalette : p.palette ) || [];
    const pinkSoft   = t.primaryColor   || '#E898B4';
    const green      = t.secondaryColor || '#B4D592';
    const greenDeep  = t.tertiaryColor  || green;
    const lightPink  = t.surface        || '#F8DBE6';
    const brightPink = cp[ 0 ]          || '#FFC9DC';
    const rose       = cp[ 1 ]          || '#CB6B91';

    this.skTop        = '#FFFFFF'; // lighter sky up top (owner 2026-07-29)
    this.skLow        = '#FFE0E7'; // == the classic tab-fill below, so the strip blends into it (owner 2026-07-29)
    this.skHaze       = pinkSoft;
    // No green ground (owner 2026-07-29: on the phone the green hill meets the
    // pink color skin below the strip in a hard line). The strip is now an
    // all-pink sky that fades to a soft pink at the bottom, so it blends into
    // the color skin. Ground/plain kept pink in case anything references them.
    this.skGroundFill = mixHex( '#FFFFFF', pinkSoft, 0.16 );
    this.skPlainFill  = mixHex( '#FFFFFF', pinkSoft, 0.20 );
    this.skTrunkFill  = mixHex( '#7A5340', rose, 0.12 );
    this.skLeafFill   = mixHex( '#FFFFFF', green, 0.18 );
    this.skDotFill    = rose;

    const blue = cp[ 3 ] || '#69A2BD';
    const pA = mixHex( '#FFFFFF', pinkSoft, 0.45 );
    const pB = lightPink;
    const pC = mixHex( '#FFFFFF', brightPink, 0.40 );
    const pGreen = mixHex( '#FFFFFF', green, 0.42 );   // theme green leaf accent
    const pBlue = mixHex( '#FFFFFF', blue, 0.40 );      // theme blue accent
    const sparkPink = mixHex( '#FFFFFF', rose, 0.30 );

    // No ground hill (owner 2026-07-29: the bottom must be the same colour as
    // the tab-fill it blends into). The sky gradient's own bottom (skLow =
    // #FFE0E7) IS the strip's bottom, so it meets the fill seamlessly.
    this.skGroundD = '';

    // No hero tree (owner 2026-07-29: the big cherry tree spoiled the strip; it
    // moves to the Home tab glyph instead). Menu-bg is now the soft pastel sky +
    // hills + bushes + drifting petals/blossoms only.
    this.skTrunkD = '';
    this.skBranchD = '';
    this.skCanopy = [];
    this.skLeaves = [];
    this.skDots = [];

    const clump = ( cs: [ number, number, number ][], fill: string, o: number ) =>
      ( { circles: cs.map( ( [ cx, cy, r ] ) => ( { cx, cy, r } ) ), fill, o } );
    // Soft GREEN foliage clumps (theme green) sitting low in the scene — colour
    // variety without a hard bottom band (owner 2026-07-29: add some blue/green,
    // don't leave it all one pink).
    // Pinkish bushes (owner 2026-07-29: back to the pink tone).
    const bushFill = mixHex( '#FFFFFF', pinkSoft, 0.50 );
    const bushFillDeep = mixHex( '#FFFFFF', rose, 0.62 );
    this.skBushes = [
      clump( [ [ 110, 108, 15 ], [ 134, 100, 18 ], [ 158, 108, 14 ] ], bushFill, 0.70 ),
      clump( [ [ 730, 110, 14 ], [ 755, 101, 17 ], [ 780, 110, 13 ] ], bushFillDeep, 0.60 ),
      clump( [ [ 1150, 108, 16 ], [ 1178, 99, 19 ], [ 1206, 108, 15 ] ], bushFill, 0.62 ),
      clump( [ [ 1350, 110, 13 ], [ 1371, 103, 15 ], [ 1392, 110, 12 ] ], bushFillDeep, 0.56 ),
    ];

    const cloud = ( cx: number, cy: number, s: number, o: number ) =>
      ( { o, circles: [
        { cx: cx - 1.30 * s, cy: cy + 0.05 * s, r: 0.55 * s },
        { cx: cx - 0.35 * s, cy: cy - 0.45 * s, r: 0.75 * s },
        { cx: cx + 0.60 * s, cy: cy - 0.12 * s, r: 0.62 * s },
        { cx: cx + 1.35 * s, cy: cy + 0.12 * s, r: 0.45 * s },
        { cx, cy: cy + 0.28 * s, r: 0.95 * s },
      ] } );
    this.skClouds = [ cloud( 215, 44, 9, 0.85 ), cloud( 660, 40, 11, 0.82 ), cloud( 1080, 46, 10, 0.70 ) ];

    const petal = ( x: number, y: number, rot: number, sc: number, fill: string, o: number ) =>
      ( { transform: `translate(${ x },${ y }) rotate(${ rot }) scale(${ sc })`, fill, o } );
    this.skPetals = [
      petal( 95, 44, 25, 1.2, pA, 0.85 ), petal( 150, 88, -15, 1.0, pB, 0.70 ), petal( 250, 60, 40, 1.1, pC, 0.80 ),
      petal( 305, 104, 10, 0.9, pA, 0.65 ), petal( 520, 50, -30, 1.2, pB, 0.85 ), petal( 560, 96, 55, 1.0, pC, 0.70 ),
      petal( 612, 72, 15, 1.1, pA, 0.80 ), petal( 700, 110, -20, 0.9, pB, 0.60 ), petal( 788, 58, 35, 1.1, pC, 0.80 ),
      petal( 842, 92, 5, 1.0, pA, 0.72 ),
      // wide-screen fillers past the visible phone window (~880px)
      petal( 960, 54, -25, 1.1, pB, 0.70 ), petal( 1080, 100, 30, 1.0, pC, 0.65 ), petal( 1185, 64, 45, 1.1, pA, 0.75 ),
      petal( 1300, 90, -10, 1.0, pB, 0.65 ), petal( 1405, 52, 20, 1.1, pC, 0.70 ),
    ];

    const blossom = ( cx: number, cy: number, s: number, o: number, petalFill: string ) =>
      ( { cx, cy, s, o, petalFill, centerFill: sparkPink,
          petalTransforms: [ 0, 1, 2, 3, 4 ].map( k => `rotate(${ k * 72 }) translate(0,-1.6)` ) } );
    this.skBlossoms = [
      blossom( 190, 54, 2.0, 0.95, pC ), blossom( 720, 48, 1.7, 0.90, pB ), blossom( 560, 102, 1.5, 0.85, pA ),
      blossom( 1000, 58, 1.7, 0.75, pC ), blossom( 1255, 96, 1.5, 0.70, pB ),
    ];

    const spark = ( x: number, y: number, r: number, fill: string, o: number ) => ( {
      fill, o,
      d: `M ${ x } ${ y - r } L ${ x + r * 0.28 } ${ y - r * 0.28 } L ${ x + r } ${ y }`
        + ` L ${ x + r * 0.28 } ${ y + r * 0.28 } L ${ x } ${ y + r }`
        + ` L ${ x - r * 0.28 } ${ y + r * 0.28 } L ${ x - r } ${ y } L ${ x - r * 0.28 } ${ y - r * 0.28 } Z`,
    } );
    this.skSparkles = [
      spark( 340, 44, 3, '#FFFFFF', 0.90 ), spark( 500, 80, 2.5, sparkPink, 0.80 ), spark( 662, 60, 3, '#FFFFFF', 0.85 ),
      spark( 150, 64, 2.5, sparkPink, 0.70 ),
      spark( 1120, 52, 3, '#FFFFFF', 0.70 ), spark( 1320, 74, 2.5, sparkPink, 0.65 ),
    ];
  }

  /**
   * Tokyo: a SIMPLE, cohesive twilight scene (like Sakura's soft sky) — a
   * plum→lavender sky whose bottom == the tab-fill, a soft moon, a few gentle
   * stars, and one subtle skyline silhouette whose bases blend into the lavender
   * (masked in the template) plus a sprinkle of soft window lights. No hero
   * tower — the strip is a quiet part of the whole, not a busy illustration.
   */
  private buildTokyo( p: Product ): void {
    const H = this.H, W = this.W;
    const plum = '#51385E';
    const skin = '#886399';                             // the color skin (== chat input bar)
    this.tkSkyTop = mixHex( skin, plum, 0.38 );         // softer plum up top (owner 2026-08-07: lighter sky)
    this.tkSkyLow = skin;                                // bottom == the color skin, so it matches
    // The buildings take a BLUER plum than the sky/ground (owner 2026-08-05,
    // ~20° toward blue) so the skyline leans cooler in step with the tab
    // buttons, while the sky / ground / tab-fill (skin) stay put.
    const plumBld = '#3D385F';                          // plum, hue nudged toward blue
    // Owner 2026-08-07: lighter + more washed again — the skyline should recede
    // behind the tab glyphs rather than compete with them.
    const sil = mixHex( skin, plumBld, 0.36 );          // silhouette (fainter / more washed)
    const haze = mixHex( skin, plumBld, 0.24 );         // hazier / distant
    const fr = ( v: number ) => v - Math.floor( v );

    this.tkMoon = { cx: 250, cy: 44, r: 18 };
    this.tkStars = [];
    for ( let i = 0; i < 15; i++ ) {
      this.tkStars.push( {
        x: Math.round( 20 + fr( i * 0.6180339887 + 0.17 ) * ( W - 40 ) ),
        y: Math.round( 12 + fr( i * 0.7548776662 + 0.40 ) * 46 ),
        r: +( 0.6 + fr( i * 0.8736592734 + 0.20 ) * 0.8 ).toFixed( 2 ),
        o: +( 0.30 + fr( i * 0.5497004779 + 0.60 ) * 0.45 ).toFixed( 2 ),
      } );
    }

    // Buildings sit on a GROUND line above the strip bottom, so a strip of
    // ground/road shows below them (owner: don't sit them flush on the bottom).
    const base = H - 26;                              // ground line (~124)
    this.tkGroundTop = mixHex( skin, plum, 0.22 );

    // ---- rectangular silhouettes ONLY (no triangles / spires): a flat top, a
    // small rooftop box, or a setback top. Thin, tall, varied. ----
    const boxTop = ( x: number, w: number, y: number ) =>
      `M${ x } ${ base } L${ x } ${ y } L${ x + w } ${ y } L${ x + w } ${ base } Z`;
    const roofTop = ( x: number, w: number, y: number ) => {
      const rw = Math.max( 5, w * 0.34 ), rx = x + ( w - rw ) / 2, rh = 6;
      return boxTop( x, w, y ) + ` M${ rx } ${ y } L${ rx } ${ y - rh } L${ rx + rw } ${ y - rh } L${ rx + rw } ${ y } Z`;
    };
    const stepTop = ( x: number, w: number, y: number ) => {
      const uw = w * 0.58, ux = x + ( w - uw ) / 2, sy = y + 14;
      return `M${ x } ${ base } L${ x } ${ sy } L${ ux } ${ sy } L${ ux } ${ y } `
        + `L${ ux + uw } ${ y } L${ ux + uw } ${ sy } L${ x + w } ${ sy } L${ x + w } ${ base } Z`;
    };

    const warm = '#FFD489', cool = '#CDB6FF';
    const addWindows = ( x: number, w: number, y: number, seed: number ) => {
      const cols = Math.max( 2, Math.floor( w / 9 ) );
      const rows = Math.max( 2, Math.floor( ( base - y ) / 12 ) );
      for ( let r = 0; r < rows; r++ ) {
        for ( let c = 0; c < cols; c++ ) {
          if ( fr( ( seed * 17 + r * 5 + c * 3 ) * 0.6180339887 + 0.2 ) < 0.5 ) { continue; }
          const wx = x + 4 + ( cols > 1 ? c * ( w - 8 ) / ( cols - 1 ) : 0 );
          const wy = y + 7 + r * 11;
          if ( wy > base - 6 ) { continue; }
          this.tkWindows.push( {
            x: Math.round( wx ), y: Math.round( wy ), s: 1.8,
            fill: fr( ( seed + r + c ) * 0.37 + 0.5 ) > 0.28 ? warm : cool,
            o: +( 0.45 + fr( ( seed * 5 + c ) * 0.27 ) * 0.35 ).toFixed( 2 ),
          } );
        }
      }
    };

    // Varied, non-repeating skyline: thin/tall towers, cycling widths + varied
    // heights + three flat top styles; hazy ones sit back without windows.
    this.tkBuildings = [];
    this.tkWindows = [];
    const wSet = [ 20, 28, 22, 34, 18, 30, 24, 38, 26, 32 ];
    let x = -8, i = 0;
    while ( x < W + 20 ) {
      const w = wSet[ i % wSet.length ] + Math.round( fr( i * 0.317 + 0.2 ) * 6 );
      const topY = 44 + Math.round( fr( i * 0.6180339887 + 0.15 ) * 56 );
      const backish = fr( i * 0.31 + 0.4 ) > 0.70;
      const typ = Math.floor( fr( i * 0.7548776662 + 0.33 ) * 3 );
      const d = typ === 1 ? roofTop( x, w, topY ) : typ === 2 ? stepTop( x, w, topY ) : boxTop( x, w, topY );
      this.tkBuildings.push( { d, fill: backish ? haze : sil } );
      if ( !backish ) { addWindows( x, w, topY, i ); }
      x += w + 5 + Math.round( fr( i * 0.53 + 0.4 ) * 8 );
      i++;
    }
  }

  // ---- Paris ("paris") scene ----

  /** A rectangle, always wound the same way (right, down, left, up). Every part
   *  of every Paris monument goes through this: SVG's nonzero rule cancels a
   *  clockwise subpath against a counter-clockwise one where they overlap, and a
   *  cathedral built from bars drawn in two directions comes out with its
   *  towers punched through (the lesson Istanbul's bridge legs taught). */
  private parBar( x0: number, y0: number, x1: number, y1: number ): string {
    const f = ( v: number ) => +v.toFixed( 1 );
    return `M${ f( x0 ) },${ f( y0 ) } L${ f( x1 ) },${ f( y0 ) }`
      + ` L${ f( x1 ) },${ f( y1 ) } L${ f( x0 ) },${ f( y1 ) } Z`;
  }

  /**
   * The Eiffel Tower, as ONE simple polygon.
   *
   * The outline runs up the outer left, across the top, down the outer right,
   * then back in under the first platform and up over the arch between the legs
   * — so the gap between the legs is a NOTCH in a single closed loop, never a
   * second subpath subtracted from a solid body. At 33px tall that gap is the
   * whole silhouette: without it the tower reads as a pylon.
   *
   * The taper is a power curve rather than the real structure's exponential,
   * because an exponential at this size collapses the top two thirds into a
   * hairline; 2.05 keeps the shaft visible while the flare at the feet still
   * reads as Eiffel rather than as a spire.
   */
  private parEiffel( cx: number, foot: number, h: number ): string {
    const f = ( v: number ) => +v.toFixed( 1 );
    const ow = ( t: number ) => 1.5 + 12.0 * Math.pow( 1 - t, 2.05 );
    const T_ARCH = 0.30;                       // the first platform, in t
    const iw = ( t: number ) => t >= T_ARCH ? 0
      : ow( t ) * 0.60 * Math.sqrt( 1 - Math.pow( t / T_ARCH, 2 ) );

    const N = 24, M = 10;
    const left: string[] = [], right: string[] = [];
    for ( let i = 0; i <= N; i++ ) {
      const t = i / N, y = foot - t * h;
      left.push( `${ f( cx - ow( t ) ) },${ f( y ) }` );
      right.push( `${ f( cx + ow( t ) ) },${ f( y ) }` );
    }
    const inL: string[] = [], inR: string[] = [];
    for ( let i = 0; i <= M; i++ ) {
      const t = ( i / M ) * T_ARCH, y = foot - t * h;
      inL.push( `${ f( cx - iw( t ) ) },${ f( y ) }` );
      inR.push( `${ f( cx + iw( t ) ) },${ f( y ) }` );
    }
    let d = 'M' + left.join( ' L' )
      + ' L' + right.reverse().join( ' L' )
      + ' L' + inR.join( ' L' )
      + ' L' + inL.reverse().join( ' L' ) + ' Z';

    // The two platforms and the antenna. Bars, so they cannot disagree with the
    // outline's winding.
    const p1 = foot - T_ARCH * h, p2 = foot - 0.63 * h;
    d += ' ' + this.parBar( cx - ow( T_ARCH ) - 2.4, p1 - 1.3, cx + ow( T_ARCH ) + 2.4, p1 + 1.3 );
    d += ' ' + this.parBar( cx - ow( 0.63 ) - 1.8, p2 - 1.1, cx + ow( 0.63 ) + 1.8, p2 + 1.1 );
    d += ' ' + this.parBar( cx - 0.7, foot - h - 4, cx + 0.7, foot - h );
    return d;
  }

  /**
   * The Arc de Triomphe: a block with the great arch cut into it as a notch in
   * the same single loop, plus the attic cornice that gives it its proportion.
   */
  private parArc( cx: number, foot: number, w: number, h: number ): string {
    const f = ( v: number ) => +v.toFixed( 1 );
    const iw = w * 0.19;                       // half the opening
    const spring = foot - h * 0.40;
    const crown = foot - h * 0.66;
    const K = 10;
    const arch: string[] = [];
    for ( let i = 0; i <= K; i++ ) {
      const a = Math.PI * ( i / K );           // right springing -> left springing
      arch.push( `${ f( cx + iw * Math.cos( a ) ) },${ f( spring - ( spring - crown ) * Math.sin( a ) ) }` );
    }
    const d = `M${ f( cx - w / 2 ) },${ f( foot ) } L${ f( cx - w / 2 ) },${ f( foot - h ) }`
      + ` L${ f( cx + w / 2 ) },${ f( foot - h ) } L${ f( cx + w / 2 ) },${ f( foot ) }`
      + ` L${ f( cx + iw ) },${ f( foot ) } L${ arch.join( ' L' ) } L${ f( cx - iw ) },${ f( foot ) } Z`;
    return d + ' ' + this.parBar( cx - w / 2 - 1.8, foot - h, cx + w / 2 + 1.8, foot - h + 2.4 );
  }

  /** Notre-Dame: the twin west towers, the facade between them, the nave running
   *  off to the right and the flèche standing on it. */
  private parNotreDame( cx: number, foot: number, h: number ): string {
    const f = ( v: number ) => +v.toFixed( 1 );
    const tw = h * 0.37;                       // tower width
    const gap = h * 0.28;
    const d: string[] = [];
    d.push( this.parBar( cx - gap / 2 - tw, foot - h, cx - gap / 2, foot ) );
    d.push( this.parBar( cx + gap / 2, foot - h, cx + gap / 2 + tw, foot ) );
    d.push( this.parBar( cx - gap / 2 - tw, foot - h * 0.62, cx + gap / 2 + tw, foot ) );
    // The nave, one step lower, and the apse rounding off its far end.
    const nx0 = cx + gap / 2 + tw, nx1 = cx + h * 1.42, ny = foot - h * 0.52;
    d.push( this.parBar( nx0, ny, nx1, foot ) );
    d.push( `M${ f( nx1 ) },${ f( ny ) } A ${ f( h * 0.16 ) } ${ f( h * 0.26 ) } 0 0 1 ${ f( nx1 ) },${ f( foot ) } Z` );
    // The flèche. A needle, and needles are the one thing that survives this
    // scale — the roof crest it stands on would not.
    const sx = cx + h * 0.78;
    d.push( `M${ f( sx - 1.6 ) },${ f( ny ) } L${ f( sx ) },${ f( ny - h * 0.62 ) } L${ f( sx + 1.6 ) },${ f( ny ) } Z` );
    return d.join( ' ' );
  }

  /** A domed monument (the Panthéon / Invalides silhouette), answering the
   *  cathedral from the right end of the bank: drum, dome, lantern. */
  private parDome( cx: number, foot: number, w: number, h: number ): string {
    const f = ( v: number ) => +v.toFixed( 1 );
    const drum = foot - h * 0.44;
    const r = w * 0.40;
    return this.parBar( cx - w / 2, drum, cx + w / 2, foot )
      // Left springing over the top to the right one: sweep 1 arcs OVER, and the
      // closing Z runs back along the base, so this winds clockwise like parBar
      // and the dome unions with its drum instead of cancelling against it.
      + ` M${ f( cx - r ) },${ f( drum ) } A ${ f( r ) } ${ f( h * 0.46 ) } 0 0 1 ${ f( cx + r ) },${ f( drum ) } Z`
      + ' ' + this.parBar( cx - 1.5, foot - h, cx + 1.5, foot - h * 0.86 );
  }

  /**
   * PARIS — the Seine at night, over a bridge arcade.
   *
   * The scene is the theme's own icon turned around: the icon looks UP the
   * avenue at the Arc, the Eiffel Tower and Notre-Dame over a sunset; this looks
   * across the river at the same three, at night, from the water. What replaces
   * the icon's avenue is the ARCADE — a run of twelve arched bays, which is
   * Pont Neuf's count and, much more to the point, the one motif that solves a
   * 1472px strip that has to TILE. A repeating rhythm whose period is exactly
   * W/12 continues across the join with nothing to fudge, and the join itself
   * falls down the centre line of a pier.
   *
   * DARK, and evenly so. The five tab glyphs sit over roughly y=30..110, and on
   * this strip that band holds only sky, silhouette and the parapet's dark mass
   * — the brightest passage, the city's glow pooled on the river, is at y≈118,
   * below the glyphs, showing through the bays. The six parapet lamps are the
   * one deliberate exception and they are 4px across.
   *
   * TILING: the sky, the haze and the water are vertical gradients (uniform in
   * x, so they cannot seam); the glow ellipses stop well short of both edges;
   * the roofline is a sum of INTEGER-frequency sines and any roof crossing x=W
   * is drawn a second time a full width to the left, so tile n's right fragment
   * and tile n+1's left fragment reassemble into one building; the monuments
   * keep a wide margin from the join, because a spire sliced by the seam reads
   * as damage and a column diff never catches it.
   *
   * PLACEHOLDER SKIN: see the field block — every skin colour here is Roraima's,
   * to be resampled once the owner picks Paris's LINE base theme.
   */
  private buildParis( p: Product ): void {
    const fr = ( v: number ) => v - Math.floor( v );
    const f = ( v: number ) => +v.toFixed( 1 );

    // LIGHT, and built out of HAUSSMANN TERRACES (owner 2026-08-14: "bi kere
    // light mode'a uygun olsun ... ben haussman evleri istiyorum ve daha büyük
    // olsun onlar. sonra istersen alta köprü olur").
    //
    // This replaces a night river with the Eiffel standing over it, which was
    // drawn while Paris still carried a PLACEHOLDER dark skin. The real skin
    // turned out to be a pale pink, and on a light skin a night strip is not
    // merely off-key: the classic tab strip has to END on the chat input bar,
    // #FDBCC8, so a dark scene shows a hard seam under the bar on the phone.
    // Two themes in this family have already been rejected or re-uploaded over
    // exactly that edge.
    //
    // The tower is deliberately absent as well. It carries the Home tab and the
    // passcode dots, and a strip that repeats the hero leaves the scene nothing
    // of its own to say. The bridge that was the draft's subject is demoted to a
    // low arcade at the waterline, because arches at the size the draft wanted
    // ate the height the buildings needed.
    const IVORY = '#E6D7DD', SMOKE = '#C79FAE', MAUVE = '#5D3F49';
    const ROSEWOOD = '#6A3C4D', PEACH = '#F3B7A9', CHERRY = '#DF7583';
    const GOLD = '#F1C970', SEINE = '#5E7BB3', TWILIGHT = '#87A1D3';

    // ---- the sky: pale all the way, warming only at the horizon ----
    this.paSkyTop = mixHex( '#FFF6F6', PEACH, 0.10 );
    this.paSkyMid = mixHex( '#FFF1F2', PEACH, 0.26 );
    this.paSkyLow = mixHex( PEACH, CHERRY, 0.22 );
    this.paStarFill = mixHex( TWILIGHT, IVORY, 0.55 );
    this.paStars = [];
    this.paGlow = { cx: 736, cy: 300, rx: 1, ry: 1 };
    this.paHeroGlow = { cx: 736, cy: 300, rx: 1, ry: 1 };
    this.paGlowFill = GOLD;
    this.paLampFill = GOLD;
    this.paLamps = [];
    this.paRefl = [];

    // ---- the terrace ----
    // EIGHT bays across 1472, so the run is periodic by construction and the
    // seam cannot show half a building. Each bay is one Haussmann block: a stone
    // face, a cornice under the eaves, the continuous second-floor balcony that
    // is the whole tell of the style, a mansard roof above it, chimney stacks on
    // the ridge.
    // A CLEAR MARGIN at both ends (owner 2026-08-14: "sol ve sağda biraz boşluk
    // bırak, bitti gitti", then "çok boşluk oldu, azıcık yeter"). Everything with
    // an edge — the terrace, the trees, the arcade — is inset, so what meets at
    // the tile seam is sky above and flat water below, and those tile by
    // construction. Sixteen units is enough: measured, the join comes out at
    // 7/255 against an adjacent-column p99 of 86, i.e. inside the sky gradient's
    // own dithering.
    //
    // This replaces a pass that tried to make the join reconstruct itself by
    // drawing a twin of the edge trees a full width over. It very nearly worked
    // and was not worth the machinery: a strip whose scene simply does not reach
    // the edge cannot have a seam at all.
    const GAP = 16;
    const SPAN = this.W - 2 * GAP;
    const BAYS = 8;
    const BW = SPAN / BAYS;
    // SMALLER than the first pass (owner 2026-08-14): the terrace ran 72 to 120
    // and read as a wall of housing given the strip's whole height. Pulled in to
    // 78-110 it is still the subject and it leaves the room the bridge needs.
    const EAVE = 78;
    const GROUND = 110;
    const face: string[] = [];
    const roof: string[] = [];
    const trim: string[] = [];
    const glass: string[] = [];

    for ( let b = 0; b < BAYS; b++ ) {
      const x0 = GAP + b * BW;
      // ALL ONE HEIGHT (owner 2026-08-14: "binaların yükseklikleri aynı
      // olsun"). The first pass staggered the eaves by up to 12 units to give
      // the terrace a skyline, and the owner is right that it reads better flat:
      // a Haussmann street IS uniform — the cornice line running unbroken down
      // the block is the thing Haussmann's rules actually produced, and the
      // stagger was making it look like a row of separate houses.
      const eave = EAVE;
      const ridge = eave - 19;
      const l = x0 + 3, r = x0 + BW - 3;

      face.push( `M${ f( l ) },${ f( GROUND ) } L${ f( l ) },${ f( eave ) }`
        + ` L${ f( r ) },${ f( eave ) } L${ f( r ) },${ f( GROUND ) } Z` );
      // The mansard: a steep lower slope and a shallow cap. That break is what
      // says Paris roof rather than "a triangle on a box".
      roof.push( `M${ f( l - 3 ) },${ f( eave ) } L${ f( l + 10 ) },${ f( ridge + 5 ) }`
        + ` L${ f( r - 10 ) },${ f( ridge + 5 ) } L${ f( r + 3 ) },${ f( eave ) } Z` );
      roof.push( `M${ f( l + 10 ) },${ f( ridge + 5 ) } L${ f( l + 18 ) },${ f( ridge ) }`
        + ` L${ f( r - 18 ) },${ f( ridge ) } L${ f( r - 10 ) },${ f( ridge + 5 ) } Z` );
      // chimney stacks on the ridge
      const nSt = 2 + Math.round( fr( b * 0.7548776662 ) * 2 );
      for ( let k = 0; k < nSt; k++ ) {
        const cx = l + 24 + ( k + 0.5 ) * ( BW - 48 ) / nSt;
        const ch = 7 + Math.round( fr( b * 0.4142 + k * 0.31 ) * 5 );
        roof.push( `M${ f( cx - 3.6 ) },${ f( ridge ) } L${ f( cx - 3.6 ) },${ f( ridge - ch ) }`
          + ` L${ f( cx + 3.6 ) },${ f( ridge - ch ) } L${ f( cx + 3.6 ) },${ f( ridge ) } Z` );
      }
      // cornice, then the balcony band one floor down
      trim.push( `M${ f( l - 2 ) },${ f( eave ) } L${ f( r + 2 ) },${ f( eave ) }`
        + ` L${ f( r + 2 ) },${ f( eave + 3.4 ) } L${ f( l - 2 ) },${ f( eave + 3.4 ) } Z` );
      const balc = eave + 18;
      trim.push( `M${ f( l - 2 ) },${ f( balc ) } L${ f( r + 2 ) },${ f( balc ) }`
        + ` L${ f( r + 2 ) },${ f( balc + 3 ) } L${ f( l - 2 ) },${ f( balc + 3 ) } Z` );
      // three floors of tall windows
      const cols = 5;
      for ( let fl = 0; fl < 3; fl++ ) {
        const wy = eave + 8 + fl * 15;
        for ( let c = 0; c < cols; c++ ) {
          const wx = l + 10 + ( c + 0.5 ) * ( BW - 20 ) / cols - 4;
          const wh = fl === 2 ? 8 : 10;
          glass.push( `M${ f( wx ) },${ f( wy ) } L${ f( wx ) },${ f( wy + wh ) }`
            + ` L${ f( wx + 8 ) },${ f( wy + wh ) } L${ f( wx + 8 ) },${ f( wy ) }`
            + ` Q${ f( wx + 4 ) },${ f( wy - 3.2 ) } ${ f( wx ) },${ f( wy ) } Z` );
        }
      }
    }
    this.paCityD = face.join( ' ' );
    // BEIGE, not white (owner: "binalar bembeyaz olmuş, azıcık bej olsun").
    // Haussmann facades are limestone and read warm; a white terrace on a pink
    // sky also loses its own edges. Champagne Gold carried most of the way to
    // white gives the stone without turning it yellow.
    this.paCityFill = mixHex( '#FFFFFF', GOLD, 0.17 );
    this.paRoofD = roof.join( ' ' );
    this.paRoofFill = mixHex( MAUVE, SMOKE, 0.46 );
    this.paHeroD = trim.join( ' ' );
    this.paHeroFill = mixHex( SMOKE, MAUVE, 0.32 );
    this.paWindowD = glass.join( ' ' );

    // PLANE TREES along the quay, light green (owner: "aralara biraz yeşil ağaç
    // at, açık yeşil"). They stand ON THE JOINS between bays, which is the one
    // place a tree can go without covering a facade — and it is also where the
    // real ones are, since a Paris street tree sits at the kerb in front of the
    // party wall. Two per bay join, one behind and smaller, so the row has depth
    // rather than reading as a stamped hedge.
    const trees: string[] = [];
    const trunks: string[] = [];
    // The joins are b = 0 .. BAYS-1, and the b=0 pair is ALSO drawn a full width
    // to the right. That twin is what makes the tile seamless: without it the
    // strip's right edge carried the trees of one join and its left edge the
    // trees of another, so the two did not line up and a column diff put the
    // seam at 85/255 across rows 83-89 — exactly the crown band. Drawing the
    // pair twice means tile n's right edge and tile n+1's left edge are the same
    // two trees, reassembled across the join.
    // INTERIOR joins only: b = 1 .. BAYS-1. The outer two would sit in the
    // margin and put half a tree on the seam.
    for ( let b = 1; b < BAYS; b++ ) {
      const jx = GAP + b * BW;
      for ( let k = 0; k < 2; k++ ) {
        const tx = jx + ( k === 0 ? -9 : 9 );
        const scale = k === 0 ? 1 : 0.82;
        const crownR = 11 * scale;
        const top = GROUND - 26 * scale;
        // The trunk is its OWN path and its own colour (owner: "ağaçların
        // gövdeleri kahverengi olsun"). Sharing the crown's fill made the trunks
        // pink, which read as a stem rather than as a tree.
        trunks.push( `M${ f( tx - 1.6 * scale ) },${ f( GROUND ) } L${ f( tx - 1.6 * scale ) },${ f( top + crownR * 0.9 ) }`
          + ` L${ f( tx + 1.6 * scale ) },${ f( top + crownR * 0.9 ) } L${ f( tx + 1.6 * scale ) },${ f( GROUND ) } Z` );
        // crown: three overlapping lobes, not one circle — a plane tree is
        // pollarded and its head is lumpy
        trees.push( `M${ f( tx ) },${ f( top - crownR * 0.55 ) }`
          + ` C${ f( tx + crownR ) },${ f( top - crownR * 0.6 ) } ${ f( tx + crownR * 1.05 ) },${ f( top + crownR * 0.7 ) } ${ f( tx + crownR * 0.35 ) },${ f( top + crownR ) }`
          + ` C${ f( tx ) },${ f( top + crownR * 1.25 ) } ${ f( tx - crownR * 0.35 ) },${ f( top + crownR * 1.2 ) } ${ f( tx - crownR * 0.4 ) },${ f( top + crownR ) }`
          + ` C${ f( tx - crownR * 1.05 ) },${ f( top + crownR * 0.7 ) } ${ f( tx - crownR ) },${ f( top - crownR * 0.6 ) } ${ f( tx ) },${ f( top - crownR * 0.55 ) } Z` );
      }
    }
    this.paTreeD = trees.join( ' ' );
    // Tuileries Green lifted well toward white: on a pale pink sky a saturated
    // green is the only cold thing in the picture and shouts.
    this.paTreeFill = mixHex( '#5CA86C', '#FFFFFF', 0.42 );
    this.paTrunkD = trunks.join( ' ' );
    // A warm brown built out of the theme's own colours rather than imported:
    // Rosewood carried toward Champagne Gold lands on bark without introducing a
    // hue this palette does not have.
    this.paTrunkFill = mixHex( ROSEWOOD, GOLD, 0.35 );
    this.paWindowFill = mixHex( ROSEWOOD, SMOKE, 0.38 );

    // ---- the quay, and a REAL bridge across the water ----
    // The draft made the bridge the subject and it ate the height the buildings
    // needed; demoted to the foreground it does what a Seine bridge actually
    // does in a view like this — crosses in front of the terrace and gives the
    // strip a foreground. Sixteen bays, periodic in W, so the arcade continues
    // through the tile seam.
    // No quay band any more: the deck starts exactly where the terrace ends
    // (owner 2026-08-14: "köprü üzeri tam binaların altında olsun"), so the
    // buildings stand ON the bridge's roadway rather than on a strip of ground
    // that then had a bridge under it.
    this.paQuayD = '';
    this.paQuayFill = mixHex( SMOKE, MAUVE, 0.22 );

    // The arches are REAL OPENINGS, not a drawn arc (owner 2026-08-14: "köprü
    // ilk çizimdeki gibi arclı olsun"). The pass before this stroked a thin
    // crescent onto a solid deck and it read as a railing: at strip scale an
    // arch is only an arch if you can see through it.
    //
    // So the whole bridge is ONE path under fill-rule="evenodd" — a solid block
    // with the bays punched out of it — and the water is drawn BEFORE it, so
    // what shows through each opening is the Seine.
    const DECK_TOP = GROUND, DECK_BOT = GROUND + 6, ARCH_BOT = 146;
    const BAYS2 = 15;
    const AW = SPAN / BAYS2;
    const rr = AW * 0.29;
    // SMALLER arches, and their crowns kept UNDER the roadway (owner
    // 2026-08-14: "arclar büyük olduğu için içinden geçmiş, köprünün tepeden
    // çıkmış"). The pass before sprang them at y=136 with a 28-unit rise, which
    // put each crown at y=108 — above the deck's own top edge — so the opening's
    // subpath escaped the block it was meant to be punched out of and evenodd
    // cancelled nothing. The bridge rendered SOLID, which is why the arches were
    // not visible at all rather than merely too large.
    const HEAD = DECK_BOT + 4;          // where the crown of each arch sits
    const springY = 134;                // where its jambs start rising
    const parts: string[] = [
      `M${ GAP },${ DECK_TOP } L${ f( this.W - GAP ) },${ DECK_TOP }`
      + ` L${ f( this.W - GAP ) },${ ARCH_BOT } L${ GAP },${ ARCH_BOT } Z`,
    ];
    for ( let a = 0; a < BAYS2; a++ ) {
      const cx = GAP + ( a + 0.5 ) * AW;
      // each opening: straight jambs up from the water, a round head on top
      parts.push( `M${ f( cx - rr ) },${ ARCH_BOT } L${ f( cx - rr ) },${ f( springY ) }`
        + ` A${ f( rr ) },${ f( springY - HEAD ) } 0 0 1 ${ f( cx + rr ) },${ f( springY ) }`
        + ` L${ f( cx + rr ) },${ ARCH_BOT } Z` );
    }
    this.paBridgeD = parts.join( ' ' );
    this.paBridgeFill = mixHex( SMOKE, MAUVE, 0.34 );
    this.paBalusterD = '';
    this.paArchRingD = '';
    this.paCopingFill = mixHex( SMOKE, MAUVE, 0.52 );
    this.paDeckTop = DECK_TOP;
    this.paDeckBot = DECK_BOT;

    // ---- the water, ending on exactly what LINE paints below the strip ----
    this.paWaterTop = GROUND + 8;
    this.paWaterDeep = mixHex( SMOKE, SEINE, 0.24 );
    this.paWaterMid = mixHex( SMOKE, lineTabFill( p.key, p.appearance ), 0.55 );
    this.paWaterLow = lineTabFill( p.key, p.appearance );
    this.paGlints = [];
    this.paRiverGlow = { cx: 736, cy: 300, rx: 1, ry: 1 };
    this.paGlintFill = GOLD;
  }

  // ---- colour helpers ----

  private lighten( hex: string, f: number ): string {
    const [ r, g, b ] = this.rgb( hex );
    const adj = ( c: number ) => f >= 0 ? c + ( 255 - c ) * f : c * ( 1 + f );
    return this.hex( adj( r ), adj( g ), adj( b ) );
  }

  private rgb( hex: string ): number[] {
    const h = ( hex || '#000000' ).replace( '#', '' );
    const full = h.length === 3 ? h.split( '' ).map( c => c + c ).join( '' ) : h;
    return [ 0, 2, 4 ].map( i => parseInt( full.substr( i, 2 ), 16 ) || 0 );
  }

  private hex( r: number, g: number, b: number ): string {
    return '#' + [ r, g, b ].map( v => Math.max( 0, Math.min( 255, Math.round( v ) ) ).toString( 16 ).padStart( 2, '0' ) ).join( '' );
  }
}
